import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Paper, 
  CircularProgress, 
  Alert,
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Tooltip,
  Card,
  CardHeader,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  FormControlLabel,
  Switch,
  TextField,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { 
  Refresh as RefreshIcon,
  Error as ErrorIcon,
  Fullscreen as FullscreenIcon,
  ViewModule as ViewModuleIcon,
  OpenInNew as OpenInNewIcon,
  GridView as GridViewIcon,
  AutoFixHigh as AutoFixHighIcon,
  RestartAlt as RestartAltIcon,
  Launch as LaunchIcon,
  OpenInFull as OpenInFullIcon,
  FullscreenExit as FullscreenExitIcon,
  PictureInPicture as PictureInPictureIcon,
  CropSquare as CropSquareIcon,
  AspectRatio as AspectRatioIcon,
  Slideshow as SlideshowIcon,
  NotificationsActive as NotificationsActiveIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchPoolMonitorGrants,
  isPoolMonitorGranted,
  type PoolMonitorGrants,
} from '../brand/poolMonitorPolicy';
import { viewerGrade } from '../brand/menuCatalog';
import {
  fetchPrimaryPoolSlots,
  PRIMARY_POOLS_UPDATED_EVENT,
  type PrimaryPoolSlot,
} from '../services/dashboardSettingsService';
import { sortPoolsByPrimaryOrder } from '../utils/sortPoolsByPrimaryOrder';
import {
  clampHlsBufferSec,
  HLS_BUFFER_SEC_DEFAULT,
} from '../utils/hlsPlayback';
import api from '../services/api';
import axios from 'axios';
import Hls from 'hls.js';
import AllPoolsMonitoringPopup, {
  type AllPoolsMonitoringPopupHandle,
} from '../components/monitoring/AllPoolsMonitoringPopup';
import {
  openPoolMonitoringInNewWindow,
  openPoolMonitoringRotationInNewWindow,
  DEFAULT_MONITORING_ROTATION_INTERVAL_SEC,
  getMonitoringPopupWindowFeatures,
  placeMonitoringPopupOnPreferredScreen,
  focusMonitoringPopupWindow,
  readMonitoringPoolGridRotationIntervalSec,
  writeMonitoringPoolGridRotationIntervalSec,
  readMonitoringRotationIntervalSec,
  writeMonitoringRotationIntervalSec,
} from '../components/monitoring/openPoolMonitoringWindow';
import {
  filterCentralMonitoringCctvs,
  normalizeCctvListFromApiPayload,
} from '../components/monitoring/monitoringPopupData';
import { SelectChangeEvent } from '@mui/material';
import config from '../config.json';
import { standardPoolApi } from '../services/standardPoolApi';
import PoolCard from '../components/dashboard/PoolCard';
import { Pool } from '../types';
import { riskDetectionService } from '../services/riskDetectionService';
import { API_ORIGIN } from '../config';
import { bboxDeltaMax, lerpBbox } from '../utils/bboxLerp';
import {
  lookupServerSnapshot,
  filterServerDetectionsForDisplay,
  overlayTracksFromServerSnapshot,
  enrichOverlayTracksWithOpenPose,
  serverSnapshotKey,
  isRawPersonPipelineMode,
  type ServerSnapshotPayload,
} from '../utils/monitoringServerOverlay';
import { resolveAbsoluteCctvStreamUrl } from '../utils/cctvStreamUrl';
import {
  listMonitoringZoneTiles,
  remapBboxToCrop,
  type ZoneRect,
} from '../utils/cctvZoneView';
import {
  remapBboxRotate180,
  remapZoneListRotate180,
} from '../utils/cctvDisplayRotate';
import { isNormalOperatingPoolStatus } from '../utils/poolUtils';
import RiskSimulateBar from '../components/central-monitoring/RiskSimulateBar';
import CentralRiskEventDialog from '../components/central-monitoring/CentralRiskEventDialog';
import ControlCenterSafetyAlertDialog from '../components/central-monitoring/ControlCenterSafetyAlertDialog';
import ControlRoomSirenDialog from '../components/central-monitoring/ControlRoomSirenDialog';
import PoolManualAlertDialog, {
  type PoolManualAlertTarget,
} from '../components/central-control/PoolManualAlertDialog';
import {
  type CentralRiskEventBbox,
  type CentralRiskEventPayload,
  riskTypeToLabel,
  dismissCentralRiskDialogPayload,
  isCentralRiskDialogDismissed,
  isCentralRiskUiEventDismissed,
} from '../components/central-monitoring/centralRiskEventTypes';
import { resolveRiskAppearance } from '../constants/riskEventAppearance';
import { resolveManualRiskEvents } from '../constants/scenarioRiskEvents';
import { resolveDetectionForFacilityProfile } from '../constants/cctv';
import { captureVideoFrame } from '../utils/captureVideoFrame';
import { scheduleRiskEvidenceAtDetection } from '../utils/scheduleRiskEvidenceAtDetection';
import {
  applyCentralAiDetectShowBboxDefaults,
  syncMonitoringPopupShowBboxForAiDetect,
} from '../components/monitoring/monitoringPopupBboxScript';
import { useEventContext } from '../contexts/EventContext';
import { useMonitoringViewport } from '../hooks/useMonitoringViewport';
import { eventService } from '../services/eventService';
import { isDrowningEventType } from '../services/riskClipService';

import CentralMonitoringCctvPlayer, {
  type CentralCctvPlayerHandle,
} from '../components/central-monitoring/CentralMonitoringCctvPlayer';
import ZoomPanViewport from '../components/central-monitoring/ZoomPanViewport';
import CctvGpuSelector from '../components/central-monitoring/CctvGpuSelector';
import CentralMonitoringOverlays, { OverlayTrack } from '../components/central-monitoring/CentralMonitoringOverlays';
import OpenPoseSkeletonLayer from '../components/central-monitoring/OpenPoseSkeletonLayer';
import {
  useWearableTrackConfidenceMap,
  type TrackConfidenceMap,
} from '../hooks/useWearableTrackConfidenceMap';
import MonitoringOpsChips from '../components/monitoring/MonitoringOpsChips';
import KymaBrandTitle from '../components/common/KymaBrandTitle';
import DetectionLabelToolbar, {
  DetectionLabelMode,
} from '../components/monitoring/DetectionLabelToolbar';
import OperatorMessageToolbarButton from '../components/monitoring/OperatorMessageToolbarButton';
import CommunicationToolbarMenu from '../components/monitoring/CommunicationToolbarMenu';
import DetectionLabelLayer from '../components/monitoring/DetectionLabelLayer';
import { useMonitoringAiSettings } from '../hooks/useMonitoringAiSettings';
import {
  DRAW_RAW_SERVER_SNAPSHOTS,
  TRUST_SERVER_SNAPSHOT_OVERLAY,
  USE_HYBRID_MONITOR_PIPELINE,
  HYBRID_CLIENT_YOLO_FALLBACK,
  resolveInferCaptureMaxWidth,
  getDetectionCooldownMs,
  maxServerMissForDisplay,
  passesDetectionDisplayFilters,
  isCentralPoolMonitored,
  isCentralPoolAiOverlayEligible,
  passesAlertPopupGate,
  type MonitoringAiSettings,
} from '../constants/monitoringAiSettings';
import { parseOpenPoseOverlay } from '../constants/openposeOverlay';
import { monitoringWebSocket } from '../services/websocketService';
import {
  filterHybridDetectionList,
  hybridRefineClassName,
  lowerPoolEdgeRipplePersonFp,
  ver5LaneStripePersonLockFp,
} from '../utils/hybridMonitorDetection';
import {
  excludePoolFromCentralAi,
  fetchCentralAiSnapshots,
  includePoolInCentralAi,
  pingCentralAiPresence,
  endCentralMonitoringSession,
  resetCentralPoolMonitoringAllOff,
} from '../services/centralAiMonitorApi';
import { isCentralMonitoringPopupWindow } from '../utils/centralMonitoringWindow';
import {
  fetchGpuAllocationStatus,
  streamAssignmentKey,
  type GpuAllocationStatus,
} from '../services/gpuAllocationService';
import { useCentralAiOpsPresence } from '../hooks/useCentralAiOpsPresence';

/** rAF 보간 — 낮을수록 덜 “춤춤”, 검출 지터 흡수 */
const DISPLAY_TRACK_LERP = 0.28;
const DISPLAY_TRACK_LERP_FAST = 0.52;
/** 서버 target EMA (0=이전 유지, 1=즉시 반영) */
const DISPLAY_TARGET_SMOOTH = 0.38;
/** 속도 예측: 0=끔(지터·오탐 시 bbox 날아다님 방지). 안정 추적 필요 시 0.25~0.4 */
const DISPLAY_TRACK_PREDICT_MAX_SEC = 0;
const DISPLAY_TRACK_PREDICT_MIN_SPEED_PX = 12;
const DISPLAY_TRACK_PREDICT_MAX_SPEED_PX = 220;
/** 서버 miss 시 투명도 */
const DISPLAY_TRACK_MISS_FADE = 0.55;
/** API 한 프레임 빠져도 박스 유지 (로컬) */
const DISPLAY_LOCAL_MAX_MISS = 6;
/** 이상치 점프(px) — 초과 시 서버 bbox 무시(지터·ID 스왑 완화). 수영 등 고속 이동 고려해 여유 있게 */
const DISPLAY_TARGET_REJECT_JUMP_PX = 220;

function bboxCenter(b: number[]): [number, number] {
  return [(b[0] + b[2]) * 0.5, (b[1] + b[3]) * 0.5];
}

function extrapolateBboxByVelocity(
  bbox: number[],
  vx: number,
  vy: number,
  dtSec: number
): number[] {
  const dx = vx * dtSec;
  const dy = vy * dtSec;
  return [bbox[0] + dx, bbox[1] + dy, bbox[2] + dx, bbox[3] + dy];
}

function resolveTrackVelocityPxPerSec(
  det: {
    velocity_x?: unknown;
    velocity_y?: unknown;
    frame_width?: number;
  },
  prev: DisplayTrackState | undefined,
  targetBbox: number[],
  nowMs: number
): [number, number] {
  const inferFps = 3;
  let vx = Number(det.velocity_x);
  let vy = Number(det.velocity_y);
  if (Number.isFinite(vx) && Number.isFinite(vy)) {
    vx *= inferFps;
    vy *= inferFps;
  } else if (prev?.targetBbox && prev.serverUpdatedAt) {
    const dt = Math.max(0.05, (nowMs - prev.serverUpdatedAt) / 1000);
    const [pcx, pcy] = bboxCenter(prev.targetBbox);
    const [tcx, tcy] = bboxCenter(targetBbox);
    vx = (tcx - pcx) / dt;
    vy = (tcy - pcy) / dt;
  } else {
    vx = prev?.velocityX ?? 0;
    vy = prev?.velocityY ?? 0;
  }
  const fw = Math.max(320, Number(det.frame_width) || 1280);
  const cap = Math.min(DISPLAY_TRACK_PREDICT_MAX_SPEED_PX, fw * 0.35);
  vx = Math.max(-cap, Math.min(cap, vx));
  vy = Math.max(-cap, Math.min(cap, vy));
  if (prev?.velocityX != null && prev?.velocityY != null) {
    vx = prev.velocityX * 0.72 + vx * 0.28;
    vy = prev.velocityY * 0.72 + vy * 0.28;
  }
  return [vx, vy];
}

type DisplayTrackState = {
  trackId: number;
  targetBbox: number[];
  displayBbox: number[];
  class_name: string;
  score: number;
  miss_count: number;
  frame_width?: number;
  frame_height?: number;
  alpha: number;
  motionRisk?: boolean;
  riskLevel?: string;
  riskSubtype?: string;
  riskSubtypeLabel?: string;
  borderColor?: string;
  displayLocalMiss?: number;
  velocityX?: number;
  velocityY?: number;
  serverUpdatedAt?: number;
};

const MOTION_RISK_HIGHLIGHT_MS = 2800;

type MotionRiskTrackMeta = { level: string; riskType: string; riskLabel: string };

function normalizeMotionRiskType(raw?: string): string {
  const t = String(raw || '')
    .trim()
    .toLowerCase();
  if (!t) return 'motion_risk';
  if (t === 'drowning_risk') return 'drowning';
  return t;
}

function motionRiskMessage(_poolName: string, _riskType: string): string {
  // 팝업 메시지에는 헤더·bbox만 — 별도 이벤트 문구 없음
  return '';
}

function collectMotionRiskMeta(riskAlerts: unknown): Map<number, MotionRiskTrackMeta> {
  const map = new Map<number, MotionRiskTrackMeta>();
  if (!Array.isArray(riskAlerts)) return map;
  for (const raw of riskAlerts) {
    const a = raw as {
      risk_type?: string;
      risk_label?: string;
      risk_level?: string;
      track_id?: unknown;
      object_id?: unknown;
    };
    const rawType = String(a.risk_type || '').trim();
    if (!rawType) continue;
    const riskType = normalizeMotionRiskType(rawType);
    const tid = Number(a.track_id ?? a.object_id);
    if (!Number.isFinite(tid)) continue;
    const level = String(a.risk_level || '주의').trim() || '주의';
    const riskLabel =
      String(a.risk_label || '').trim() || riskTypeToLabel(riskType) || riskType;
    const prev = map.get(tid);
    if (!prev || level === '위험' || prev.level !== '위험') {
      map.set(tid, { level, riskType, riskLabel });
    }
  }
  return map;
}

/** 이벤트 팝업용 — 위험 트랙 우선, 없으면 현재 표시 트랙 */
function collectEventBboxesFromDisplayTracks(
  tracks: Iterable<DisplayTrackState> | DisplayTrackState[] | null | undefined,
  riskByTrackId?: Map<number, MotionRiskTrackMeta>
): CentralRiskEventBbox[] {
  if (!tracks) return [];
  const list = Array.from(tracks);
  if (list.length === 0) return [];
  const mapped = list
    .filter((t) => Array.isArray(t.displayBbox) && t.displayBbox.length === 4)
    .map((t) => {
      const riskMeta = riskByTrackId?.get(t.trackId);
      const motionRisk = Boolean(t.motionRisk || riskMeta);
      return {
        bbox: t.displayBbox.map(Number),
        frame_width: t.frame_width,
        frame_height: t.frame_height,
        trackId: t.trackId,
        motionRisk,
        riskSubtypeLabel: riskMeta?.riskLabel || t.riskSubtypeLabel,
        class_name: t.class_name,
        borderColor: motionRisk
          ? t.borderColor ||
            resolveRiskAppearance(riskMeta?.riskType || t.riskSubtype, {
              label: riskMeta?.riskLabel || t.riskSubtypeLabel,
            }).color
          : t.borderColor,
      };
    });
  const riskOnly = mapped.filter((b) => b.motionRisk);
  return riskOnly.length > 0 ? riskOnly : mapped;
}

function collectEventBboxesFromDetections(
  detections: any[] | null | undefined,
  riskByTrackId?: Map<number, MotionRiskTrackMeta>
): CentralRiskEventBbox[] {
  if (!Array.isArray(detections) || detections.length === 0) return [];
  let fallbackId = 1;
  const mapped = detections
    .filter((d) => Array.isArray(d?.bbox) && d.bbox.length === 4)
    .map((d) => {
      let tid = Number(d.track_id ?? d.id);
      if (!Number.isFinite(tid)) {
        tid = 1_000_000 + fallbackId;
        fallbackId += 1;
      }
      const riskMeta = riskByTrackId?.get(tid);
      return {
        bbox: (d.bbox as number[]).map(Number),
        frame_width: d.frame_width != null ? Number(d.frame_width) : undefined,
        frame_height: d.frame_height != null ? Number(d.frame_height) : undefined,
        trackId: tid,
        motionRisk: Boolean(riskMeta),
        riskSubtypeLabel: riskMeta?.riskLabel,
        class_name: String(d.class_name || 'person'),
        borderColor: riskMeta
          ? resolveRiskAppearance(riskMeta.riskType, {
              label: riskMeta.riskLabel,
            }).color
          : undefined,
      };
    });
  const riskOnly = mapped.filter((b) => b.motionRisk);
  return riskOnly.length > 0 ? riskOnly : mapped;
}

// 타입 정의
interface Cctv {
  id: number;
  pool_id: number;
  name: string;
  location: string;
  url?: string;
  stream_url?: string;
  playback_url?: string;
  webrtc_url?: string;
  local_rtsp_url?: string;
  status: 'active' | 'inactive' | 'maintenance';
  is_active?: boolean;
  camera_type: 'safety' | 'management' | 'other';
  purpose?: string;
  detection_scenario?: string | null;
  risk_detection_zones?: number[][];
  special_management_zones?: number[][];
  /** 관제 표시 오탐 필터 강도: 0=완화, 1=균형, 2=강화 */
  monitor_filter_strength?: number | null;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  status: number;
  message: string;
}

interface PoolResponse {
  data?: Pool[];
  pools?: Pool[];
}

const LOCATION_SPLIT_RE = /(\d+|\D+)/g;
const cctvLocationNaturalCompare = (a: Cctv, b: Cctv) => {
  const la = String(a.location || a.name || '');
  const lb = String(b.location || b.name || '');
  const pa = la.match(LOCATION_SPLIT_RE) || [la];
  const pb = lb.match(LOCATION_SPLIT_RE) || [lb];
  const n = Math.max(pa.length, pb.length);
  for (let i = 0; i < n; i++) {
    const xa = pa[i] ?? '';
    const xb = pb[i] ?? '';
    const na = Number(xa);
    const nb = Number(xb);
    const bothNum = Number.isFinite(na) && Number.isFinite(nb) && xa.trim() !== '' && xb.trim() !== '';
    if (bothNum) {
      if (na !== nb) return na - nb;
      continue;
    }
    const cmp = xa.localeCompare(xb, 'ko');
    if (cmp !== 0) return cmp;
  }
  return a.id - b.id;
};

const onRiskEventRef = { current: (_payload: CentralRiskEventPayload) => {} };
const onOpenManualAlertRef = { current: (_pool: Pool) => {} };
const patchRiskEventIdRef = {
  current: (_poolId: number, _cctvId: number, _riskType: string, _eventDbId: number) => {},
};
/** 페이지 이탈 후에도 동일 위험 팝업 재표시 방지 */
const riskDialogDedupRef = { key: '', at: 0 };
/** 팝업이 열린 동안 다른 시설 이벤트를 덮어쓰지 않고 대기 */
const pendingRiskEventsRef: { current: CentralRiskEventPayload[] } = { current: [] };
/** PoolGridItem(useMemo 고정)에서도 참조 — 위험 팝업 중 스낵바 억제 */
const centralRiskUiRef = { dialogOpen: false };

/** "[AI] ○○에서 N명 감지" 안내 스낵바 — 기능 없음, 표시 금지 */
function isPersonCountAiSnackMessage(message: string): boolean {
  return /\[AI\].*명\s*감지/.test(message) || /(^|\s)\d+명\s*감지/.test(message);
}

/** PoolGridItem 로직 버전 — 올려야 useMemo([]) 고정의 구 클로저가 갱신됨 */
const POOL_GRID_ITEM_REV = 10;

type PoolFullscreenOverlayState = {
  poolIndex: number;
  paused: boolean;
  intervalSec: number;
};

type CctvRotationSlide = {
  cctvId: number;
  zoneIndex: number;
  viewCrop?: ZoneRect;
};

type CctvRotationOverlayState = {
  poolId: number;
  poolName: string;
  siteGatewayEnabled: boolean;
  slideIndex: number;
  paused: boolean;
  intervalSec: number;
  /** 순환 슬라이드 (구역 전용 모드면 구역마다 1장) */
  slides: CctvRotationSlide[];
  zoneTilesOnly: boolean;
};

const CentralMonitoring: React.FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { authState, logout } = useAuth();
  const [poolFullscreen, setPoolFullscreen] =
    useState<PoolFullscreenOverlayState | null>(null);
  const [poolFsIntervalInput, setPoolFsIntervalInput] = useState('20');
  /** 전체화면 시설 CCTV가 모두 올라온 뒤에만 롤링 타이머 가동 */
  const [poolFsVideosReady, setPoolFsVideosReady] = useState(false);
  const [cctvRotation, setCctvRotation] =
    useState<CctvRotationOverlayState | null>(null);
  const cctvRotPlayerRef = useRef<CentralCctvPlayerHandle | null>(null);
  const cctvRotOverlayContainerRef = useRef<HTMLDivElement | null>(null);
  const [cctvRotOverlayTick, setCctvRotOverlayTick] = useState(0);
  const [cctvRotIntervalInput, setCctvRotIntervalInput] = useState('5');
  const [cctvRotVideoReady, setCctvRotVideoReady] = useState(false);
  const brandVariant =
    location.pathname.startsWith('/individual-monitoring') ? 'individual' : 'monitor';
  const showIndividualLogout =
    brandVariant === 'individual' && !authState.user?.is_superuser;
  const assignedAdminIdFilter = useMemo(() => {
    const selfId =
      authState.user?.id != null ? Number(authState.user.id) : NaN;
    const raw = searchParams.get('adminId');
    const fromQuery =
      raw != null && raw !== '' ? Number(raw) : NaN;
    // 시설 전체 관제: 본인 adminId 쿼리여도 전체 시설 표시
    if (
      authState.user?.monitor_all_pools &&
      (!Number.isFinite(fromQuery) || fromQuery === selfId)
    ) {
      return null;
    }
    if (Number.isFinite(fromQuery) && fromQuery > 0) return fromQuery;
    // 일반관리자가 통합 URL로 들어와도 본인 배당만 보이게
    if (!authState.user?.is_superuser && Number.isFinite(selfId) && selfId > 0) {
      return selfId;
    }
    return null;
  }, [
    searchParams,
    authState.user?.is_superuser,
    authState.user?.id,
    authState.user?.monitor_all_pools,
  ]);
  const [pools, setPools] = useState<Pool[]>([]);
  const [poolMonitorGrants, setPoolMonitorGrants] = useState<PoolMonitorGrants | null>(null);
  const [primaryPoolSlots, setPrimaryPoolSlots] = useState<PrimaryPoolSlot[]>([]);
  const [poolCctvs, setPoolCctvs] = useState<{ [key: string]: Cctv[] }>({});
  const [useRemotePlayback, setUseRemotePlayback] = useState<boolean>(false);
  const [centralGo2rtcAvailable, setCentralGo2rtcAvailable] = useState<boolean>(false);
  const [centralGo2rtcStreams, setCentralGo2rtcStreams] = useState<string[]>([]);
  /** producer 목록에서 잠깐 빠져도 유지 (ms). 짧으면 WebRTC→죽은 HLS 흑화면 */
  const go2rtcStreamSeenRef = useRef<Record<string, number>>({});
  const go2rtcAvailSeenAtRef = useRef<number>(0);
  const GO2RTC_STREAM_HOLD_MS = 120_000;
  const GO2RTC_AVAIL_HOLD_MS = 120_000;
  const [playbackConfigReady, setPlaybackConfigReady] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [riskAlert, setRiskAlert] = useState<{ type: string; message: string } | null>(null);
  const [centralRiskEvent, setCentralRiskEvent] = useState<CentralRiskEventPayload | null>(null);
  const [centralRiskDialogOpen, setCentralRiskDialogOpen] = useState(false);
  /** 위험 이벤트 다이얼로그 닫힌 뒤 그리드 CCTV 재생 재개 신호 */
  const [playbackResumeNonce, setPlaybackResumeNonce] = useState(0);
  /** 상단 새로고침 — HLS 스트림 강제 재연결 */
  const [streamReloadNonce, setStreamReloadNonce] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [manualAlertTarget, setManualAlertTarget] = useState<PoolManualAlertTarget | null>(null);
  const [manualAlertSnack, setManualAlertSnack] = useState<string | null>(null);
  const centralRiskEventRef = useRef<CentralRiskEventPayload | null>(null);
  const centralRiskDialogOpenRef = useRef(false);
  centralRiskEventRef.current = centralRiskEvent;
  centralRiskDialogOpenRef.current = centralRiskDialogOpen;
  centralRiskUiRef.dialogOpen = centralRiskDialogOpen;

  const emitCentralRiskEvent = useCallback((payload: CentralRiskEventPayload) => {
    if (isCentralRiskDialogDismissed(payload)) {
      return;
    }
    const key = `${payload.poolId}:${payload.cctvId ?? 0}:${payload.riskType}`;
    const now = Date.now();
    if (
      riskDialogDedupRef.key === key &&
      now - riskDialogDedupRef.at < 25000
    ) {
      return;
    }
    const alreadyQueued = pendingRiskEventsRef.current.some(
      (p) =>
        p.poolId === payload.poolId &&
        (p.cctvId ?? 0) === (payload.cctvId ?? 0) &&
        p.riskType === payload.riskType
    );
    if (alreadyQueued) {
      return;
    }
    const enriched = { ...payload, triggerTs: payload.triggerTs ?? Date.now() / 1000 };
    const current = centralRiskEventRef.current;
    // 팝업이 열린 동안 같은 시설 이벤트는 대기열에도 넣지 않음
    // (오탐 폭주 시 「닫기」해도 다음 창이 바로 떠서 안 닫히는 것처럼 보임)
    if (centralRiskDialogOpenRef.current && current) {
      if (current.poolId === enriched.poolId) {
        return;
      }
      riskDialogDedupRef.key = key;
      riskDialogDedupRef.at = now;
      scheduleRiskEvidenceAtDetection(enriched);
      pendingRiskEventsRef.current.push(enriched);
      return;
    }
    riskDialogDedupRef.key = key;
    riskDialogDedupRef.at = now;
    scheduleRiskEvidenceAtDetection(enriched);
    setCentralRiskEvent(enriched);
    setCentralRiskDialogOpen(true);
  }, []);

  useEffect(() => {
    return () => {
      if (centralRiskDialogOpenRef.current && centralRiskEventRef.current) {
        dismissCentralRiskDialogPayload(centralRiskEventRef.current);
      }
    };
  }, []);

  const patchCentralRiskEventId = useCallback(
    (poolId: number, cctvId: number, riskType: string, eventDbId: number) => {
      setCentralRiskEvent((prev) => {
        if (!prev) return prev;
        if (
          prev.poolId !== poolId ||
          prev.cctvId !== cctvId ||
          prev.riskType !== riskType
        ) {
          return prev;
        }
        return { ...prev, eventDbId };
      });
    },
    []
  );

  onRiskEventRef.current = emitCentralRiskEvent;
  patchRiskEventIdRef.current = patchCentralRiskEventId;
  onOpenManualAlertRef.current = (pool: Pool) => {
    const poolId = Number(pool.id);
    if (!Number.isFinite(poolId)) return;
    setManualAlertTarget({
      poolId,
      poolName: pool.name || pool.code || `시설 #${poolId}`,
      safetyPhoneRaw: String(pool.safety_phone || '').trim(),
    });
  };

  const { subscribeToEvents } = useEventContext();

  useEffect(() => {
    return subscribeToEvents((ev) => {
      if (ev.status === 'resolved') return;
      if (isCentralRiskUiEventDismissed(ev.id)) return;
      const eventType = String(ev.metadata?.event_type || ev.title || '');
      const lower = eventType.toLowerCase();
      if (
        !isDrowningEventType(eventType) &&
        !lower.includes('risk') &&
        !lower.includes('motion') &&
        ev.type !== 'danger' &&
        ev.type !== 'warning'
      ) {
        return;
      }
      const poolId = Number(ev.poolId);
      if (!Number.isFinite(poolId)) return;
      const poolRow = pools.find((p) => String(p.id) === String(poolId));
      emitCentralRiskEvent({
        poolId,
        poolName: ev.poolName || poolRow?.name || `시설 #${poolId}`,
        cctvId:
          ev.metadata?.cctv_id != null ? Number(ev.metadata.cctv_id) : undefined,
        location: ev.location || '',
        riskType: eventType || 'drowning_risk',
        riskLabel: riskTypeToLabel(eventType),
        message: ev.description || ev.title,
        eventDbId:
          (ev.metadata?.event_db_id as number | string | undefined) ??
          eventService.resolveAckId(ev),
        uiEventId: ev.id,
        imageDataUrl: ev.imageUrl,
        safetyPhoneRaw: String(poolRow?.safety_phone || '').trim(),
        poolSirens: Array.isArray(poolRow?.sirens)
          ? poolRow.sirens.filter((s) => String(s).trim())
          : [],
        source: 'websocket',
      });
    });
  }, [subscribeToEvents, emitCentralRiskEvent, pools]);
  
  // 로컬 스토리지에서 설정값 불러오기 (URL cols/interval 우선 — 새창에서 모니터링)
  const [poolsPerRow, setPoolsPerRow] = useState<number>(() => {
    try {
      const fromQuery = parseInt(
        new URLSearchParams(window.location.search).get('cols') || '',
        10
      );
      if (Number.isFinite(fromQuery) && fromQuery >= 1) {
        localStorage.setItem('poolsPerRow', String(fromQuery));
        return fromQuery;
      }
    } catch {
      // ignore
    }
    const savedValue = localStorage.getItem('poolsPerRow');
    return savedValue ? parseInt(savedValue, 10) : 3;
  });
  
  /** 통합관제 진입 시 스트림은 360p로 시작 (사용자가 UI에서 변경 가능) */
  const [globalQuality, setGlobalQuality] = useState<string>('360p');
  
  const [bufferSize, setBufferSize] = useState<number>(() => {
    const savedValue = localStorage.getItem('bufferSize');
    const parsed = savedValue ? parseInt(savedValue, 10) : HLS_BUFFER_SEC_DEFAULT;
    return clampHlsBufferSec(parsed);
  });

  /** 순환 전환 간격(초). UI 조정 없이 기본 5초 고정 */
  const rotationIntervalSec = DEFAULT_MONITORING_ROTATION_INTERVAL_SEC;

  const monitorViewerGrade = useMemo(
    () => viewerGrade(authState.user, authState.isAuthenticated),
    [authState.user, authState.isAuthenticated],
  );

  const monitoringPools = useMemo(
    () => {
      let list = pools.filter((p) =>
        isNormalOperatingPoolStatus(
          p.status,
          (p as Pool & { is_active?: boolean }).is_active,
        ),
      );
      // brand-manager 시설별 관제 보기 권한 (체크 안 된 등급은 제외)
      list = list.filter((p) =>
        isPoolMonitorGranted(p.id, monitorViewerGrade, poolMonitorGrants),
      );
      if (assignedAdminIdFilter != null) {
        list = list.filter((p) => {
          const raw = (p as Pool & { monitoring_admin_id?: number | null })
            .monitoring_admin_id;
          // 배당 없음(null/0): 시설에 CCTV만 붙인 경우에도 관제에 표시
          if (raw == null || Number(raw) <= 0) {
            return true;
          }
          return Number(raw) === assignedAdminIdFilter;
        });
      }
      return sortPoolsByPrimaryOrder(list, primaryPoolSlots);
    },
    [pools, assignedAdminIdFilter, primaryPoolSlots, monitorViewerGrade, poolMonitorGrants],
  );

  const poolIds = useMemo(
    () => monitoringPools.map((p) => Number(p.id)).filter((id) => !Number.isNaN(id)),
    [monitoringPools],
  );
  const opsApiReady =
    authState.isAuthenticated &&
    authState.isAdminPasswordVerified &&
    !authState.loading;

  const settingsEnabled =
    authState.isAuthenticated && !authState.loading;
  const { map: wearableTrackMap } = useWearableTrackConfidenceMap(opsApiReady, 3000);
  const {
    settings: monitoringAiSettings,
    refresh: refreshMonitoringAi,
    patchMonitoringAi,
  } = useMonitoringAiSettings(opsApiReady);
  useEffect(() => {
    if (!opsApiReady) return;
    void refreshMonitoringAi();
  }, [opsApiReady, refreshMonitoringAi]);
  useCentralAiOpsPresence(opsApiReady);
  const [gpuAllocationStatus, setGpuAllocationStatus] =
    useState<GpuAllocationStatus | null>(null);
  useEffect(() => {
    if (!settingsEnabled || !monitoringAiSettings.gpu_allocation_enabled) {
      setGpuAllocationStatus(null);
      return undefined;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const st = await fetchGpuAllocationStatus();
        if (!cancelled) setGpuAllocationStatus(st);
      } catch {
        /* 다음 주기에 재시도 */
      }
    };
    void poll();
    const id = window.setInterval(() => void poll(), 12_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [settingsEnabled, monitoringAiSettings.gpu_allocation_enabled]);

  const handleCctvGpuChanged = useCallback(
    (poolId: number, cctvId: number, gpuIndex: number) => {
      setGpuAllocationStatus((prev) => {
        if (!prev) return prev;
        const key = streamAssignmentKey(poolId, cctvId);
        return {
          ...prev,
          stream_assignments: { ...prev.stream_assignments, [key]: gpuIndex },
          cctv_overrides: { ...prev.cctv_overrides, [key]: gpuIndex },
        };
      });
    },
    []
  );
  const allVisiblePoolsExcluded = useMemo(() => {
    if (poolIds.length === 0) return true;
    return poolIds.every(
      (id) => !isCentralPoolMonitored(id, monitoringAiSettings, poolIds)
    );
  }, [poolIds, monitoringAiSettings, monitoringAiSettings.included_pool_ids.join(',')]);
  const excludeToggleInFlightRef = useRef<Set<number>>(new Set());
  const [serverSnapshotsByCctv, setServerSnapshotsByCctv] = useState<
    Record<string, ServerSnapshotPayload>
  >({});
  const popupPoolScopeResetRef = useRef(false);
  useEffect(() => {
    if (!opsApiReady || !isCentralMonitoringPopupWindow()) return;
    if (popupPoolScopeResetRef.current) return;
    popupPoolScopeResetRef.current = true;
    let cancelled = false;
    void (async () => {
      try {
        const result = await resetCentralPoolMonitoringAllOff();
        if (cancelled) return;
        patchMonitoringAi({
          excluded_pool_ids: result.excluded_pool_ids.map((id) => Number(id)),
          included_pool_ids: result.included_pool_ids.map((id) => Number(id)),
        });
        setServerSnapshotsByCctv({});
      } catch (e) {
        console.error('통합 관제 감시 초기화 실패:', e);
        if (!cancelled) void refreshMonitoringAi();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [opsApiReady, patchMonitoringAi, refreshMonitoringAi]);
  const monitoringAiRef = useRef(monitoringAiSettings);
  monitoringAiRef.current = monitoringAiSettings;
  const poolIdsRef = useRef(poolIds);
  poolIdsRef.current = poolIds;
  const [excludeBusyPoolId, setExcludeBusyPoolId] = useState<number | null>(null);
  const totalMonitoringCctvs = useMemo(
    () =>
      monitoringPools.reduce(
        (sum, p) => sum + (poolCctvs[String(p.id)]?.length ?? 0),
        0
      ),
    [monitoringPools, poolCctvs]
  );

  const handleTogglePoolExclude = useCallback(
    async (poolId: number) => {
      const pid = Number(poolId);
      if (!Number.isFinite(pid)) return;
      if (excludeToggleInFlightRef.current.has(pid)) return;
      const excluded = !isCentralPoolMonitored(
        pid,
        monitoringAiSettings,
        poolIdsRef.current
      );
      excludeToggleInFlightRef.current.add(pid);
      setExcludeBusyPoolId(pid);
      try {
        const result = excluded
          ? await includePoolInCentralAi(pid)
          : await excludePoolFromCentralAi(pid);
        patchMonitoringAi({
          excluded_pool_ids: result.excluded_pool_ids.map((id) => Number(id)),
          included_pool_ids: result.included_pool_ids.map((id) => Number(id)),
        });
        if (excluded) {
          applyCentralAiDetectShowBboxDefaults();
          void pingCentralAiPresence().catch(() => {});
        }
        if (!excluded) {
          setServerSnapshotsByCctv((prev) => {
            const cctvs = poolCctvsRef.current[String(pid)] ?? [];
            if (!cctvs.length) return prev;
            const next = { ...prev };
            for (const c of cctvs) {
              delete next[serverSnapshotKey(pid, Number(c.id))];
            }
            return next;
          });
        }
      } catch (e) {
        console.error('시설 감시 제외 설정 실패:', e);
        setRiskAlert({
          type: 'warning',
          message: '시설 감시 ON/OFF 저장에 실패. 로그인·네트워크를 확인.',
        });
        void refreshMonitoringAi();
      } finally {
        excludeToggleInFlightRef.current.delete(pid);
        setExcludeBusyPoolId(null);
      }
    },
    [
      monitoringAiSettings.excluded_pool_ids,
      monitoringAiSettings.included_pool_ids,
      patchMonitoringAi,
      refreshMonitoringAi,
    ]
  );

  const centralServerInferenceActive =
    monitoringAiSettings.server_ai_enabled && !allVisiblePoolsExcluded;

  useEffect(() => {
    if (
      !opsApiReady ||
      !monitoringAiSettings.server_ai_enabled ||
      allVisiblePoolsExcluded
    ) {
      setServerSnapshotsByCctv({});
      return undefined;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const rows = await fetchCentralAiSnapshots();
        if (cancelled) return;
        const map: Record<string, ServerSnapshotPayload> = {};
        const visiblePoolIds = poolIdsRef.current;
        const visibleSet = new Set(visiblePoolIds.map((id) => Number(id)));
        rows.forEach((s) => {
          const pid = Number(s.pool_id);
          if (!Number.isFinite(pid)) return;
          if (visibleSet.size > 0 && !visibleSet.has(pid)) return;
          const cctvId = Number(s.cctv_id);
          if (!Number.isFinite(cctvId)) return;
          map[serverSnapshotKey(pid, cctvId)] = {
            detections: s.detections || [],
            risk_alerts: s.risk_alerts || [],
            mount_type: s.mount_type,
            frame_width: s.frame_width,
            frame_height: s.frame_height,
            updated_at: s.updated_at,
            openpose_overlay: parseOpenPoseOverlay(s.openpose_overlay),
            openpose_ran: Boolean(s.openpose_ran),
            pipeline_mode: String(
              (s as { pipeline_mode?: string }).pipeline_mode || ''
            ),
          };
        });
        setServerSnapshotsByCctv(map);
      } catch (e) {
        console.warn('서버 AI 스냅샷 폴링 실패:', e);
      }
    };
    void poll();
    const ms = Math.max(
      900,
      getDetectionCooldownMs(Math.max(1, totalMonitoringCctvs), monitoringAiRef.current)
    );
    const pollId = window.setInterval(() => void poll(), ms);
    return () => {
      cancelled = true;
      window.clearInterval(pollId);
    };
  }, [
    opsApiReady,
    allVisiblePoolsExcluded,
    monitoringAiSettings.server_ai_enabled,
    totalMonitoringCctvs,
    poolIds.join(','),
    monitoringAiSettings.included_pool_ids.join(','),
  ]);

  useEffect(() => {
    if (!cctvRotation) return;
    setCctvRotOverlayTick((n) => n + 1);
  }, [cctvRotation?.slideIndex, cctvRotation?.poolId, serverSnapshotsByCctv]);

  const poolCctvsRef = useRef(poolCctvs);
  poolCctvsRef.current = poolCctvs;
  const allPoolsMonitoringRef = useRef<AllPoolsMonitoringPopupHandle>(null);
  const useRemotePlaybackRef = useRef(useRemotePlayback);
  useRemotePlaybackRef.current = useRemotePlayback;
  const { isMobile, isCompactToolbar, effectivePoolColumns, stackCctvVertically } =
    useMonitoringViewport(poolsPerRow);
  const API_URL = API_ORIGIN;
  /** 실시간 이벤트(WebSocket)는 항상 ON — 예전 알림 토글 역할 */
  useEffect(() => {
    localStorage.setItem('centralMonitoringWebSocketEnabled', 'true');
    monitoringWebSocket.enableWebSocket();
    monitoringWebSocket.connectToControlCenter();
  }, []);

  /** 이벤트 발생 시 등록 채널로 알림 자동 발송 (기본 OFF) */
  const [autoAlertEnabled, setAutoAlertEnabled] = useState<boolean>(() => {
    return localStorage.getItem('centralMonitoringAutoAlertEnabled') === 'true';
  });

  // 해상도 옵션 정의
  const qualityOptions = [
    { label: '원본', value: 'original' },
    { label: '자동', value: 'auto' },
    { label: '1080p', value: '1080p' },
    { label: '720p', value: '720p' },
    { label: '480p', value: '480p' },
    { label: '360p', value: '360p' }
  ];

  // 버퍼 크기 옵션 (실제 hls.js maxBufferLength에 반영, 최대 15초)
  const bufferOptions = [
    { label: '최소 (6초)', value: 6 },
    { label: '짧음 (8초)', value: 8 },
    { label: '기본 (10초)', value: 10 },
    { label: '중간 (12초)', value: 12 },
    { label: '최대 (15초)', value: 15 },
  ];

  const bufferSizeHelpTitle = (
    <Box sx={{ maxWidth: 340, p: 0.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        화면(HLS) 버퍼
      </Typography>
      <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.45 }}>
        브라우저가 카메라마다 미리 받아 둘 영상 길이. 짧을수록 RAM·네트워크
        부하↓, 길수록 업로드 지연·끊김에 강해짐. 상한 15초. 끊기면 10~15초 권장
      </Typography>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        WebRTC 타일
      </Typography>
      <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.45 }}>
        초록 WebRTC 점은 WHEP 원본 재생. 해상도·화면 버퍼 선택은 HLS에만 적용되며
        WebRTC에는 반영되지 않음. 끊김·타일링은 카메라/NVR 인코딩·현장 WHIP
        ts_buffer(DATA_GATEWAY_WHIP_TS_BUFFER)를 brand-manager에서 시설별로 확인.
      </Typography>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        AI(OpenCV) 버퍼
      </Typography>
      <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
        서버 추론은 별도로 버퍼 1프레임 + 최신 프레임만 쓰도록 고정되어 있음.
        이 드롭다운으로 AI 쪽을 늘리지는 않음(지연·GPU 낭비 방지).
      </Typography>
    </Box>
  );

  // 설정값 변경 핸들러
  const handleGlobalQualityChange = (event: SelectChangeEvent) => {
    const newQuality = event.target.value;
    setGlobalQuality(newQuality);
    localStorage.setItem('globalQuality', newQuality);
  };

  const handleBufferSizeChange = (event: SelectChangeEvent<number>) => {
    const newSize = clampHlsBufferSec(Number(event.target.value));
    setBufferSize(newSize);
    localStorage.setItem('bufferSize', newSize.toString());
  };

  const handleColumnsChange = (event: SelectChangeEvent<number>) => {
    const newValue = event.target.value as number;
    setPoolsPerRow(newValue);
    localStorage.setItem('poolsPerRow', newValue.toString());
  };

  // 설정 초기화 함수
  const resetSettings = () => {
    // 기본값 설정
    const defaultSettings = {
      poolsPerRow: 3,
      globalQuality: '360p',
      bufferSize: HLS_BUFFER_SEC_DEFAULT,
    };

    // 상태 업데이트
    setPoolsPerRow(defaultSettings.poolsPerRow);
    setGlobalQuality(defaultSettings.globalQuality);
    setBufferSize(defaultSettings.bufferSize);

    // 로컬 스토리지 업데이트
    localStorage.setItem('poolsPerRow', defaultSettings.poolsPerRow.toString());
    localStorage.setItem('globalQuality', defaultSettings.globalQuality);
    localStorage.setItem('bufferSize', defaultSettings.bufferSize.toString());
  };

  const handleToggleAutoAlert = (nextEnabled: boolean) => {
    setAutoAlertEnabled(nextEnabled);
    localStorage.setItem('centralMonitoringAutoAlertEnabled', String(nextEnabled));
  };

  // 시설 목록 가져오기
  const fetchPools = async (): Promise<Pool[]> => {
    setLoading(true);
    setError(null);
    try {
      const response = await standardPoolApi.getAllPools();
      console.log('시설 목록 응답:', response);
      
      if (response.success) {
        let poolsData: Pool[] = [];
        const responseData = response.data as PoolResponse | Pool[] | undefined;
        
        if (Array.isArray(responseData)) {
          poolsData = responseData;
        } else if (responseData?.data && Array.isArray(responseData.data)) {
          poolsData = responseData.data;
        } else if (responseData?.pools && Array.isArray(responseData.pools)) {
          poolsData = responseData.pools;
        }
        
        console.log('처리된 시설 목록:', poolsData);
        
        if (poolsData.length > 0) {
          setPools(poolsData);
        } else {
          console.warn('시설 목록이 비어있습니다.');
          setPools([]);
        }
        return poolsData;
      } else {
        console.error('API 응답 실패:', response);
        throw new Error(response.message || '시설 목록을 가져오기 실패');
      }
    } catch (error: any) {
      console.error('시설 목록 로딩 실패:', error);
      setError(error.message || '시설 목록을 불러오는 중 오류 발생');
      // 기존 목록은 유지 — 새로고침 실패로 화면이 비지 않도록 함
      return [];
    } finally {
      setLoading(false);
    }
  };

  // 각 시설의 CCTV 목록 가져오기 (인증 apiClient — 시설 CCTV API와 동일)
  const fetchPoolCctvs = async (poolId: string | number) => {
    try {
      // apiClient.get 은 이미 response.data(본문)를 반환
      const payload = await api.get(`/api/pools/${poolId}/cctvs`, undefined, {
        useCache: false,
        silentError: true,
      });
      console.log(`시설 ${poolId}의 CCTV 목록 응답:`, payload);

      const cctvData = normalizeCctvListFromApiPayload(payload);
      console.log(`시설 ${poolId}의 처리된 CCTV 데이터:`, cctvData);

      const monitoringCctvs = filterCentralMonitoringCctvs(cctvData) as Cctv[];
      console.log(`시설 ${poolId}의 필터링된 CCTV 목록:`, monitoringCctvs);

      setPoolCctvs((prev) => ({
        ...prev,
        [String(poolId)]: monitoringCctvs,
      }));
    } catch (error) {
      console.error(`시설 ${poolId}의 CCTV 목록 로딩 실패:`, error);
      setPoolCctvs((prev) => ({
        ...prev,
        [String(poolId)]: [],
      }));
    }
  };

  // 초기 데이터 로딩
  useEffect(() => {
    fetchPools();
  }, []);

  useEffect(() => {
    let cancelled = false;
    void fetchPoolMonitorGrants()
      .then((grants) => {
        if (!cancelled) setPoolMonitorGrants(grants);
      })
      .catch(() => {
        if (!cancelled) setPoolMonitorGrants(null);
      });
    const timer = window.setInterval(() => {
      void fetchPoolMonitorGrants(true)
        .then((grants) => {
          if (!cancelled) setPoolMonitorGrants(grants);
        })
        .catch(() => undefined);
    }, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadPrimary = async () => {
      try {
        const slots = await fetchPrimaryPoolSlots();
        if (!cancelled) setPrimaryPoolSlots(slots);
      } catch {
        if (!cancelled) setPrimaryPoolSlots([]);
      }
    };
    void loadPrimary();
    const onUpdated = () => void loadPrimary();
    window.addEventListener(PRIMARY_POOLS_UPDATED_EVENT, onUpdated);
    return () => {
      cancelled = true;
      window.removeEventListener(PRIMARY_POOLS_UPDATED_EVENT, onUpdated);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadPlaybackConfig = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/gateway/playback-config`);
        if (cancelled) return;
        const nextRemote = Boolean(res.data?.use_remote_playback);
        const nextAvail = Boolean(res.data?.central_go2rtc_available);
        const nextStreams: string[] = Array.isArray(res.data?.central_go2rtc_streams)
          ? res.data.central_go2rtc_streams.map(String)
          : [];
        setUseRemotePlayback((prev) => (prev === nextRemote ? prev : nextRemote));
        setCentralGo2rtcAvailable((prev) => {
          const now = Date.now();
          if (nextAvail) {
            go2rtcAvailSeenAtRef.current = now;
            return true;
          }
          // probe 순단으로 false 가 되면 전 타일이 HLS 로 떨어져 끊김처럼 보임
          if (
            prev &&
            now - go2rtcAvailSeenAtRef.current < GO2RTC_AVAIL_HOLD_MS
          ) {
            return prev;
          }
          return false;
        });
        setCentralGo2rtcStreams((prev) => {
          const now = Date.now();
          const seen = go2rtcStreamSeenRef.current;
          // 이번에 보고된 스트림은 시각 갱신
          nextStreams.forEach((name) => {
            seen[name] = now;
          });
          // 빈 응답: 직전 목록 유지 (502·go2rtc 순단)
          if (nextStreams.length === 0 && prev.length > 0) {
            return prev;
          }
          // 일부만 빠진 경우에도 HOLD 동안 유지 (active_only 깜빡임 → HLS 흑화면 방지)
          const held = new Set(nextStreams);
          Object.keys(seen).forEach((name) => {
            const age = now - (seen[name] || 0);
            if (age <= GO2RTC_STREAM_HOLD_MS) {
              held.add(name);
            } else {
              delete seen[name];
            }
          });
          const merged = Array.from(held).sort();
          if (
            prev.length === merged.length &&
            prev.every((v, i) => v === merged[i])
          ) {
            return prev;
          }
          return merged;
        });
      } catch {
        // 502 등 — remote 플래그·스트림 목록을 비우지 않음 (관제 끊김 연쇄 방지)
      } finally {
        if (!cancelled) setPlaybackConfigReady(true);
      }
    };
    void loadPlaybackConfig();
    const timer = window.setInterval(loadPlaybackConfig, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [API_URL]);

  const poolIdsKey = useMemo(
    () => monitoringPools.map((p) => String(p.id)).filter(Boolean).join(','),
    [monitoringPools],
  );

  // 시설 ID가 바뀔 때만 CCTV 목록 로드 (참조 변경만으로 재요청하지 않음)
  useEffect(() => {
    if (!poolIdsKey) return;
    poolIdsKey.split(',').forEach((id) => {
      const num = Number(id);
      if (!Number.isNaN(num)) fetchPoolCctvs(num);
    });
  }, [poolIdsKey]);

  useEffect(() => {
    if (poolFullscreen) {
      setPoolFsIntervalInput(String(poolFullscreen.intervalSec));
    }
  }, [poolFullscreen]);

  // 전체화면 오버레이: 해당 시설 video 가 모두 재생 가능 상태가 될 때까지 대기
  useEffect(() => {
    if (!poolFullscreen) {
      setPoolFsVideosReady(false);
      return;
    }
    setPoolFsVideosReady(false);
    const pool = monitoringPools[poolFullscreen.poolIndex];
    const expected = pool
      ? (poolCctvs[String(pool.id)] ?? []).filter((c) => c && c.id != null).length
      : 0;
    if (expected <= 0) {
      setPoolFsVideosReady(true);
      return;
    }

    let cancelled = false;
    let timeoutId = 0;
    const startedAt = Date.now();
    const MAX_WAIT_MS = 90000;

    const isVideoReady = (v: HTMLVideoElement) => {
      if (v.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) return true;
      if (!v.paused && v.currentTime > 0.05) return true;
      return false;
    };

    const tick = () => {
      if (cancelled) return;
      const root = document.getElementById('pool-fullscreen-overlay');
      const videos = root
        ? Array.from(root.querySelectorAll('video'))
        : ([] as HTMLVideoElement[]);
      const readyCount = videos.filter(isVideoReady).length;
      if (videos.length >= expected && readyCount >= expected) {
        setPoolFsVideosReady(true);
        return;
      }
      if (Date.now() - startedAt >= MAX_WAIT_MS) {
        setPoolFsVideosReady(true);
        return;
      }
      timeoutId = window.setTimeout(tick, 400);
    };
    timeoutId = window.setTimeout(tick, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [poolFullscreen, monitoringPools, poolCctvs]);

  const enterPoolFullscreen = useCallback(
    (poolId: number | string) => {
      if (!playbackConfigReady) {
        alert('재생 설정을 불러오는 중. 잠시 후 다시 시도');
        return;
      }
      const idx = monitoringPools.findIndex(
        (p) => String(p.id) === String(poolId)
      );
      if (idx < 0) {
        alert('시설 정보를 찾기 불가');
        return;
      }
      const cctvs = poolCctvs[String(monitoringPools[idx].id)] ?? [];
      if (cctvs.length === 0) {
        alert('표시할 관제 CCTV가 없음');
        return;
      }
      const sec = readMonitoringPoolGridRotationIntervalSec();
      setPoolFsVideosReady(false);
      setCctvRotation(null);
      setPoolFullscreen({
        poolIndex: idx,
        paused: true, // 롤링 초기 OFF
        intervalSec: sec,
      });
    },
    [monitoringPools, poolCctvs, playbackConfigReady]
  );

  const exitPoolFullscreen = useCallback(() => {
    setPoolFullscreen(null);
    try {
      const doc = document as Document & {
        webkitFullscreenElement?: Element | null;
        webkitExitFullscreen?: () => Promise<void>;
      };
      if (doc.fullscreenElement || doc.webkitFullscreenElement) {
        const exit = document.exitFullscreen || doc.webkitExitFullscreen;
        if (exit) void exit.call(document);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const shiftPoolFullscreen = useCallback(
    (delta: number, pauseAfter: boolean) => {
      setPoolFullscreen((prev) => {
        if (!prev || monitoringPools.length < 2) return prev;
        const next =
          (prev.poolIndex + delta + monitoringPools.length) %
          monitoringPools.length;
        return {
          ...prev,
          poolIndex: next,
          paused: pauseAfter ? true : prev.paused,
        };
      });
    },
    [monitoringPools.length]
  );

  const exitCctvRotation = useCallback(() => {
    setCctvRotation(null);
    setCctvRotVideoReady(false);
  }, []);

  const enterCctvRotation = useCallback(
    (
      poolIdOrCode: number | string,
      opts?: {
        intervalSec?: number;
        cctvId?: number;
        zoneIndex?: number;
        zoneTilesOnly?: boolean;
        startPaused?: boolean;
      }
    ) => {
      if (!playbackConfigReady) {
        alert('재생 설정을 불러오는 중. 잠시 후 다시 시도');
        return;
      }
      const token = String(poolIdOrCode || '').trim();
      const pool =
        monitoringPools.find((p) => String(p.id) === token) ||
        monitoringPools.find(
          (p) => String(p.code || '').toLowerCase() === token.toLowerCase()
        );
      if (!pool?.id) {
        alert('시설 정보를 찾기 불가');
        return;
      }
      const siteGw = Boolean(
        (pool as Pool & { site_gateway_enabled?: boolean }).site_gateway_enabled
      );
      const zoneTilesOnly =
        opts?.zoneTilesOnly != null
          ? Boolean(opts.zoneTilesOnly)
          : Boolean(
              (pool as Pool & { monitor_zone_tiles_only?: boolean })
                .monitor_zone_tiles_only
            );
      const list = [...(poolCctvs[String(pool.id)] ?? [])].sort(
        cctvLocationNaturalCompare
      );
      const withUrl = list.filter((c) => {
        const url = resolveAbsoluteCctvStreamUrl(c, useRemotePlayback, siteGw);
        return Boolean(url);
      });
      if (withUrl.length === 0) {
        alert('순환 재생할 CCTV 스트림 없음');
        return;
      }
      let slides: CctvRotationSlide[];
      if (zoneTilesOnly) {
        const tiles = listMonitoringZoneTiles(withUrl);
        slides = tiles.map((t) => ({
          cctvId: Number(t.cctv.id),
          zoneIndex: t.zoneIndex,
          viewCrop: t.viewCrop,
        }));
        if (slides.length === 0) {
          alert('감시구역 박스가 지정된 CCTV가 없음');
          return;
        }
      } else {
        slides = withUrl.map((c) => ({
          cctvId: Number(c.id),
          zoneIndex: 0,
        }));
      }
      let slideIndex = 0;
      if (opts?.cctvId != null && Number.isFinite(opts.cctvId)) {
        const wantZi = opts.zoneIndex;
        const found = slides.findIndex((s) => {
          if (s.cctvId !== Number(opts.cctvId)) return false;
          if (wantZi == null || !Number.isFinite(wantZi)) return true;
          return s.zoneIndex === Number(wantZi);
        });
        if (found >= 0) slideIndex = found;
      }
      const sec = Math.min(
        300,
        Math.max(
          1,
          Math.floor(
            opts?.intervalSec ??
              rotationIntervalSec ??
              readMonitoringRotationIntervalSec()
          ) || 5
        )
      );
      // 시설 전체화면 위에 CCTV 롤링을 올린 경우, 돌아가기 시 전체화면으로 복귀하도록
      // poolFullscreen 은 유지한다 (그리드에서 바로 롤링 진입 시에는 원래 null).
      setCctvRotVideoReady(false);
      setCctvRotIntervalInput(String(sec));
      setCctvRotation({
        poolId: Number(pool.id),
        poolName: pool.name || pool.code || `시설 #${pool.id}`,
        siteGatewayEnabled: siteGw,
        slideIndex,
        paused: Boolean(opts?.startPaused),
        intervalSec: sec,
        slides,
        zoneTilesOnly,
      });
    },
    [
      monitoringPools,
      poolCctvs,
      playbackConfigReady,
      useRemotePlayback,
      rotationIntervalSec,
    ]
  );

  const shiftCctvRotation = useCallback(
    (delta: number, pauseAfter: boolean) => {
      setCctvRotVideoReady(false);
      setCctvRotation((prev) => {
        if (!prev || prev.slides.length < 1) return prev;
        const next =
          (prev.slideIndex + delta + prev.slides.length) % prev.slides.length;
        return {
          ...prev,
          slideIndex: next,
          paused: pauseAfter ? true : prev.paused,
        };
      });
    },
    []
  );

  // 시설 전체화면 자동 롤링 — OFF가 아니고, CCTV가 다 올라온 뒤에만
  useEffect(() => {
    if (!poolFullscreen) return;
    if (poolFullscreen.paused) return;
    if (!poolFsVideosReady) return;
    if (monitoringPools.length < 2) return;
    const ms = Math.max(1, poolFullscreen.intervalSec) * 1000;
    const t = window.setInterval(() => {
      shiftPoolFullscreen(1, false);
    }, ms);
    return () => window.clearInterval(t);
  }, [
    poolFullscreen,
    poolFsVideosReady,
    monitoringPools.length,
    shiftPoolFullscreen,
  ]);

  // CCTV 순환 오버레이: 현재 영상 준비 대기
  useEffect(() => {
    if (!cctvRotation) {
      setCctvRotVideoReady(false);
      return;
    }
    setCctvRotVideoReady(false);
    let cancelled = false;
    let timeoutId = 0;
    const startedAt = Date.now();
    const tick = () => {
      if (cancelled) return;
      const root = document.getElementById('cctv-rotation-overlay');
      const video = root?.querySelector('video') as HTMLVideoElement | null;
      if (
        video &&
        (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA ||
          (!video.paused && video.currentTime > 0.05))
      ) {
        setCctvRotVideoReady(true);
        return;
      }
      if (Date.now() - startedAt >= 60000) {
        setCctvRotVideoReady(true);
        return;
      }
      timeoutId = window.setTimeout(tick, 400);
    };
    timeoutId = window.setTimeout(tick, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [cctvRotation?.poolId, cctvRotation?.slideIndex, cctvRotation]);

  // CCTV 순환 자동 롤링
  useEffect(() => {
    if (!cctvRotation) return;
    if (cctvRotation.paused) return;
    if (!cctvRotVideoReady) return;
    if (cctvRotation.slides.length < 2) return;
    const ms = Math.max(1, cctvRotation.intervalSec) * 1000;
    const t = window.setInterval(() => {
      shiftCctvRotation(1, false);
    }, ms);
    return () => window.clearInterval(t);
  }, [cctvRotation, cctvRotVideoReady, shiftCctvRotation]);

  // 시설 전체화면 키보드
  useEffect(() => {
    if (!poolFullscreen || cctvRotation) return;
    const onKey = (ev: KeyboardEvent) => {
      const tag = (ev.target as HTMLElement | null)?.tagName?.toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (ev.key === 'Escape') {
        ev.preventDefault();
        exitPoolFullscreen();
        return;
      }
      if (ev.key === ' ' || ev.key === 'Spacebar') {
        ev.preventDefault();
        setPoolFullscreen((p) => (p ? { ...p, paused: !p.paused } : p));
        return;
      }
      if (ev.key === 'ArrowLeft') {
        ev.preventDefault();
        shiftPoolFullscreen(-1, true);
        return;
      }
      if (ev.key === 'ArrowRight') {
        ev.preventDefault();
        shiftPoolFullscreen(1, true);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [poolFullscreen, cctvRotation, exitPoolFullscreen, shiftPoolFullscreen]);

  // CCTV 순환 키보드
  useEffect(() => {
    if (!cctvRotation) return;
    const onKey = (ev: KeyboardEvent) => {
      const tag = (ev.target as HTMLElement | null)?.tagName?.toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (ev.key === 'Escape') {
        ev.preventDefault();
        exitCctvRotation();
        return;
      }
      if (ev.key === ' ' || ev.key === 'Spacebar') {
        ev.preventDefault();
        setCctvRotation((p) => (p ? { ...p, paused: !p.paused } : p));
        return;
      }
      if (ev.key === 'ArrowLeft') {
        ev.preventDefault();
        shiftCctvRotation(-1, true);
        return;
      }
      if (ev.key === 'ArrowRight') {
        ev.preventDefault();
        shiftCctvRotation(1, true);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [cctvRotation, exitCctvRotation, shiftCctvRotation]);

  useEffect(() => {
    window.__openCentralSinglePoolMonitoring = (poolId: string) => {
      exitCctvRotation();
      enterPoolFullscreen(poolId);
    };

    window.__openCentralPoolRotationMonitoring = (
      poolIdOrCode: string,
      intervalSec?: number,
      cctvId?: number,
      startPaused?: boolean
    ) => {
      enterCctvRotation(poolIdOrCode, {
        intervalSec,
        cctvId,
        startPaused: Boolean(startPaused),
      });
    };

    window.__getMonitoringPopupWindowFeatures = () => getMonitoringPopupWindowFeatures();

    window.__placeMonitoringPopup = (popup: Window) => {
      focusMonitoringPopupWindow(popup);
      void placeMonitoringPopupOnPreferredScreen(popup).then(() => {
        focusMonitoringPopupWindow(popup);
      });
    };

    return () => {
      delete window.__openCentralSinglePoolMonitoring;
      delete window.__openCentralPoolRotationMonitoring;
      delete window.__getMonitoringPopupWindowFeatures;
      delete window.__placeMonitoringPopup;
    };
  }, [enterPoolFullscreen, enterCctvRotation, exitCctvRotation]);

  // 뒤로 가기 / 팝업 닫기
  const centralMonitoringPopup = isCentralMonitoringPopupWindow();

  const handleLeaveMonitoring = useCallback(async () => {
    await endCentralMonitoringSession();
  }, []);

  const handleCloseMonitoringWindow = () => {
    void handleLeaveMonitoring().finally(() => {
      window.close();
    });
  };

  // 새로고침 — 시설·CCTV·재생설정 재로드 + 영상 스트림 강제 재연결
  const handleRefresh = () => {
    if (refreshing) return;
    void (async () => {
      setRefreshing(true);
      setError(null);
      try {
        try {
          const res = await axios.get(`${API_URL}/api/gateway/playback-config`);
          setUseRemotePlayback(Boolean(res.data?.use_remote_playback));
        } catch {
          setUseRemotePlayback(false);
        } finally {
          setPlaybackConfigReady(true);
        }

        try {
          const st = await fetchGpuAllocationStatus();
          setGpuAllocationStatus(st);
        } catch {
          /* keep previous */
        }

        const poolsData = await fetchPools();
        let list = poolsData.filter((p) =>
          isNormalOperatingPoolStatus(
            p.status,
            (p as Pool & { is_active?: boolean }).is_active,
          ),
        );
        list = list.filter((p) =>
          isPoolMonitorGranted(p.id, monitorViewerGrade, poolMonitorGrants),
        );
        if (assignedAdminIdFilter != null) {
          list = list.filter((p) => {
            const raw = (p as Pool & { monitoring_admin_id?: number | null })
              .monitoring_admin_id;
            if (raw == null || Number(raw) <= 0) return true;
            return Number(raw) === assignedAdminIdFilter;
          });
        }
        const reloadIds =
          list.length > 0
            ? list.map((p) => p.id)
            : poolIdsKey.split(',').filter(Boolean);
        await Promise.all(
          reloadIds.map((id) => {
            const num = Number(id);
            return Number.isNaN(num) ? Promise.resolve() : fetchPoolCctvs(num);
          }),
        );

        // URL이 같아도 플레이어가 HLS를 다시 붙이도록 신호
        setStreamReloadNonce((n) => n + 1);
        setPlaybackResumeNonce((n) => n + 1);
      } finally {
        setRefreshing(false);
      }
    })();
  };

  // 상태 표시 텍스트 변환 함수
  const getStatusText = (status: string | undefined): string => {
    if (!status) return '알 수 없음';
    
    const lowerStatus = status.toLowerCase();
    
    if (lowerStatus.includes('inactive')) return '비활성';
    if (lowerStatus.includes('maintenance')) return '유지보수 중';
    if (
      lowerStatus.includes('active') ||
      lowerStatus === 'open' ||
      lowerStatus === 'operating'
    ) {
      return '정상';
    }
    
    return status;
  };

  // 시설 상태에 따른 색상
  const getPoolStatusColor = (status: string | undefined): string => {
    if (!status) return '#9e9e9e';
    
    const lowerStatus = status.toLowerCase();
    
    if (lowerStatus.includes('inactive')) return '#f44336';
    if (lowerStatus.includes('maintenance')) return '#ff9800';
    if (
      lowerStatus.includes('active') ||
      lowerStatus === 'open' ||
      lowerStatus === 'operating'
    ) {
      return '#4caf50';
    }
    
    return '#9e9e9e';
  };

  // 가상 이벤트 핸들러
  const handleVirtualEvent = async (poolId: string | number) => {
    try {
      const poolIdStr = String(poolId);
      console.log('가상 이벤트 시뮬레이션:', poolIdStr);
      
      // CCTV 목록 확인
      const cctvList = poolCctvs[poolIdStr] || [];
      console.log('CCTV 목록:', cctvList);
      
      if (cctvList.length > 0) {
        const cctv = cctvList[0];
        console.log('선택된 CCTV:', cctv);
        
        // 스트림 URL 확인
        const poolRow = pools.find((p) => String(p.id) === poolIdStr);
        const siteGw = Boolean(
          (poolRow as Pool & { site_gateway_enabled?: boolean } | undefined)?.site_gateway_enabled
        );
        const streamUrl = resolveAbsoluteCctvStreamUrl(cctv, useRemotePlayback, siteGw);
        console.log('CCTV 스트림 URL:', streamUrl);
        
        if (!streamUrl) {
          alert('CCTV 스트림 URL이 설정되지 않음');
          return;
        }
        
        // 16:9 비율로 창 크기 계산 (1280x720)
        const width = 1280;
        const height = 720;
        
        // 현재 브라우저 창의 중앙에 위치하도록 계산
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        
        const windowFeatures = `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no,scrollbars=no,resizable=no,transparent=yes`;
        const newWindow = window.open('', 'cctv_monitor', windowFeatures);
        
        if (!newWindow) {
          alert('팝업 창을 여기 불가. 브라우저 설정에서 팝업 허용을 확인.');
          return;
        }

        const baseUrl = API_ORIGIN;
        const htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>${cctv.name} - 이벤트 모니터링</title>
            <style>
              body, html { 
                margin: 0; 
                padding: 0; 
                background-color: transparent; 
                color: white; 
                font-family: Arial;
                overflow: hidden;
                width: 100vw;
                height: 100vh;
              }
              .cctv-container {
                position: relative;
                width: 100%;
                height: 100%;
                display: flex;
                flex-direction: column;
                background-color: rgba(0, 0, 0, 0.8);
                backdrop-filter: blur(5px);
                -webkit-backdrop-filter: blur(5px);
              }
              .cctv-header {
                background-color: rgba(255, 255, 255, 0.95);
                padding: 8px;
                font-size: 14px;
                font-weight: bold;
                border-bottom: 1px solid rgba(85, 85, 85, 0.5);
                display: flex;
                justify-content: space-between;
                align-items: center;
                color: #000000;
              }
              .cctv-header span {
                color: #000000;
              }
              .cctv-header span:last-child {
                color: #666;
              }
              .cctv-content {
                flex: 1;
                position: relative;
                background-color: rgba(0, 0, 0, 0.9);
                aspect-ratio: 16/9;
              }
              video {
                width: 100%;
                height: 100%;
                object-fit: contain;
                background-color: transparent;
              }
              .event-info {
                position: absolute;
                top: 10px;
                right: 10px;
                background: rgba(255, 0, 0, 0.7);
                padding: 5px 10px;
                border-radius: 4px;
                font-size: 12px;
                animation: blink 1s infinite;
                backdrop-filter: blur(2px);
                -webkit-backdrop-filter: blur(2px);
              }
              @keyframes blink {
                0% { opacity: 1; }
                50% { opacity: 0.5; }
                100% { opacity: 1; }
              }
            </style>
            <script src="https://cdn.jsdelivr.net/npm/hls.js@latest"></script>
          </head>
          <body>
            <div class="cctv-container">
              <div class="cctv-header">
                <span>${cctv.name} - 이벤트 발생</span>
                <span style="font-size: 12px; color: #888;">${new Date().toLocaleString()}</span>
              </div>
              <div class="cctv-content">
                <video id="cctv-video" muted playsinline></video>
                <div class="event-info">이벤트 발생</div>
              </div>
            </div>
            <script>
              document.addEventListener('DOMContentLoaded', function() {
                const video = document.getElementById('cctv-video');
                const streamUrl = '${streamUrl}';
                console.log('스트림 URL:', streamUrl);
                
                if (Hls.isSupported()) {
                  console.log('HLS 지원됨');
                  const hls = new Hls({
                    enableWorker: true,
                    lowLatencyMode: true,
                    backBufferLength: ${bufferSize},
                    maxBufferSize: ${bufferSize} * 1000 * 1000,
                    maxBufferLength: ${bufferSize},
                    maxMaxBufferLength: ${bufferSize},
                    debug: true
                  });
                  
                  hls.on(Hls.Events.ERROR, function(event, data) {
                    console.error('HLS 에러:', data);
                    if (data.fatal) {
                      switch(data.type) {
                        case Hls.ErrorTypes.NETWORK_ERROR:
                          console.log('네트워크 오류, 재시도 중...');
                          hls.startLoad();
                          break;
                        case Hls.ErrorTypes.MEDIA_ERROR:
                          console.log('미디어 오류, 복구 시도 중...');
                          hls.recoverMediaError();
                          break;
                        default:
                          console.log('복구 불가능한 오류');
                          hls.destroy();
                          break;
                      }
                    }
                  });
                  
                  hls.loadSource(streamUrl);
                  hls.attachMedia(video);
                  
                  hls.on(Hls.Events.MANIFEST_PARSED, () => {
                    console.log('HLS 매니페스트 파싱 완료');
                    video.play().catch(e => {
                      console.error('비디오 재생 실패:', e);
                    });
                  });
                } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
                  console.log('Safari 네이티브 HLS 지원');
                  video.src = streamUrl;
                  video.addEventListener('loadedmetadata', () => {
                    video.play().catch(e => {
                      console.error('비디오 재생 실패:', e);
                    });
                  });
                } else {
                  console.error('HLS를 지원하지 않는 브라우저입니다.');
                }

                // 10초 후 창 닫기
                setTimeout(() => {
                  window.close();
                }, 10000);
              });
            </script>
          </body>
          </html>
        `;

        newWindow.document.write(htmlContent);
        newWindow.document.close();
      } else {
        console.warn('CCTV 목록이 비어있습니다.');
        alert('해당 시설에 CCTV가 없음');
      }
    } catch (error: any) {
      console.error('가상 이벤트 생성 오류:', error);
      alert('가상 이벤트 생성 중 오류 발생');
    }
  };

  // 시설 그리드 — useMemo로 컴포넌트 타입 고정(부모 3초 폴링 시 전체 CCTV remount 방지)
  const PoolGridItem = useMemo(() => {
    const PoolGridItemComponent: React.FC<{
      pool: Pool;
      cctvs: Cctv[];
      poolsPerRow: number;
      wearableTrackMap: TrackConfidenceMap;
      bufferSize: number;
      globalQuality: string;
      playbackConfigReady: boolean;
      useRemotePlayback: boolean;
      centralGo2rtcAvailable: boolean;
      centralGo2rtcStreams: string[];
      monitoringAiSettings: MonitoringAiSettings;
      centralOperatingPoolIds: number[];
      serverSnapshotsByCctv: Record<string, ServerSnapshotPayload>;
      excludeBusyPoolId: number | null;
      onTogglePoolExclude: (poolId: number) => void;
      gpuAllocationStatus: GpuAllocationStatus | null;
      serverInferenceActive: boolean;
      onCctvGpuChanged: (poolId: number, cctvId: number, gpuIndex: number) => void;
      rotationIntervalSec: number;
      totalInferCctvCount: number;
      inferGpuCount: number;
      playbackResumeNonce: number;
      streamReloadNonce: number;
      riskDialogOpen: boolean;
      stackCctvVertically?: boolean;
      displayPoolColumns?: number;
      /** 동일 창 시설 전체화면 진입 */
      onEnterPoolFullscreen?: (poolId: number) => void;
      /** 동일 창 CCTV 순환 진입 */
      onEnterCctvRotation?: (
        poolId: number,
        opts?: {
          cctvId?: number;
          zoneIndex?: number;
          /** 관제 화면 토글 값 (없으면 시설 설정) */
          zoneTilesOnly?: boolean;
          startPaused?: boolean;
          intervalSec?: number;
        }
      ) => void;
      /** true면 CCTV 셀 클릭 → 순환 보기 비활성 */
      disableCctvRotationClick?: boolean;
      /** 전체화면 오버레이 안에서는 전체화면 버튼 숨김 */
      hideFullscreenButton?: boolean;
    }> = ({
      pool,
      cctvs,
      poolsPerRow,
      wearableTrackMap,
      bufferSize,
      globalQuality,
      playbackConfigReady,
      useRemotePlayback,
      centralGo2rtcAvailable,
      centralGo2rtcStreams,
      monitoringAiSettings,
      centralOperatingPoolIds,
      serverSnapshotsByCctv,
      excludeBusyPoolId,
      onTogglePoolExclude,
      gpuAllocationStatus,
      serverInferenceActive,
      onCctvGpuChanged,
      rotationIntervalSec,
      totalInferCctvCount,
      inferGpuCount,
      playbackResumeNonce,
      streamReloadNonce,
      riskDialogOpen,
      stackCctvVertically = false,
      displayPoolColumns,
      onEnterPoolFullscreen,
      onEnterCctvRotation,
      disableCctvRotationClick = false,
      hideFullscreenButton = false,
    }) => {
    const siteGatewayEnabled = Boolean(
      (pool as Pool & { site_gateway_enabled?: boolean }).site_gateway_enabled
    );
    const poolId = pool.id;
    const facilityZoneTilesDefault = Boolean(
      (pool as Pool & { monitor_zone_tiles_only?: boolean }).monitor_zone_tiles_only
    );
    const [zoneTilesOnly, setZoneTilesOnly] = useState(facilityZoneTilesDefault);
    useEffect(() => {
      setZoneTilesOnly(facilityZoneTilesDefault);
    }, [pool.id, facilityZoneTilesDefault]);
    const safetyCctvs = cctvs.length > 0 ? cctvs : [];
    const hasCctvs = safetyCctvs.length > 0;
    const videoRefs = useRef<{ [key: string]: HTMLVideoElement | null }>({});
    const playerHandlesRef = useRef<{ [key: number]: CentralCctvPlayerHandle | null }>({});
    const overlayContainerRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const overlayContainerElRef = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const overlayResizeObservers = useRef<{ [key: string]: ResizeObserver }>({});
    const [overlayLayoutTick, setOverlayLayoutTick] = useState(0);
    const detectionDebugRef = useRef<{
      [cctvId: number]: { raw: number; shown: number; pipelineMode?: string };
    }>({});
    const poolHasServerSnapshot = useMemo(
      () =>
        safetyCctvs.some((c) => {
          const cid = Number(c.id);
          return (
            Number.isFinite(cid) &&
            lookupServerSnapshot(
              serverSnapshotsByCctv,
              Number(poolId),
              cid
            ) != null
          );
        }),
      [serverSnapshotsByCctv, safetyCctvs, poolId]
    );
    const poolExcluded = !isCentralPoolAiOverlayEligible(
      Number(poolId),
      monitoringAiSettings,
      centralOperatingPoolIds,
      poolHasServerSnapshot
    );
    const isMonitored = !poolExcluded;
    const [labelMode, setLabelMode] = useState<DetectionLabelMode>('off');
    const [labelFeedback, setLabelFeedback] = useState<string | null>(null);
    const isDetectingRef = useRef<boolean>(false);
    const [lastDetectionTime, setLastDetectionTime] = useState<number>(0);
    const detectionIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
      null
    );
    const poolKey = String(poolId ?? '');
    const ALLOWED_CLASSES = new Set(['swimmer']);
    const MIN_MOVE_PX = 4;
    const MIN_MOTION_SCORE = 0.2;
    const DEDUP_IOU_THRESHOLD = 0.42;
    const nextTrackIdRef = useRef<number>(1);
    const tracksRef = useRef<{ [cctvId: number]: any[] }>({});
    const [visibleClassMap, setVisibleClassMap] = useState<Record<string, boolean>>({
      person: true,
      swimmer: true,
      drowning: false,
      lifeguard: false,
    });
    useEffect(() => {
      if (!isMonitored) return;
      applyCentralAiDetectShowBboxDefaults();
      setVisibleClassMap({
        person: true,
        swimmer: true,
        drowning: true,
        lifeguard: true,
      });
    }, [isMonitored]);
    const detectionQueue = useRef<number[]>([]); // 감지 대기열
    const detectionQueueBusy = useRef(false); // 큐에서 한 건씩만 처리
    const processingDetections = useRef<Set<number>>(new Set()); // 처리 중인 감지
    const detectionCache = useRef<Map<number, {data: any, timestamp: number}>>(new Map()); // 감지 결과 캐시
    const CACHE_DURATION = 0; // 실시간성 우선: 캐시 비활성화
    const performanceMetrics = useRef<{
      totalDetections: number,
      averageProcessingTime: number,
      lastProcessingTimes: number[]
    }>({
      totalDetections: 0,
      averageProcessingTime: 0,
      lastProcessingTimes: []
    });
    const bboxMapRef = useRef<{ [cctvId: number]: any[] }>({});
    const displayTracksRef = useRef<{ [cctvId: number]: Map<number, DisplayTrackState> }>({});
    const [displayTracksSnapshot, setDisplayTracksSnapshot] = useState<{
      [cctvId: number]: DisplayTrackState[];
    }>({});
    const detectionStreakRef = useRef<{ [cctvId: number]: Map<string, number> }>({});
    const motionRiskHighlightRef = useRef<
      Record<number, Map<number, { meta: MotionRiskTrackMeta; until: number }>>
    >({});
    const [, forceUpdate] = useState(0);

    const syncDisplayTracksFromApi = (
      cctvId: number,
      detections: any[],
      riskByTrackId?: Map<number, MotionRiskTrackMeta>
    ) => {
      if (!displayTracksRef.current[cctvId]) {
        displayTracksRef.current[cctvId] = new Map();
      }
      const map = displayTracksRef.current[cctvId];
      const seen = new Set<number>();
      let fallbackId = 1;
      for (const det of detections) {
        if (!Array.isArray(det?.bbox) || det.bbox.length !== 4) continue;
        const miss = Number(det.miss_count ?? 0);
        if (miss > maxServerMissForDisplay(monitoringAiSettings)) continue;
        let tid = Number(det.track_id ?? det.id);
        if (!Number.isFinite(tid)) {
          tid = 1_000_000 + fallbackId;
          fallbackId += 1;
        }
        const className = (det.class_name || 'person').toLowerCase();
        const detScore = Number(det.score ?? det.confidence ?? 0);
        const riskMeta = riskByTrackId?.get(tid);
        const motionRisk = Boolean(riskMeta);
        if (visibleClassMap[className] === false && !motionRisk) continue;
        seen.add(tid);
        const prev = map.get(tid);
        const rawTarget = det.bbox.map(Number);
        const nowMs = performance.now();
        let target = rawTarget;
        if (prev?.targetBbox) {
          const rawJump = bboxDeltaMax(prev.targetBbox, rawTarget);
          if (rawJump >= DISPLAY_TARGET_REJECT_JUMP_PX) {
            target = prev.targetBbox;
          } else {
            target = lerpBbox(prev.targetBbox, rawTarget, DISPLAY_TARGET_SMOOTH);
          }
        }
        const [velocityX, velocityY] = resolveTrackVelocityPxPerSec(det, prev, target, nowMs);
        const [tx1, ty1, tx2, ty2] = target;
        const swimWide = (tx2 - tx1) / Math.max(1, ty2 - ty1) >= 1.2;
        let displayBbox = prev?.displayBbox ?? target;
        if (prev?.displayBbox) {
          const jump = bboxDeltaMax(prev.displayBbox, target);
          const snap =
            miss > 0 || jump > 55
              ? swimWide
                ? 0.9
                : 0.82
              : jump > 90
                ? 0.72
                : 0;
          if (snap > 0) {
            displayBbox = lerpBbox(prev.displayBbox, target, snap);
          }
        }
        map.set(tid, {
          trackId: tid,
          targetBbox: target,
          displayBbox,
          class_name: det.class_name || 'person',
          score: Number(det.score ?? det.confidence ?? 0),
          miss_count: miss,
          frame_width: det.frame_width,
          frame_height: det.frame_height,
          alpha: miss > 0 ? DISPLAY_TRACK_MISS_FADE : 1,
          motionRisk,
          riskLevel: riskMeta?.level,
          riskSubtype: riskMeta?.riskType,
          riskSubtypeLabel: riskMeta?.riskLabel,
          borderColor: motionRisk
            ? resolveRiskAppearance(riskMeta?.riskType, {
                label: riskMeta?.riskLabel,
              }).color
            : undefined,
          displayLocalMiss: 0,
          velocityX,
          velocityY,
          serverUpdatedAt: nowMs,
        });
      }
      for (const tid of [...map.keys()]) {
        if (seen.has(tid)) continue;
        const t = map.get(tid);
        if (!t) continue;
        const nextLocalMiss = (t.displayLocalMiss ?? 0) + 1;
        if (nextLocalMiss > DISPLAY_LOCAL_MAX_MISS) {
          map.delete(tid);
          continue;
        }
        t.displayLocalMiss = nextLocalMiss;
        t.alpha = Math.max(0.3, 1 - nextLocalMiss * 0.12);
      }
      setDisplayTracksSnapshot((prev) => ({
        ...prev,
        [cctvId]: Array.from(map.values()),
      }));
    };

    const tickDisplayTrackInterpolation = () => {
      let changed = false;
      const nextSnap: { [cctvId: number]: DisplayTrackState[] } = {};
      for (const cctv of safetyCctvs) {
        const cid = Number(cctv.id);
        const map = displayTracksRef.current[cid];
        if (!map || map.size === 0) continue;
        map.forEach((t) => {
          const jump = bboxDeltaMax(t.displayBbox, t.targetBbox);
          let lerpTarget = t.targetBbox;
          const speed = Math.hypot(t.velocityX ?? 0, t.velocityY ?? 0);
          if (
            DISPLAY_TRACK_PREDICT_MAX_SEC > 0 &&
            t.miss_count <= 0 &&
            t.serverUpdatedAt != null &&
            Number.isFinite(t.velocityX) &&
            Number.isFinite(t.velocityY) &&
            speed >= DISPLAY_TRACK_PREDICT_MIN_SPEED_PX &&
            speed <= DISPLAY_TRACK_PREDICT_MAX_SPEED_PX
          ) {
            const dtSec = Math.min(
              DISPLAY_TRACK_PREDICT_MAX_SEC,
              Math.max(0, (performance.now() - t.serverUpdatedAt) / 1000)
            );
            if (dtSec > 0.02) {
              lerpTarget = extrapolateBboxByVelocity(
                t.targetBbox,
                t.velocityX!,
                t.velocityY!,
                dtSec
              );
            }
          }
          const lerpT =
            jump > 90
              ? DISPLAY_TRACK_LERP_FAST
              : jump > 45
                ? 0.38
                : DISPLAY_TRACK_LERP;
          const next = lerpBbox(t.displayBbox, lerpTarget, lerpT);
          if (bboxDeltaMax(t.displayBbox, next) > 0.4) changed = true;
          t.displayBbox = next;
        });
        nextSnap[cid] = Array.from(map.values());
      }
      if (changed) {
        setDisplayTracksSnapshot((prev) => ({ ...prev, ...nextSnap }));
      }
    };

    const isLikelyClothingFragment = (
      bbox: number[],
      conf: number,
      frameW: number,
      frameH: number
    ) => {
      const [x1, y1, x2, y2] = bbox;
      const w = Math.max(0, x2 - x1);
      const h = Math.max(0, y2 - y1);
      const aspect = w / Math.max(1, h);
      const areaRatio = (w * h) / Math.max(1, frameW * frameH);
      const longest = Math.max(w, h);
      const shortest = Math.min(w, h);
      if (areaRatio < 0.0010 && longest < 36) return true;
      if (
        areaRatio < 0.0016 &&
        longest < 46 &&
        shortest < 24 &&
        aspect >= 0.5 &&
        aspect <= 1.45 &&
        conf < 0.78
      ) {
        return true;
      }
      return false;
    };

    const isLikelyWaterRippleStatic = (
      bbox: number[],
      conf: number,
      frameW: number,
      frameH: number
    ) => {
      const [x1, y1, x2, y2] = bbox;
      const w = Math.max(0, x2 - x1);
      const h = Math.max(0, y2 - y1);
      if (w < 8 || h < 8) return true;
      const aspect = w / Math.max(1, h);
      const areaRatio = (w * h) / Math.max(1, frameW * frameH);
      if (areaRatio >= 0.002 && conf >= 0.28 && aspect <= 4.5) return false;
      if (h < 26 && aspect > 1.8) return true;
      if (areaRatio < 0.0012 && aspect > 2.2) return true;
      if (conf < 0.48 && h < 28 && aspect > 2.4) return true;
      return false;
    };

    const isLikelyWaterRipple = (bbox: number[], conf: number, frameW: number, frameH: number) => {
      if (isLikelyWaterGlareReflection(bbox, conf, frameW, frameH)) return true;
      return isLikelyWaterRippleStatic(bbox, conf, frameW, frameH);
    };

    const isLikelyPoolLaneLine = (
      bbox: number[],
      conf: number,
      frameW: number,
      frameH: number
    ) => {
      const [x1, y1, x2, y2] = bbox;
      const w = Math.max(0, x2 - x1);
      const h = Math.max(0, y2 - y1);
      const aspect = w / Math.max(1, h);
      const cy = (y1 + y2) / 2;
      const cyRatio = cy / Math.max(1, frameH);
      const areaRatio = (w * h) / Math.max(1, frameW * frameH);
      if (cyRatio < 0.45) return false;
      if (aspect >= 4.0 && h <= 22) return true;
      if (aspect >= 3.0 && h <= 15) return true;
      if (w >= 0.2 * frameW && h <= 18) return true;
      if (areaRatio < 0.00042 && aspect >= 2.8) return true;
      if (h <= 12 && w >= 56) return true;
      if (conf < 0.52 && aspect >= 3.2 && h <= 20) return true;
      if (cyRatio >= 0.46 && aspect >= 1.05 && aspect <= 3.8 && h >= 14 && h <= 55 && w >= 40 && conf < 0.62) {
        return true;
      }
      if (
        cyRatio >= 0.42 &&
        aspect >= 1.15 &&
        h <= 72 &&
        w >= 0.1 * frameW &&
        areaRatio < 0.028
      ) {
        return true;
      }
      if (w >= 0.05 * frameW && h <= 0.04 * frameH && aspect >= 1.0 && conf < 0.52) return true;
      if (areaRatio < 0.0028 && aspect >= 1.12 && aspect <= 2.85 && h <= 48 && conf < 0.48) return true;
      return false;
    };

    const isLikelyLaneRopeFloatSegment = (
      bbox: number[],
      frameW: number,
      frameH: number,
      conf: number
    ) => {
      if (isPartialBackHeadShouldersBlob(bbox, conf, frameW, frameH)) return false;
      if (isPronePoolFloorHumanBlob(bbox, conf, frameW, frameH)) return false;
      const [x1, y1, x2, y2] = bbox;
      const w = Math.max(0, x2 - x1);
      const h = Math.max(0, y2 - y1);
      const aspect = w / Math.max(1, h);
      const cy = (y1 + y2) / 2;
      const cyRatio = cy / Math.max(1, frameH);
      const areaRatio = (w * h) / Math.max(1, frameW * frameH);
      if (w >= 0.1 * frameW && h >= 30) return false;
      if (aspect >= 1.05 && h >= 34 && areaRatio >= 0.00055) return false;
      if (cyRatio < 0.52 || cyRatio > 0.92) return false;
      if (aspect < 0.45 || aspect > 1.75) return false;
      if (areaRatio < 0.00028 || areaRatio > 0.0035) return false;
      if (Math.max(w, h) < 18 || Math.min(w, h) < 10) return false;
      return true;
    };

    const isPartialBackHeadShouldersBlob = (
      bbox: number[],
      conf: number,
      frameW: number,
      frameH: number
    ) => {
      if (isLikelyPoolLaneLine(bbox, conf, frameW, frameH)) return false;
      const [x1, y1, x2, y2] = bbox;
      const w = Math.max(0, x2 - x1);
      const h = Math.max(0, y2 - y1);
      const aspect = w / Math.max(1, h);
      const cy = (y1 + y2) / 2;
      const cyRatio = cy / Math.max(1, frameH);
      const areaRatio = (w * h) / Math.max(1, frameW * frameH);
      if (cyRatio < 0.36 || Math.max(w, h) < 9 || Math.min(w, h) < 7) return false;
      if (areaRatio < 0.00014 || areaRatio > 0.0026) return false;
      if (aspect >= 0.18 && aspect <= 1.42) return true;
      return false;
    };

    const isLikelyPoolFloorLaneBand = (
      bbox: number[],
      conf: number,
      frameW: number,
      frameH: number
    ) => {
      if (isPartialBackHeadShouldersBlob(bbox, conf, frameW, frameH)) return false;
      const [x1, y1, x2, y2] = bbox;
      const w = Math.max(0, x2 - x1);
      const h = Math.max(0, y2 - y1);
      const aspect = w / Math.max(1, h);
      const cy = (y1 + y2) / 2;
      const cyRatio = cy / Math.max(1, frameH);
      const areaRatio = (w * h) / Math.max(1, frameW * frameH);
      if (cyRatio < 0.38 || cyRatio > 0.86) return false;
      if (aspect < 0.82 || h < 8 || h > 52) return false;
      if (w < 32 && w < 0.06 * frameW) return false;
      if (
        aspect >= 1.08 &&
        h >= 8 &&
        h <= 58 &&
        w >= 0.09 * frameW &&
        areaRatio <= 0.026
      ) {
        return true;
      }
      if (
        aspect >= 0.88 &&
        h >= 8 &&
        h <= 50 &&
        (w >= 0.055 * frameW || w >= 38) &&
        areaRatio <= 0.006 &&
        conf < 0.78
      ) {
        return true;
      }
      if (
        aspect >= 1.08 &&
        aspect <= 4.5 &&
        h >= 10 &&
        h <= 48 &&
        w >= 36 &&
        areaRatio < 0.0048 &&
        conf < 0.74
      ) {
        return true;
      }
      return false;
    };

    const isLikelyChoppyWaterSurfaceFp = (
      bbox: number[],
      conf: number,
      frameW: number,
      frameH: number
    ) => {
      if (isPartialBackHeadShouldersBlob(bbox, conf, frameW, frameH)) return false;
      if (isSubmergedPartialHumanBlob(bbox, conf, frameW, frameH)) return false;
      const [x1, y1, x2, y2] = bbox;
      const w = Math.max(0, x2 - x1);
      const h = Math.max(0, y2 - y1);
      const aspect = w / Math.max(1, h);
      const cy = (y1 + y2) / 2;
      const cyRatio = cy / Math.max(1, frameH);
      const areaRatio = (w * h) / Math.max(1, frameW * frameH);
      if (cyRatio < 0.3 || cyRatio > 0.88) return false;
      if (conf >= 0.62) return false;
      if (aspect >= 1.25 && aspect <= 4.5 && h <= 34 && areaRatio < 0.0026 && conf < 0.56) {
        return true;
      }
      if (
        aspect >= 0.52 &&
        aspect <= 1.38 &&
        areaRatio < 0.00135 &&
        Math.max(w, h) < 46 &&
        conf < 0.52
      ) {
        return true;
      }
      return false;
    };

    const isLowerPoolHorizontalTileFp = (
      bbox: number[],
      conf: number,
      frameW: number,
      frameH: number
    ) => {
      if (isPronePoolFloorHumanBlob(bbox, conf, frameW, frameH)) return false;
      if (isPartialBackHeadShouldersBlob(bbox, conf, frameW, frameH)) return false;
      const [x1, y1, x2, y2] = bbox;
      const w = Math.max(0, x2 - x1);
      const h = Math.max(0, y2 - y1);
      const fw = Math.max(1, frameW);
      const fh = Math.max(1, frameH);
      const aspect = w / Math.max(1, h);
      const cyRatio = ((y1 + y2) / 2) / fh;
      const areaRatio = (w * h) / (fw * fh);
      if (cyRatio < 0.56) return false;
      if (aspect >= 1.28 && h <= 88 && w >= 0.08 * fw && areaRatio < 0.035) return true;
      if (aspect >= 2.05 && h <= 72 && w >= 0.1 * fw) return true;
      if (cyRatio >= 0.68 && aspect >= 1.15 && h <= 58 && w >= 0.12 * fw && areaRatio < 0.022) {
        return true;
      }
      return false;
    };

    const isOverheadThickHorizontalBandFp = (
      bbox: number[],
      conf: number,
      frameW: number,
      frameH: number
    ) => {
      if (isPartialBackHeadShouldersBlob(bbox, conf, frameW, frameH)) return false;
      if (isPronePoolFloorHumanBlob(bbox, conf, frameW, frameH)) return false;
      if (isLowerPoolHorizontalTileFp(bbox, conf, frameW, frameH)) return true;
      const [x1, y1, x2, y2] = bbox;
      const w = Math.max(0, x2 - x1);
      const h = Math.max(0, y2 - y1);
      const fw = Math.max(1, frameW);
      const fh = Math.max(1, frameH);
      const aspect = w / Math.max(1, h);
      const cy = (y1 + y2) / 2;
      const cyRatio = cy / fh;
      const areaRatio = (w * h) / (fw * fh);
      if (cyRatio < 0.3 || cyRatio > 0.93) return false;
      if (conf >= 0.92 && aspect >= 1.08 && w >= 0.07 * fw && h <= 62 && areaRatio < 0.032) {
        return true;
      }
      if (aspect >= 1.05 && w >= 0.065 * fw && areaRatio >= 0.0018 && h <= 52) return true;
      if (aspect >= 1.22 && w >= 48 && h >= 8 && h <= 34 && areaRatio >= 0.0012) return true;
      if (w >= 0.18 * fw && h <= 0.24 * fh && aspect >= 0.95) return true;
      return false;
    };

    const isNonHumanPoolSurfaceFp = (
      bbox: number[],
      conf: number,
      frameW: number,
      frameH: number
    ) => {
      if (isLowerPoolHorizontalTileFp(bbox, conf, frameW, frameH)) return true;
      if (isOverheadThickHorizontalBandFp(bbox, conf, frameW, frameH)) return true;
      if (isPartialBackHeadShouldersBlob(bbox, conf, frameW, frameH)) return false;
      if (isSubmergedPartialHumanBlob(bbox, conf, frameW, frameH)) return false;
      if (isPronePoolFloorHumanBlob(bbox, conf, frameW, frameH)) return false;
      if (isLikelyLaneRopeFloatSegment(bbox, frameW, frameH, conf)) return true;
      if (isLikelyLaneBuoyOrFloat(bbox, conf, frameW, frameH)) return true;
      if (isLikelyPoolFloorLaneBand(bbox, conf, frameW, frameH)) return true;
      if (isLikelyPoolLaneLine(bbox, conf, frameW, frameH)) return true;
      if (isLikelyChoppyWaterSurfaceFp(bbox, conf, frameW, frameH)) return true;
      if (isLikelyWaterGlareReflection(bbox, conf, frameW, frameH)) return true;
      return false;
    };

    const isSituationHumanBlob = (
      bbox: number[],
      frameW: number,
      frameH: number,
      conf: number
    ) => {
      if (isLikelyWaterGlareReflection(bbox, conf, frameW, frameH)) return false;
      return (
        isPartialBackHeadShouldersBlob(bbox, conf, frameW, frameH) ||
        isSupineOrCompactHumanBlob(bbox, conf, frameW, frameH) ||
        isPronePoolFloorHumanBlob(bbox, conf, frameW, frameH) ||
        isSubmergedPartialHumanBlob(bbox, conf, frameW, frameH)
      );
    };

    const isSubmergedPartialHumanBlob = (
      bbox: number[],
      conf: number,
      frameW: number,
      frameH: number
    ) => {
      if (isLikelyPoolLaneLine(bbox, conf, frameW, frameH)) return false;
      const [x1, y1, x2, y2] = bbox;
      const w = Math.max(0, x2 - x1);
      const h = Math.max(0, y2 - y1);
      const aspect = w / Math.max(1, h);
      const cy = (y1 + y2) / 2;
      const cyRatio = cy / Math.max(1, frameH);
      const areaRatio = (w * h) / Math.max(1, frameW * frameH);
      if (cyRatio < 0.44 || areaRatio < 0.00032 || Math.max(w, h) < 18) {
        return false;
      }
      if (areaRatio > 0.0024) return false;
      if (aspect >= 1.02 && w >= 0.042 * frameW) return false;
      if (isOverheadThickHorizontalBandFp(bbox, conf, frameW, frameH)) return false;
      if (aspect >= 0.45 && aspect <= 1.35) return true;
      return false;
    };

    const isLikelyOpenWaterFalsePositive = (
      bbox: number[],
      conf: number,
      frameW: number,
      frameH: number
    ) => {
      if (isLikelyWaterGlareReflection(bbox, conf, frameW, frameH)) return true;
      if (isStrongSituationHumanBlob(bbox, frameW, frameH, conf)) return false;
      if (isSituationHumanBlob(bbox, frameW, frameH, conf)) return false;
      const [x1, y1, x2, y2] = bbox;
      const w = Math.max(0, x2 - x1);
      const h = Math.max(0, y2 - y1);
      const aspect = w / Math.max(1, h);
      const cy = (y1 + y2) / 2;
      const cyRatio = cy / Math.max(1, frameH);
      const areaRatio = (w * h) / Math.max(1, frameW * frameH);
      const longest = Math.max(w, h);
      if (
        cyRatio >= 0.48 &&
        cyRatio <= 0.84 &&
        aspect >= 0.52 &&
        aspect <= 1.55 &&
        areaRatio >= 0.00042 &&
        areaRatio <= 0.0032 &&
        longest >= 24 &&
        Math.max(w, h) < 52 &&
        Math.min(w, h) < 28
      ) {
        return true;
      }
      return false;
    };

    const isLikelyLaneBuoyOrFloat = (
      bbox: number[],
      conf: number,
      frameW: number,
      frameH: number
    ) => {
      if (isLikelyPoolLaneLine(bbox, conf, frameW, frameH)) return true;
      const [x1, y1, x2, y2] = bbox;
      const w = Math.max(0, x2 - x1);
      const h = Math.max(0, y2 - y1);
      const aspect = w / Math.max(1, h);
      const cy = (y1 + y2) / 2;
      const cyRatio = cy / Math.max(1, frameH);
      const areaRatio = (w * h) / Math.max(1, frameW * frameH);
      const longest = Math.max(w, h);
      const shortest = Math.min(w, h);
      if (cyRatio < 0.42) return false;
      if (
        aspect >= 0.52 &&
        aspect <= 1.48 &&
        areaRatio >= 0.00032 &&
        areaRatio <= 0.00165 &&
        longest >= 14 &&
        longest <= 56 &&
        shortest >= 10 &&
        conf < 0.68
      ) {
        return true;
      }
      if (areaRatio < 0.0009 && longest < 44 && conf < 0.58 && cyRatio >= 0.48) return true;
      if (aspect >= 0.85 && aspect <= 1.18 && longest <= 38 && conf < 0.55 && cyRatio >= 0.5) {
        return true;
      }
      return false;
    };

    const isSupineOrCompactHumanBlob = (
      bbox: number[],
      conf: number,
      frameW: number,
      frameH: number
    ) => {
      if (isLikelyPoolLaneLine(bbox, conf, frameW, frameH)) return false;
      if (isLikelyLaneBuoyOrFloat(bbox, conf, frameW, frameH)) return false;
      if (isPartialBackHeadShouldersBlob(bbox, conf, frameW, frameH)) return true;
      const [x1, y1, x2, y2] = bbox;
      const w = Math.max(0, x2 - x1);
      const h = Math.max(0, y2 - y1);
      const aspect = w / Math.max(1, h);
      const areaRatio = (w * h) / Math.max(1, frameW * frameH);
      const longest = Math.max(w, h);
      if (longest < 10 || areaRatio < 0.00028) return false;
      if (aspect >= 1.35 && h <= 22 && w >= 0.08 * frameW) return false;
      if (aspect >= 0.42 && aspect <= 1.55 && areaRatio >= 0.00052 && longest >= 18) {
        if (isLikelyWaterGlareReflection(bbox, conf, frameW, frameH)) return false;
        return true;
      }
      return false;
    };

    const isStrongSituationHumanBlob = (
      bbox: number[],
      frameW: number,
      frameH: number,
      conf: number
    ) =>
      isPartialBackHeadShouldersBlob(bbox, conf, frameW, frameH) ||
      isSubmergedPartialHumanBlob(bbox, conf, frameW, frameH) ||
      isPronePoolFloorHumanBlob(bbox, conf, frameW, frameH);

    const isOverheadSwimmerElongationBlob = (
      bbox: number[],
      conf: number,
      frameW: number,
      frameH: number
    ) => {
      if (isLikelyPoolLaneLine(bbox, conf, frameW, frameH)) return false;
      const [x1, y1, x2, y2] = bbox;
      const w = Math.max(0, x2 - x1);
      const h = Math.max(0, y2 - y1);
      const fw = Math.max(1, frameW);
      const aspect = w / Math.max(1, h);
      const areaRatio = (w * h) / (fw * Math.max(1, frameH));
      if (!(aspect >= 1.22 && aspect <= 2.95 && h >= 16 && h <= 62)) return false;
      if (w >= 0.24 * fw || areaRatio > 0.028 || areaRatio < 0.0004) return false;
      return true;
    };

    /** 백엔드 _is_deck_center_stuck_person_fp — 중앙 데크 타일·줄눈 고착 person */
    const isDeckCenterStuckPersonFp = (
      bbox: number[],
      conf: number,
      frameW: number,
      frameH: number
    ) => {
      if (conf < 0.86) return false;
      const [x1, y1, x2, y2] = bbox;
      const w = Math.max(0, x2 - x1);
      const h = Math.max(0, y2 - y1);
      const fw = Math.max(1, frameW);
      const fh = Math.max(1, frameH);
      const cxRatio = (x1 + x2) * 0.5 / fw;
      const cyRatio = (y1 + y2) * 0.5 / fh;
      if (cyRatio >= 0.5) return false;
      const aspect = w / Math.max(1, h);
      const wRatio = w / fw;
      const hRatio = h / fh;
      if (cxRatio < 0.34 || cxRatio > 0.6) return false;
      if (cyRatio < 0.24 || cyRatio > 0.48) return false;
      if (aspect < 0.55 || aspect > 1.02) return false;
      if (wRatio < 0.04 || wRatio > 0.092) return false;
      if (hRatio < 0.088 || hRatio > 0.17) return false;
      if (h > 86 || w > 74) return false;
      if (h < 52 || w < 38) return false;
      return true;
    };

    const isCenterDeckTilePhantomFp = (
      bbox: number[],
      conf: number,
      frameW: number,
      frameH: number
    ) =>
      isDeckCenterStuckPersonFp(bbox, conf, frameW, frameH);

    const isCocoCompactSurfacePersonFp = (
      bbox: number[],
      conf: number,
      frameW: number,
      frameH: number
    ) => {
      if (conf >= 0.78) return false;
      if (isStrongSituationHumanBlob(bbox, frameW, frameH, conf)) return false;
      if (isOverheadSwimmerElongationBlob(bbox, conf, frameW, frameH)) return false;
      const [x1, y1, x2, y2] = bbox;
      const w = Math.max(0, x2 - x1);
      const h = Math.max(0, y2 - y1);
      const fw = Math.max(1, frameW);
      const fh = Math.max(1, frameH);
      const aspect = w / Math.max(1, h);
      const cy = (y1 + y2) / 2;
      const cyRatio = cy / fh;
      const areaRatio = (w * h) / (fw * fh);
      if (cyRatio < 0.3 || cyRatio > 0.9) return false;
      // 기립·보행·사람 크기면 compact 오탐으로 보지 않음
      if (aspect <= 1.12 && h >= 22 && conf >= 0.18) return false;
      if (conf >= 0.28 && h >= 24 && areaRatio >= 0.0005) return false;
      if (aspect >= 1.18 && h >= 14 && w >= 0.038 * fw) return false;
      if (
        aspect >= 0.36 &&
        aspect <= 1.2 &&
        areaRatio >= 0.00032 &&
        areaRatio <= 0.0014 &&
        h <= 36 &&
        w <= 0.08 * fw &&
        conf < 0.55 &&
        Math.max(w, h) <= 40
      ) {
        return true;
      }
      if (aspect <= 0.98 && areaRatio < 0.0007 && h < 22 && conf < 0.45) return true;
      return false;
    };

    /** 백엔드 overhead_raw_person_shape_whitelist 와 동일 정책 */
    const overheadRawPersonShapeWhitelist = (
      bbox: number[],
      conf: number,
      frameW: number,
      frameH: number
    ) => {
      if (isNonHumanPoolSurfaceFp(bbox, conf, frameW, frameH)) return false;
      if (isOverheadThickHorizontalBandFp(bbox, conf, frameW, frameH)) return false;
      if (isLikelyPoolFloorLaneBand(bbox, conf, frameW, frameH)) return false;
      if (isLikelyWaterGlareReflection(bbox, conf, frameW, frameH)) return false;
      if (isLikelyChoppyWaterSurfaceFp(bbox, conf, frameW, frameH)) return false;
      if (isCocoCompactSurfacePersonFp(bbox, conf, frameW, frameH)) return false;
      if (isStrongSituationHumanBlob(bbox, frameW, frameH, conf)) return true;
      const [x1, y1, x2, y2] = bbox;
      const w = Math.max(0, x2 - x1);
      const h = Math.max(0, y2 - y1);
      const fw = Math.max(1, frameW);
      const aspect = w / Math.max(1, h);
      if (aspect <= 0.92 && h >= 22 && w < 0.12 * fw && conf >= 0.18) return true;
      if (aspect <= 1.12 && h >= 24 && conf >= 0.22) return true;
      if (conf >= 0.28 && h >= 22 && aspect >= 0.2 && aspect <= 3.5) return true;
      if (
        conf >= 0.4 &&
        h >= 22 &&
        aspect >= 1.38 &&
        aspect <= 2.45 &&
        !isLikelyPoolLaneLine(bbox, conf, frameW, frameH)
      ) {
        if (
          isPartialBackHeadShouldersBlob(bbox, conf, frameW, frameH) ||
          isPronePoolFloorHumanBlob(bbox, conf, frameW, frameH) ||
          conf >= 0.52
        ) {
          return true;
        }
      }
      return false;
    };

    const monitorApiDisplayAllowed = (
      bbox: number[],
      conf: number,
      frameW: number,
      frameH: number,
      className: string,
      trackHits: number
    ) => {
      const cn = (className || 'person').toLowerCase();
      if (!['person', 'swimmer', 'drowning', 'lifeguard'].includes(cn)) return false;
      if (isCocoCompactSurfacePersonFp(bbox, conf, frameW, frameH)) return false;
      if (isNonHumanPoolSurfaceFp(bbox, conf, frameW, frameH)) return false;
      if (isOverheadThickHorizontalBandFp(bbox, conf, frameW, frameH)) return false;
      if (overheadRawPersonShapeWhitelist(bbox, conf, frameW, frameH)) return true;
      const minConf = monitoringAiSettings.min_confidence;
      if (cn === 'swimmer' && trackHits >= 1 && conf >= 0.65) {
        return true;
      }
      if (trackHits >= 1 && conf >= minConf) {
        if (isOverheadSwimmerElongationBlob(bbox, conf, frameW, frameH)) return true;
        if (isStrongSituationHumanBlob(bbox, frameW, frameH, conf)) return true;
        return conf >= Math.max(minConf, 0.24);
      }
      return false;
    };

    const isLikelyWaterGlareReflection = (
      bbox: number[],
      conf: number,
      frameW: number,
      frameH: number
    ) => {
      if (isPartialBackHeadShouldersBlob(bbox, conf, frameW, frameH)) return false;
      const [x1, y1, x2, y2] = bbox;
      const w = Math.max(0, x2 - x1);
      const h = Math.max(0, y2 - y1);
      const aspect = w / Math.max(1, h);
      const cy = (y1 + y2) / 2;
      const cyRatio = cy / Math.max(1, frameH);
      const areaRatio = (w * h) / Math.max(1, frameW * frameH);
      const longest = Math.max(w, h);
      const shortest = Math.min(w, h);
      if (isStrongSituationHumanBlob(bbox, frameW, frameH, conf)) {
        if (aspect >= 1.12 && h >= 18 && areaRatio >= 0.00055) return false;
        if (
          isPartialBackHeadShouldersBlob(bbox, conf, frameW, frameH) &&
          longest >= 24 &&
          areaRatio >= 0.00062
        ) {
          return false;
        }
      }
      if (cyRatio < 0.32 || cyRatio > 0.84) return false;
      if (longest < 10 || shortest < 8 || longest > 58) return false;
      if (conf >= 0.74) return false;
      if (isLikelyWaterRippleStatic(bbox, conf, frameW, frameH)) return true;
      if (
        aspect >= 0.5 &&
        aspect <= 1.45 &&
        areaRatio >= 0.0003 &&
        areaRatio <= 0.0032 &&
        cyRatio >= 0.38 &&
        cyRatio <= 0.78 &&
        conf <= 0.68
      ) {
        return true;
      }
      if (
        aspect >= 0.62 &&
        aspect <= 1.22 &&
        areaRatio <= 0.00185 &&
        conf <= 0.62 &&
        cyRatio >= 0.4 &&
        cyRatio <= 0.76
      ) {
        return true;
      }
      return false;
    };

    const isPronePoolFloorHumanBlob = (
      bbox: number[],
      conf: number,
      frameW: number,
      frameH: number
    ) => {
      const [x1, y1, x2, y2] = bbox;
      const w = Math.max(0, x2 - x1);
      const h = Math.max(0, y2 - y1);
      const aspect = w / Math.max(1, h);
      const cy = (y1 + y2) / 2;
      const cyRatio = cy / Math.max(1, frameH);
      const areaRatio = (w * h) / Math.max(1, frameW * frameH);
      if (isLikelyPoolLaneLine(bbox, conf, frameW, frameH)) return false;
      if (cyRatio < 0.48 || areaRatio < 0.00038 || Math.max(w, h) < 12) {
        return false;
      }
      if (aspect >= 3.0 && h <= 18) return false;
      if (aspect >= 1.15 && aspect <= 2.85 && h >= 12 && areaRatio >= 0.00055) return true;
      if (aspect >= 0.88 && aspect <= 1.48 && h >= 28 && w >= 0.08 * frameW) return true;
      if (aspect >= 0.55 && aspect <= 1.65 && areaRatio >= 0.00048) return true;
      return false;
    };

    const isLikelyPoolArtifact = (bbox: number[], conf: number, frameW: number, frameH: number) => {
      const [x1, y1, x2, y2] = bbox;
      const w = Math.max(0, x2 - x1);
      const h = Math.max(0, y2 - y1);
      if (w < 6 || h < 6) return true;
      if (isNonHumanPoolSurfaceFp(bbox, conf, frameW, frameH)) return true;
      if (isLikelyPoolLaneLine(bbox, conf, frameW, frameH)) return true;
      if (isLikelyLaneBuoyOrFloat(bbox, conf, frameW, frameH)) return true;
      if (isLikelyOpenWaterFalsePositive(bbox, conf, frameW, frameH)) return true;
      const aspect = w / Math.max(1, h);
      const cy = (y1 + y2) / 2;
      const cyRatio = cy / Math.max(1, frameH);
      const areaRatio = (w * h) / Math.max(1, frameW * frameH);
      if (aspect >= 2.6 && h <= 36) {
        if (!isPronePoolFloorHumanBlob(bbox, conf, frameW, frameH)) {
          if (!(areaRatio >= 0.0022 && conf >= 0.3 && w >= 48)) return true;
        }
      }
      if (aspect >= 2.0 && h <= 24 && cyRatio >= 0.68) {
        if (!(areaRatio >= 0.0024 && conf >= 0.3 && w >= 52)) return true;
      }
      if (aspect <= 0.16 && w <= 34) return true;
      if (aspect <= 0.24 && w <= 22 && h >= 100) return true;
      if (cyRatio >= 0.88 && h <= 42 && aspect >= 1.5) {
        if (!isPronePoolFloorHumanBlob(bbox, conf, frameW, frameH)) return true;
      }
      if (cyRatio >= 0.76 && aspect >= 3.2 && conf < 0.58) return true;
      if (areaRatio >= 0.016 && aspect >= 3.0 && h <= 52 && conf < 0.65) return true;
      if (
        aspect >= 0.78 &&
        aspect <= 1.28 &&
        h <= 58 &&
        w <= 58 &&
        cyRatio >= 0.5 &&
        conf < 0.6 &&
        areaRatio < 0.009
      ) {
        if (isSupineOrCompactHumanBlob(bbox, conf, frameW, frameH)) {
          // 똑바로 누움·등·상체만
        } else if (
          conf >= 0.24 &&
          h >= 16 &&
          w >= 16 &&
          aspect >= 0.45 &&
          aspect <= 1.55 &&
          areaRatio >= 0.00042
        ) {
          // 탑뷰 컴팩트 blob
        } else {
          return true;
        }
      }
      if (Math.max(w, h) < 42 && conf < 0.44) {
        if (
          !(
            conf >= 0.32 &&
            h >= 20 &&
            w >= 20 &&
            aspect >= 0.48 &&
            aspect <= 1.42 &&
            areaRatio >= 0.0009
          )
        ) {
          return true;
        }
      }
      if (isLikelyPoolLaneLine(bbox, conf, frameW, frameH)) return true;
      if (isLikelyLaneBuoyOrFloat(bbox, conf, frameW, frameH)) return true;
      if (isLikelyClothingFragment(bbox, conf, frameW, frameH)) return true;
      return false;
    };

    const bboxCenter = (bbox: number[]) => [
      (bbox[0] + bbox[2]) / 2,
      (bbox[1] + bbox[3]) / 2,
    ];

    const pointInBbox = (px: number, py: number, bbox: number[]) => {
      const [x1, y1, x2, y2] = bbox;
      const loX = Math.min(x1, x2);
      const hiX = Math.max(x1, x2);
      const loY = Math.min(y1, y2);
      const hiY = Math.max(y1, y2);
      return px >= loX && px <= hiX && py >= loY && py <= hiY;
    };

    const mergeCenterDuplicateDetections = (detections: any[], distFactor = 0.52) => {
      if (!detections.length) return detections;
      const sorted = [...detections].sort((a, b) => Number(b.score || 0) - Number(a.score || 0));
      const kept: any[] = [];
      sorted.forEach((candidate) => {
        const bbox = candidate.bbox as number[];
        if (!Array.isArray(bbox) || bbox.length !== 4) return;
        const [cx, cy] = bboxCenter(bbox);
        const bw = Math.max(1, bbox[2] - bbox[0]);
        const bh = Math.max(1, bbox[3] - bbox[1]);
        const diag = Math.hypot(bw, bh);
        const dup = kept.some((k) => {
          const kb = k.bbox as number[];
          const [kcx, kcy] = bboxCenter(kb);
          const kbw = Math.max(1, kb[2] - kb[0]);
          const kbh = Math.max(1, kb[3] - kb[1]);
          const kdiag = Math.hypot(kbw, kbh);
          const dist = Math.hypot(cx - kcx, cy - kcy);
          if (dist <= distFactor * Math.min(diag, kdiag)) return true;
          return pointInBbox(cx, cy, kb) || pointInBbox(kcx, kcy, bbox);
        });
        if (!dup) kept.push(candidate);
      });
      return kept;
    };

    const refineMonitorClassName = (
      apiClass: string,
      bbox: number[],
      conf: number,
      frameW: number,
      frameH: number
    ): string | null => {
      if (isCenterDeckTilePhantomFp(bbox, conf, frameW, frameH)) return null;
      if (isNonHumanPoolSurfaceFp(bbox, conf, frameW, frameH)) return null;
      if (isLikelyLaneRopeFloatSegment(bbox, frameW, frameH, conf)) return null;
      if (isLikelyLaneBuoyOrFloat(bbox, conf, frameW, frameH)) return null;
      if (isLikelyOpenWaterFalsePositive(bbox, conf, frameW, frameH)) return null;
      if (isPartialBackHeadShouldersBlob(bbox, conf, frameW, frameH)) return 'person';
      if (isSubmergedPartialHumanBlob(bbox, conf, frameW, frameH)) return 'person';
      if (isPronePoolFloorHumanBlob(bbox, conf, frameW, frameH)) return 'person';
      if (isLikelyWaterRipple(bbox, conf, frameW, frameH)) return null;
      if (isLikelyClothingFragment(bbox, conf, frameW, frameH)) return null;
      if (isLikelyPoolLaneLine(bbox, conf, frameW, frameH)) return null;
      if (isLikelyPoolArtifact(bbox, conf, frameW, frameH)) return null;
      if (isOverheadThickHorizontalBandFp(bbox, conf, frameW, frameH)) return null;
      const cn = (apiClass || '').toLowerCase();
      if (cn === 'person' || cn === 'swimmer' || cn === 'drowning' || cn === 'lifeguard') {
        return cn;
      }
      return null;
    };

    const collapseOverlappingBoxes = (detections: any[]) => {
      if (!detections.length) return detections;
      const sorted = [...detections].sort(
        (a, b) => Number(b.score ?? b.confidence ?? 0) - Number(a.score ?? a.confidence ?? 0)
      );
      const kept: any[] = [];
      sorted.forEach((candidate) => {
        const bbox = candidate.bbox as number[];
        if (!Array.isArray(bbox) || bbox.length !== 4) return;
        const dup = kept.some((k) => {
          const kb = k.bbox as number[];
          if (!Array.isArray(kb) || kb.length !== 4) return false;
          if (getIoU(bbox, kb) >= 0.2) return true;
          const [cx, cy] = bboxCenter(bbox);
          const [kcx, kcy] = bboxCenter(kb);
          const bw = Math.max(1, bbox[2] - bbox[0]);
          const bh = Math.max(1, bbox[3] - bbox[1]);
          const kbw = Math.max(1, kb[2] - kb[0]);
          const kbh = Math.max(1, kb[3] - kb[1]);
          const dist = Math.hypot(cx - kcx, cy - kcy);
          if (dist <= 0.52 * Math.min(Math.hypot(bw, bh), Math.hypot(kbw, kbh))) return true;
          return pointInBbox(cx, cy, kb) || pointInBbox(kcx, kcy, bbox);
        });
        if (!dup) kept.push(candidate);
      });
      return kept;
    };

    const getIoU = (a: number[], b: number[]) => {
      const x1 = Math.max(a[0], b[0]);
      const y1 = Math.max(a[1], b[1]);
      const x2 = Math.min(a[2], b[2]);
      const y2 = Math.min(a[3], b[3]);
      const interW = Math.max(0, x2 - x1);
      const interH = Math.max(0, y2 - y1);
      const interArea = interW * interH;
      const areaA = Math.max(0, a[2] - a[0]) * Math.max(0, a[3] - a[1]);
      const areaB = Math.max(0, b[2] - b[0]) * Math.max(0, b[3] - b[1]);
      const union = areaA + areaB - interArea;
      return union > 0 ? interArea / union : 0;
    };

    const deduplicateDetections = (detections: any[]) => {
      if (!detections.length) return detections;
      const sorted = [...detections].sort((a, b) => Number(b.score || 0) - Number(a.score || 0));
      const kept: any[] = [];

      sorted.forEach((candidate) => {
        const isDuplicate = kept.some((k) => getIoU(k.bbox, candidate.bbox) >= DEDUP_IOU_THRESHOLD);
        if (!isDuplicate) kept.push(candidate);
      });

      return kept;
    };
    
    // 위치 기반 그리드 구성을 위한 함수 (복구)
    const organizeByGridPosition = () => {
      let maxRow = 0;
      let maxCol = 0;
      safetyCctvs.forEach(cctv => {
        if (!cctv.location) return;
        const [row, col] = cctv.location.split('-').map(Number);
        if (!isNaN(row) && !isNaN(col)) {
          maxRow = Math.max(maxRow, row);
          maxCol = Math.max(maxCol, col);
        }
      });
      const grid: (Cctv | null)[][] = [];
      for (let i = 0; i <= maxRow; i++) {
        grid[i] = [];
        for (let j = 0; j <= maxCol; j++) {
          grid[i][j] = null;
        }
      }
      safetyCctvs.forEach(cctv => {
        if (!cctv.location) return;
        const [row, col] = cctv.location.split('-').map(Number);
        if (row > 0 && col > 0 && row <= maxRow && col <= maxCol) {
          grid[row][col] = cctv;
        }
      });
      return { grid, maxRow, maxCol };
    };

    const attachOverlayResizeObserver = (
      tileKey: string | number,
      el: HTMLDivElement | null
    ) => {
      const key = String(tileKey);
      if (overlayContainerElRef.current[key] === el) {
        overlayContainerRefs.current[key] = el;
        return;
      }
      overlayContainerElRef.current[key] = el;
      overlayContainerRefs.current[key] = el;

      const prevRo = overlayResizeObservers.current[key];
      if (prevRo) {
        prevRo.disconnect();
        delete overlayResizeObservers.current[key];
      }
      if (!el || typeof ResizeObserver === 'undefined') return;

      const ro = new ResizeObserver(() => {
        setOverlayLayoutTick((n) => n + 1);
      });
      ro.observe(el);
      overlayResizeObservers.current[key] = ro;
    };

    useEffect(() => {
      return () => {
        Object.values(overlayResizeObservers.current).forEach((ro) => ro.disconnect());
        overlayResizeObservers.current = {};
      };
    }, []);

    const handleViewAllCctvs = () => {
      if (!playbackConfigReady) {
        alert('재생 설정을 불러오는 중. 잠시 후 다시 시도');
        return;
      }
      if (safetyCctvs.length === 0) {
        alert('표시할 관제 CCTV가 없음');
        return;
      }
      if (typeof onEnterPoolFullscreen === 'function' && pool.id != null) {
        onEnterPoolFullscreen(Number(pool.id));
        return;
      }
      // fallback: 동일 창 경로 이동
      syncMonitoringPopupShowBboxForAiDetect(isMonitored);
      openPoolMonitoringInNewWindow(
        {
          id: pool.id,
          code: pool.code,
          name: pool.name,
          status: pool.status,
          site_gateway_enabled: siteGatewayEnabled,
        },
        safetyCctvs,
        {
          skipPurposeFilter: true,
          useRemotePlayback,
          siteGatewayEnabled: siteGatewayEnabled,
          rotationIntervalSec,
        },
      );
    };

    const handleOpenRotationCctvs = () => {
      if (!playbackConfigReady) {
        alert('재생 설정을 불러오는 중. 잠시 후 다시 시도');
        return;
      }
      if (safetyCctvs.length === 0) {
        alert('표시할 관제 CCTV가 없음');
        return;
      }
      syncMonitoringPopupShowBboxForAiDetect(isMonitored);
      if (typeof onEnterCctvRotation === 'function' && pool.id != null) {
        onEnterCctvRotation(Number(pool.id), {
          intervalSec: rotationIntervalSec,
          startPaused: false,
          zoneTilesOnly,
        });
        return;
      }
      openPoolMonitoringRotationInNewWindow(
        {
          id: pool.id,
          code: pool.code,
          name: pool.name,
          status: pool.status,
          site_gateway_enabled: siteGatewayEnabled,
        },
        safetyCctvs,
        {
          skipPurposeFilter: true,
          useRemotePlayback,
          siteGatewayEnabled: siteGatewayEnabled,
          rotationIntervalSec,
        },
      );
    };

    const handleOpenRotationAtCctv = (
      cctvId: number,
      zoneIndex?: number
    ) => {
      if (!playbackConfigReady) {
        alert('재생 설정을 불러오는 중. 잠시 후 다시 시도');
        return;
      }
      if (safetyCctvs.length === 0) {
        alert('표시할 관제 CCTV가 없음');
        return;
      }
      syncMonitoringPopupShowBboxForAiDetect(isMonitored);
      if (typeof onEnterCctvRotation === 'function' && pool.id != null) {
        onEnterCctvRotation(Number(pool.id), {
          cctvId,
          zoneIndex,
          intervalSec: rotationIntervalSec,
          startPaused: true,
          zoneTilesOnly,
        });
        return;
      }
      openPoolMonitoringRotationInNewWindow(
        {
          id: pool.id,
          code: pool.code,
          name: pool.name,
          status: pool.status,
          site_gateway_enabled: siteGatewayEnabled,
        },
        safetyCctvs,
        {
          skipPurposeFilter: true,
          useRemotePlayback,
          siteGatewayEnabled: siteGatewayEnabled,
          rotationIntervalSec,
          initialCctvId: cctvId,
          startRotationPaused: true,
        },
      );
    };

    // 위험 알림은 PoolGridItem 내부에서만 관리
    const [localRiskAlert, setLocalRiskAlert] = useState<{ type: string; message: string } | null>(null);
    const showPoolSnack = useCallback((alert: { type: string; message: string } | null) => {
      if (!alert) {
        setLocalRiskAlert(null);
        return;
      }
      // 인원 수 안내 / 위험 이벤트 팝업이 떠 있는 동안은 스낵바 숨김
      if (alert.type === 'ai' || isPersonCountAiSnackMessage(alert.message)) return;
      if (centralRiskUiRef.dialogOpen) return;
      setLocalRiskAlert(alert);
    }, []);
    const handleManualEventForCctv = (requestedCctvId: number) => {
      try {
        const orderedCctvs = [...safetyCctvs].sort(cctvLocationNaturalCompare);
        const targetCctv =
          orderedCctvs.find((c) => Number(c.id) === Number(requestedCctvId)) ||
          orderedCctvs[0];

        if (!targetCctv) {
          showPoolSnack({
            type: 'warning',
            message: `테스트 실패: ${pool.name}에 CCTV가 없어 이벤트를 생성 불가`,
          });
          return;
        }

        const sc = resolveDetectionForFacilityProfile(
          pool.scenario_profile,
          null,
          targetCctv.detection_scenario
        ).scenario;
        const typeOpts = resolveManualRiskEvents({
          scenarioProfile: pool.scenario_profile,
          selectedCctvScenario: sc,
        });
        const riskType = typeOpts[0]?.type || 'manual';
        const locationLabel = String(targetCctv.location || targetCctv.name || '');
        const cid = Number(targetCctv.id);
        const imageDataUrl =
          playerHandlesRef.current[cid]?.captureFrame?.() ||
          captureVideoFrame(videoRefs.current[cid], {
            rotate180: Boolean(
              (targetCctv as { rotate_180?: boolean }).rotate_180
            ),
          });
        const trackMap = displayTracksRef.current[cid];
        const bboxes = collectEventBboxesFromDisplayTracks(
          trackMap ? Array.from(trackMap.values()) : []
        ).map((b) => ({
          ...b,
          motionRisk: true,
          // 코드명·색은 이벤트 창에서 입력
          riskSubtypeLabel: b.riskSubtypeLabel || '',
          borderColor: b.borderColor,
        }));
        onRiskEventRef.current({
          poolId: Number(poolId),
          poolName: pool.name,
          cctvId: Number(targetCctv.id),
          location: locationLabel,
          riskType,
          riskLabel: '',
          message: '',
          imageDataUrl,
          bboxes,
          safetyPhoneRaw: String(pool.safety_phone || '').trim(),
          poolSirens: Array.isArray(pool.sirens)
            ? pool.sirens.filter((s) => String(s).trim())
            : [],
          source: 'test',
          triggerTs: Date.now() / 1000,
          scenarioProfile: pool.scenario_profile,
          detectionScenario: sc,
          rotate_180: Boolean(
            (targetCctv as { rotate_180?: boolean }).rotate_180
          ),
        });
      } catch (e: any) {
        const detail =
          e?.response?.data?.detail ||
          e?.response?.data?.message ||
          e?.message ||
          '원인 미상';
        showPoolSnack({
          type: 'test',
          message: `테스트 이벤트 생성 실패: ${pool.name} (${detail})`,
        });
      }
    };
    
    // 감지 시작 시 비디오 준비 체크 함수
    const startDetectionWhenReady = (cctvId: number) => {
      const video = videoRefs.current[cctvId];
      if (video && video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0) {
        // 감지 대기열에 추가
        if (!processingDetections.current.has(cctvId)) {
          detectionQueue.current.push(cctvId);
          processDetectionQueue();
        }
      } else if (video) {
        // 비디오가 아직 준비되지 않은 경우, 이벤트 리스너 등록
        const onReady = () => {
          if (video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0) {
            video.removeEventListener('loadedmetadata', onReady);
            if (!processingDetections.current.has(cctvId)) {
              detectionQueue.current.push(cctvId);
              processDetectionQueue();
            }
          }
        };
        video.addEventListener('loadedmetadata', onReady);
      }
    };

    // YOLO는 GPU 1대 기준 — CCTV 여러 대일 때 동시 요청하면 인식률·응답이 급락함 → 순차 처리
    const processDetectionQueue = () => {
      if (!isDetectingRef.current || detectionQueue.current.length === 0) return;
      if (detectionQueueBusy.current) return;

      const cctvId = detectionQueue.current.shift()!;
      if (processingDetections.current.has(cctvId)) {
        processDetectionQueue();
        return;
      }

      detectionQueueBusy.current = true;
      processingDetections.current.add(cctvId);
      handleRealAiRiskDetect(cctvId)
        .finally(() => {
          processingDetections.current.delete(cctvId);
          detectionQueueBusy.current = false;
          processDetectionQueue();
        });
    };

    // 성능 모니터링 함수
    const updatePerformanceMetrics = (processingTime: number) => {
      const metrics = performanceMetrics.current;
      metrics.totalDetections++;
      metrics.lastProcessingTimes.push(processingTime);
      
      // 최근 10개의 처리 시간만 유지
      if (metrics.lastProcessingTimes.length > 10) {
        metrics.lastProcessingTimes.shift();
      }
      
      // 평균 처리 시간 계산
      metrics.averageProcessingTime = metrics.lastProcessingTimes.reduce((a, b) => a + b, 0) / 
                                    metrics.lastProcessingTimes.length;
      
      // 성능 로깅
      console.log(`성능 메트릭스:
        - 총 감지 횟수: ${metrics.totalDetections}
        - 평균 처리 시간: ${metrics.averageProcessingTime.toFixed(2)}ms
        - 현재 동시 처리 중: ${processingDetections.current.size}
        - 대기 중인 감지: ${detectionQueue.current.length}`);
    };

    // 실제 AI 위험 감지 실행 함수
    const handleRealAiRiskDetect = async (cctvId: number) => {
      const startTime = performance.now();
      const now = Date.now();
      
      try {
        if (!isDetectingRef.current) return;
        // 캐시된 결과 확인
        const cachedResult = detectionCache.current.get(cctvId);
        if (cachedResult && (now - cachedResult.timestamp) < CACHE_DURATION) {
          updateDetectionResults(cctvId, cachedResult.data);
          return;
        }

        setLastDetectionTime(now);
        const video = videoRefs.current[cctvId];
        if (!video) {
          console.error('비디오 요소를 찾을 수 없습니다:', cctvId);
          return;
        }

        // 비디오 준비 상태 체크
        if (video.readyState < 2 || video.videoWidth === 0 || video.videoHeight === 0) {
          console.warn('비디오가 아직 준비되지 않음, 100ms 후 재시도', cctvId);
          if (isDetectingRef.current) {
            setTimeout(() => {
              if (isDetectingRef.current) {
                handleRealAiRiskDetect(cctvId);
              }
            }, 100);
          }
          return;
        }

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          console.error('Canvas 컨텍스트를 생성할 수 없습니다.');
          return;
        }

        const vw = video.videoWidth;
        const vh = video.videoHeight;
        if (vw < 16 || vh < 16) {
          return;
        }
        const maxWRaw = resolveInferCaptureMaxWidth(monitoringAiSettings, {
          cctvCountInPool: safetyCctvs.length,
          globalActiveCamCount: totalInferCctvCount,
          gpuCount: inferGpuCount,
        });
        // HLS가 이미 ~960이면 추론만 480으로 반토막 내지 않음(네트워크↑ 없음)
        const maxW =
          vw >= 900 ? Math.max(maxWRaw, Math.min(vw, 960)) : maxWRaw;
        const scale = vw > maxW ? maxW / vw : 1;
        canvas.width = Math.max(16, Math.round(vw * scale));
        canvas.height = Math.max(16, Math.round(vh * scale));
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        let imageData: string;
        try {
          imageData = canvas.toDataURL('image/jpeg', monitoringAiSettings.jpeg_quality);
        } catch (capErr) {
          console.warn('프레임 캡처 실패(스트림 CORS 등):', cctvId, capErr);
          return;
        }
        if (!imageData || imageData.length < 500 || !imageData.includes('base64,')) {
          console.warn('캡처 이미지가 비어 있음:', cctvId);
          return;
        }

        const response = await axios.post(`${API_URL}/api/yolo/detect`, {
          image_data: imageData,
          include_processed_image: false,
          pool_id: Number(pool.id) || undefined,
          cctv_id: cctvId,
          evaluate_motion_risk: monitoringAiSettings.evaluate_motion_risk,
        });

        if (response.data && isDetectingRef.current) {
          let payload: any = response.data;
          if (typeof payload === 'string') {
            try {
              payload = JSON.parse(payload);
            } catch {
              return;
            }
          }
          if (!payload || !Array.isArray(payload.detections)) {
            return;
          }
          const scaleBackX = vw / canvas.width;
          const scaleBackY = vh / canvas.height;
          if (scaleBackX !== 1 || scaleBackY !== 1) {
            payload = {
              ...payload,
              detections: payload.detections.map((det: any) => {
                if (!Array.isArray(det?.bbox) || det.bbox.length !== 4) return det;
                const [x1, y1, x2, y2] = det.bbox;
                return {
                  ...det,
                  bbox: [x1 * scaleBackX, y1 * scaleBackY, x2 * scaleBackX, y2 * scaleBackY],
                  frame_width: vw,
                  frame_height: vh,
                };
              }),
            };
          }
          // 결과 캐싱
          detectionCache.current.set(cctvId, {
            data: payload,
            timestamp: now
          });
          
          updateDetectionResults(cctvId, payload);
          
          // 성능 메트릭스 업데이트
          const processingTime = performance.now() - startTime;
          updatePerformanceMetrics(processingTime);
        }
      } catch (error: any) {
        const status = error?.response?.status;
        const detail = error?.response?.data?.detail ?? error?.response?.data;
        if (status === 400) {
          console.warn('AI detect 400:', cctvId, detail);
        } else if (status === 503) {
          console.error('YOLO 미로드(503). 백엔드 기동 로그를 확인하세요.', detail);
        } else {
          console.error('AI 감지 오류:', error);
        }
      }
    };

    // 감지 결과 업데이트 함수
    const updateDetectionResults = (
      cctvId: number,
      data: any,
      options?: { trustServer?: boolean }
    ) => {
      if (!isDetectingRef.current) return;

      // API(백엔드) 검출만 표시 — 별도 streak/폴백으로 오탐을 다시 살리지 않음
      const mountType = String(data.mount_type || 'overhead').toLowerCase();
      const isOverheadMount = mountType === 'overhead';

      const directDetectionsRaw = (data.detections || []).filter(
        (det: any) => Array.isArray(det?.bbox) && det.bbox.length === 4
      );

      const frameW = Math.max(
        1,
        Number(data.frame_width) ||
          Number(directDetectionsRaw[0]?.frame_width) ||
          Number(videoRefs.current[cctvId]?.videoWidth) ||
          1920
      );
      const frameH = Math.max(
        1,
        Number(data.frame_height) ||
          Number(directDetectionsRaw[0]?.frame_height) ||
          Number(videoRefs.current[cctvId]?.videoHeight) ||
          1080
      );
      const cctvRow = safetyCctvs.find((c) => Number(c.id) === Number(cctvId));
      const monitorFilterStrength = Math.max(
        0,
        Math.min(
          2,
          cctvRow?.monitor_filter_strength != null &&
            cctvRow?.monitor_filter_strength !== undefined
            ? Number(cctvRow.monitor_filter_strength)
            : 1
        )
      );

      if (options?.trustServer) {
        const rawPerson =
          isRawPersonPipelineMode(data.pipeline_mode) ||
          monitoringAiSettings.yolo_pipeline_preset === 'raw_person_debug';
        const tracksSource = filterServerDetectionsForDisplay(
          directDetectionsRaw,
          monitoringAiSettings,
          visibleClassMap,
          frameW,
          frameH,
          { rawPerson }
        ).map((det) => ({
          ...det,
          lastUpdate: Date.now(),
        }));
        const riskFromAlerts = rawPerson
          ? new Map<number, MotionRiskTrackMeta>()
          : collectMotionRiskMeta(data.risk_alerts);
        const displayRisk = new Map<number, MotionRiskTrackMeta>();
        riskFromAlerts.forEach((meta, tid) => displayRisk.set(tid, meta));
        syncDisplayTracksFromApi(cctvId, tracksSource, displayRisk);
        bboxMapRef.current[cctvId] = tracksSource;
        tracksRef.current[cctvId] = tracksSource;
        detectionDebugRef.current[cctvId] = {
          raw: directDetectionsRaw.length,
          shown: tracksSource.length,
          pipelineMode: rawPerson ? 'raw_person' : String(data.pipeline_mode || ''),
        };
        forceUpdate((n) => n + 1);
        return;
      }

      if (DRAW_RAW_SERVER_SNAPSHOTS || TRUST_SERVER_SNAPSHOT_OVERLAY) {
        const maxMiss = maxServerMissForDisplay(monitoringAiSettings);
        const trustedRaw = directDetectionsRaw.filter((det: any) => {
          if (Number(det.miss_count ?? 0) > maxMiss) return false;
          const bbox = det.bbox as number[];
          const score = Number(det.score ?? det.confidence ?? 0);
          const hits = Number(det.hits ?? 1);
          const minHits = monitoringAiSettings.min_track_hits;
          if (minHits > 1 && hits >= 1 && hits < minHits) return false;
          return passesDetectionDisplayFilters(
            bbox,
            score,
            frameW,
            frameH,
            monitoringAiSettings,
            Math.max(hits, 1)
          );
        });
        const tracksSource = trustedRaw.map((det: any, idx: number) => {
          const score = Number(det.score ?? det.confidence ?? 0);
          // 스냅샷 프레임 크기를 우선 — det.frame_width(추론해상도)가 남으면 이중 스케일 유발
          const sw = frameW;
          const sh = frameH;
          const tid = Number(det.track_id ?? det.id);
          const trackId = Number.isFinite(tid) && tid > 0 ? tid : idx + 1;
          let bbox = (det.bbox as number[]).map(Number);
          const detFw = Math.max(1, Number(det.frame_width) || sw);
          const detFh = Math.max(1, Number(det.frame_height) || sh);
          if (detFw !== sw || detFh !== sh) {
            const maxX = Math.max(Math.abs(bbox[0]), Math.abs(bbox[2]));
            const maxY = Math.max(Math.abs(bbox[1]), Math.abs(bbox[3]));
            if (maxX <= detFw * 1.08 + 2 && maxY <= detFh * 1.08 + 2) {
              bbox = [
                (bbox[0] * sw) / detFw,
                (bbox[1] * sh) / detFh,
                (bbox[2] * sw) / detFw,
                (bbox[3] * sh) / detFh,
              ];
            }
          }
          return {
            ...det,
            bbox,
            id: trackId,
            track_id: trackId,
            class_name: String(det.class_name || 'person'),
            score,
            confidence: score,
            frame_width: sw,
            frame_height: sh,
            miss_count: 0,
            hits: Number(det.hits ?? 0),
            lastUpdate: Date.now(),
          };
        });
        const riskFromAlerts = collectMotionRiskMeta(data.risk_alerts);
        const displayRisk = new Map<number, MotionRiskTrackMeta>();
        riskFromAlerts.forEach((meta, tid) => displayRisk.set(tid, meta));
        syncDisplayTracksFromApi(cctvId, tracksSource, displayRisk);
        bboxMapRef.current[cctvId] = tracksSource;
        tracksRef.current[cctvId] = tracksSource;
        detectionDebugRef.current[cctvId] = {
          raw: directDetectionsRaw.length,
          shown: tracksSource.length,
        };
        forceUpdate((n) => n + 1);
        return;
      }

      const apiPool = directDetectionsRaw
        .filter(
          (det: any) =>
            Number(det.miss_count ?? 0) <= maxServerMissForDisplay(monitoringAiSettings)
        )
        .map((det: any, idx: number) => {
          const bbox = det.bbox as number[];
          const score = Number(det.score ?? det.confidence ?? 0);
          const sw = Number(det.frame_width) || frameW;
          const sh = Number(det.frame_height) || frameH;
          const refined = USE_HYBRID_MONITOR_PIPELINE
            ? hybridRefineClassName(
                String(det.class_name || 'person').toLowerCase(),
                bbox,
                score,
                sw,
                sh,
                monitorFilterStrength
              )
            : refineMonitorClassName(
                String(det.class_name || 'person').toLowerCase(),
                bbox,
                score,
                sw,
                sh
              );
          if (!refined) return null;
          return {
            ...det,
            id: det.track_id ?? det.id ?? idx,
            track_id: det.track_id ?? det.id ?? idx,
            miss_count: det.miss_count ?? 0,
            class_name: refined,
            score,
            frame_width: sw,
            frame_height: sh,
            lastUpdate: Date.now(),
          };
        })
        .filter(Boolean)
        .filter((det: any) => {
          if (visibleClassMap[(det.class_name || '').toLowerCase()] === false) {
            return false;
          }
          const conf = Number(det.score ?? 0);
          const sw = Number(det.frame_width) || frameW;
          const sh = Number(det.frame_height) || frameH;
          const cn = String(det.class_name || 'person').toLowerCase();
          if (isCenterDeckTilePhantomFp(det.bbox as number[], conf, sw, sh)) {
            return false;
          }
          if (!isOverheadMount) {
            return conf >= monitoringAiSettings.min_confidence;
          }
          const tid = Number(det.track_id ?? det.id);
          if (ver5LaneStripePersonLockFp(det.bbox as number[], conf, sw, sh)) {
            return false;
          }
          if (lowerPoolEdgeRipplePersonFp(det.bbox as number[], conf, sw, sh)) {
            return false;
          }
          if (isDeckCenterStuckPersonFp(det.bbox as number[], conf, sw, sh)) {
            return false;
          }
          if (
            Number.isFinite(tid) &&
            tid > 0 &&
            cn === 'person' &&
            conf >= 0.28 &&
            Number(det.hits ?? 0) >= 1
          ) {
            const cy =
              ((det.bbox as number[])[1] + (det.bbox as number[])[3]) * 0.5 / sh;
            if (cy >= 0.48) {
              return monitorApiDisplayAllowed(
                det.bbox,
                conf,
                sw,
                sh,
                cn,
                Number(det.hits ?? 0)
              );
            }
          }
          if (
            cn === 'swimmer' &&
            conf >= 0.58 &&
            Number(det.hits ?? 0) >= 2
          ) {
            return true;
          }
          return monitorApiDisplayAllowed(
            det.bbox,
            conf,
            sw,
            sh,
            cn,
            Number(det.hits ?? 0)
          );
        }) as any[];

      let tracksSource = collapseOverlappingBoxes(
        mergeCenterDuplicateDetections(deduplicateDetections(apiPool), 0.52)
      );
      if (USE_HYBRID_MONITOR_PIPELINE) {
        tracksSource = filterHybridDetectionList(
          tracksSource,
          frameW,
          frameH,
          monitorFilterStrength
        );
      }

      const visibleDetections = tracksSource;

      const activeTids = new Set(
        tracksSource
          .map((d) => Number(d.track_id ?? d.id))
          .filter((n) => Number.isFinite(n))
      );

      const riskFromAlerts = collectMotionRiskMeta(data.risk_alerts);
      const activeRiskTids = new Set(riskFromAlerts.keys());
      const nowMs = Date.now();
      const highlightMap =
        motionRiskHighlightRef.current[cctvId] ||
        new Map<number, { meta: MotionRiskTrackMeta; until: number }>();
      if (activeRiskTids.size > 0) {
        for (const det of data.detections || []) {
          const tid = Number(det.track_id ?? det.id);
          if (!Number.isFinite(tid) || !activeRiskTids.has(tid) || activeTids.has(tid)) continue;
          if (Number(det.miss_count ?? 0) > maxServerMissForDisplay(monitoringAiSettings)) {
            continue;
          }
          if (!Array.isArray(det.bbox) || det.bbox.length !== 4) continue;
          const conf = Number(det.score ?? det.confidence ?? 0);
          const sw = Number(det.frame_width) || frameW;
          const sh = Number(det.frame_height) || frameH;
          if (isLikelyClothingFragment(det.bbox as number[], conf, sw, sh)) continue;
          if (isLikelyPoolArtifact(det.bbox as number[], conf, sw, sh)) continue;
          if (ver5LaneStripePersonLockFp(det.bbox as number[], conf, sw, sh)) continue;
          if (lowerPoolEdgeRipplePersonFp(det.bbox as number[], conf, sw, sh)) continue;
          if (
            isOverheadMount &&
            !monitorApiDisplayAllowed(
              det.bbox as number[],
              conf,
              sw,
              sh,
              String(det.class_name || 'person'),
              Number(det.hits ?? 0)
            )
          ) {
            continue;
          }
          tracksSource.push(det);
          activeTids.add(tid);
        }
      }
      const nextHighlight = new Map<number, { meta: MotionRiskTrackMeta; until: number }>();
      highlightMap.forEach((entry, tid) => {
        if (entry.until > nowMs) {
          nextHighlight.set(tid, entry);
        }
      });
      riskFromAlerts.forEach((meta, tid) => {
        nextHighlight.set(tid, { meta, until: nowMs + MOTION_RISK_HIGHLIGHT_MS });
      });
      motionRiskHighlightRef.current[cctvId] = nextHighlight;

      const displayRisk = new Map<number, MotionRiskTrackMeta>();
      nextHighlight.forEach((entry, tid) => {
        if (entry.until > nowMs) {
          displayRisk.set(tid, entry.meta);
        }
      });

      syncDisplayTracksFromApi(cctvId, tracksSource, displayRisk);
      bboxMapRef.current[cctvId] = tracksSource;
      tracksRef.current[cctvId] = tracksSource;
      detectionDebugRef.current[cctvId] = {
        raw: visibleDetections.length,
        shown: visibleDetections.length,
      };
      forceUpdate(n => n + 1);

      if (data.risk_alerts?.length) {
        const popupAlerts = data.risk_alerts.filter((a: { risk_level?: string; risk_type?: string; source?: string }) =>
          passesAlertPopupGate(a, monitoringAiSettings)
        );
        const motionAlert = popupAlerts.find(
          (a: { risk_type?: string; risk_level?: string }) =>
            Boolean(String(a.risk_type || '').trim())
        );
        if (motionAlert) {
          const riskType = normalizeMotionRiskType(motionAlert.risk_type);
          const msg = motionRiskMessage(pool.name, riskType);
          const cctvRow = safetyCctvs.find((c) => Number(c.id) === Number(cctvId));
          const locationLabel = String(cctvRow?.location || cctvRow?.name || `CCTV ${cctvId}`);
          const appearance = resolveRiskAppearance(riskType, {
            label: (motionAlert as { risk_label?: string }).risk_label,
          });
          const riskLabel = appearance.label;
          const bboxColor = appearance.color;
          const imageDataUrl =
            playerHandlesRef.current[cctvId]?.captureFrame?.() ||
            captureVideoFrame(videoRefs.current[cctvId], {
              rotate180: Boolean(
                (cctvRow as { rotate_180?: boolean } | undefined)?.rotate_180
              ),
            });
          const trackMap = displayTracksRef.current[cctvId];
          const fromDisplay = collectEventBboxesFromDisplayTracks(
            trackMap ? Array.from(trackMap.values()) : [],
            riskFromAlerts
          );
          const bboxes = (
            fromDisplay.length > 0
              ? fromDisplay
              : collectEventBboxesFromDetections(tracksSource, riskFromAlerts)
          ).map((b) =>
            b.motionRisk
              ? {
                  ...b,
                  riskSubtypeLabel: b.riskSubtypeLabel || riskLabel,
                  borderColor: b.borderColor || bboxColor,
                }
              : b
          );
          onRiskEventRef.current({
            poolId: Number(poolId),
            poolName: pool.name,
            cctvId,
            location: locationLabel,
            riskType,
            riskLabel,
            message: msg,
            imageDataUrl,
            bboxes,
            safetyPhoneRaw: String(pool.safety_phone || '').trim(),
            poolSirens: Array.isArray(pool.sirens)
              ? pool.sirens.filter((s) => String(s).trim())
              : [],
            source: 'ai',
            triggerTs: Date.now() / 1000,
            scenarioProfile: pool.scenario_profile,
            detectionScenario: cctvRow?.detection_scenario ?? null,
            rotate_180: Boolean(
              (cctvRow as { rotate_180?: boolean } | undefined)?.rotate_180
            ),
          });
        }
        // 일반 인원 감지("[AI] N명 감지") 스낵바는 표시하지 않음
      }
      return;

    };

    const clearDetectionVisuals = () => {
      detectionQueue.current = [];
      detectionQueueBusy.current = false;
      processingDetections.current.clear();
      detectionCache.current.clear();
      tracksRef.current = {};
      bboxMapRef.current = {};
      displayTracksRef.current = {};
      setDisplayTracksSnapshot({});
      detectionStreakRef.current = {};
      motionRiskHighlightRef.current = {};
      forceUpdate((n) => n + 1);
    };

    // 위치 기반 그리드 정보 가져오기
    const { grid, maxRow, maxCol } = organizeByGridPosition();
    const stackedCctvs = stackCctvVertically
      ? [...safetyCctvs].sort(cctvLocationNaturalCompare)
      : [];
    const poolGridSpan = Math.max(1, displayPoolColumns ?? poolsPerRow);
    type LayoutCell = {
      cctv: Cctv;
      row: number;
      col: number;
      zoneIndex?: number;
      viewCrop?: ZoneRect;
    };
    const gridLayoutCells: LayoutCell[] =
      !stackCctvVertically && maxRow > 0 && maxCol > 0
        ? Array.from({ length: maxRow }).flatMap((_, row) =>
            Array.from({ length: maxCol }).flatMap((_, col) => {
              const cctv = grid[row + 1]?.[col + 1];
              return cctv ? [{ cctv, row, col }] : [];
            })
          )
        : [];
    const fullLayoutCells: LayoutCell[] = stackCctvVertically
      ? stackedCctvs.map((cctv, row) => ({ cctv, row, col: 0 }))
      : gridLayoutCells.length > 0
        ? gridLayoutCells
        : [...safetyCctvs]
            .sort(cctvLocationNaturalCompare)
            .map((cctv, row) => ({ cctv, row, col: 0 }));
    // 구역 타일: CCTV 전체 보기와 같은 순서로 펼침 (location 그리드·자연정렬)
    const zoneTiles = zoneTilesOnly
      ? listMonitoringZoneTiles(fullLayoutCells.map((c) => c.cctv))
      : [];
    const zoneTileCount = zoneTiles.length;
    // 열 수: 원래 관제 그리드 maxCol 유지 (sqrt 재배치로 순서 깨짐 방지)
    const zoneCols = stackCctvVertically
      ? 1
      : Math.max(
          1,
          maxCol > 0
            ? maxCol
            : Math.min(3, Math.ceil(Math.sqrt(Math.max(1, zoneTileCount))))
        );
    const zoneRows = Math.max(1, Math.ceil(zoneTileCount / zoneCols));
    const zoneLayoutCells: LayoutCell[] = zoneTiles.map((t, i) => ({
      cctv: t.cctv,
      zoneIndex: t.zoneIndex,
      viewCrop: t.viewCrop,
      row: Math.floor(i / zoneCols),
      col: i % zoneCols,
    }));
    const layoutCells: LayoutCell[] = zoneTilesOnly
      ? zoneLayoutCells
      : fullLayoutCells;
    const layoutRowCount = stackCctvVertically
      ? Math.max(1, stackedCctvs.length)
      : maxRow;
    const layoutColCount = stackCctvVertically ? 1 : maxCol;
    const effectiveLayoutRowCount = zoneTilesOnly
      ? zoneTileCount > 0
        ? zoneRows
        : 1
      : !stackCctvVertically &&
          gridLayoutCells.length === 0 &&
          safetyCctvs.length > 0
        ? Math.max(1, safetyCctvs.length)
        : layoutRowCount;
    const effectiveLayoutColCount = zoneTilesOnly
      ? zoneTileCount > 0
        ? zoneCols
        : 1
      : !stackCctvVertically &&
          gridLayoutCells.length === 0 &&
          safetyCctvs.length > 0
        ? 1
        : layoutColCount;
    void poolGridSpan;

    useEffect(() => {
      if (!isMonitored) return;
      let rafId = 0;
      const loop = () => {
        tickDisplayTrackInterpolation();
        rafId = requestAnimationFrame(loop);
      };
      rafId = requestAnimationFrame(loop);
      return () => cancelAnimationFrame(rafId);
    }, [isMonitored, safetyCctvs.length]);

    useEffect(() => {
      if (poolExcluded) return;
      const touched = safetyCctvs.some(
        (c) =>
          serverSnapshotsByCctv[
            serverSnapshotKey(Number(poolId), Number(c.id))
          ] != null
      );
      if (touched) setOverlayLayoutTick((n) => n + 1);
    }, [serverSnapshotsByCctv, safetyCctvs, poolExcluded]);

    useEffect(() => {
      isDetectingRef.current = isMonitored;
      if (!isMonitored) {
        if (detectionIntervalRef.current) {
          clearInterval(detectionIntervalRef.current);
          detectionIntervalRef.current = null;
        }
        clearDetectionVisuals();
        return undefined;
      }
      setOverlayLayoutTick((n) => n + 1);
      const applyServerSnapshots = () => {
        safetyCctvs.forEach((cctv) => {
          const cctvId = Number(cctv.id);
          if (!Number.isFinite(cctvId)) return;
          const payload = lookupServerSnapshot(
            serverSnapshotsByCctv,
            Number(poolId),
            cctvId
          );
          if (payload) {
            updateDetectionResults(cctvId, payload, { trustServer: true });
          }
        });
      };
      applyServerSnapshots();

      if (USE_HYBRID_MONITOR_PIPELINE && HYBRID_CLIENT_YOLO_FALLBACK) {
        safetyCctvs.forEach((cctv) => {
          startDetectionWhenReady(Number(cctv.id));
        });
        const baseMs = getDetectionCooldownMs(
          safetyCctvs.length,
          monitoringAiSettings
        );
        const hybridMs = Math.max(1100, baseMs * 2);
        detectionIntervalRef.current = setInterval(() => {
          safetyCctvs.forEach((cctv) => {
            const cid = Number(cctv.id);
            if (!Number.isFinite(cid)) return;
            const dbg = detectionDebugRef.current[cid];
            const shown = dbg?.shown ?? 0;
            if (shown > 0) return;
            if (
              processingDetections.current.has(cid) ||
              detectionQueue.current.includes(cid)
            ) {
              return;
            }
            detectionQueue.current.push(cid);
          });
          processDetectionQueue();
        }, hybridMs);
      }

      return () => {
        if (detectionIntervalRef.current) {
          clearInterval(detectionIntervalRef.current);
          detectionIntervalRef.current = null;
        }
      };
    }, [isMonitored, serverSnapshotsByCctv, safetyCctvs, monitoringAiSettings]);
    
    useEffect(() => {
      const handleMessage = (data: unknown) => {
        if (centralRiskUiRef.dialogOpen) return;
        const msg = data as { pool_id?: number; risk_type?: string; type?: string };
        const pid = msg?.pool_id ?? (data as { data?: { pool_id?: number } })?.data?.pool_id;
        if (Number(pid) !== Number(pool.id)) return;
        showPoolSnack({
          type: msg.risk_type || 'risk',
          message: `${pool.name}에서 이벤트가 발생${msg.risk_type ? ` (${msg.risk_type})` : ''}`,
        });
      };
      monitoringWebSocket.subscribe('events', handleMessage);
      return () => monitoringWebSocket.unsubscribe('events', handleMessage);
    }, [pool.id, pool.name, showPoolSnack]);

    useEffect(() => {
      if (riskDialogOpen) {
        setLocalRiskAlert(null);
      }
    }, [riskDialogOpen]);

    useEffect(() => {
      // 토글 변경 시 기존 bbox도 즉시 반영
      Object.keys(bboxMapRef.current).forEach((cctvId) => {
        bboxMapRef.current[Number(cctvId)] = (bboxMapRef.current[Number(cctvId)] || []).filter((det: any) => {
          const className = (det.class_name || '').toLowerCase();
          return visibleClassMap[className] !== false;
        });
      });
      forceUpdate(n => n + 1);
    }, [visibleClassMap]);

    useEffect(() => {
      return () => {
        isDetectingRef.current = false;
        if (detectionIntervalRef.current) {
          clearInterval(detectionIntervalRef.current);
          detectionIntervalRef.current = null;
        }
      };
    }, []);

    return (
      <Grid item xs={12} sm={12 / poolGridSpan}>
        <Paper 
          sx={{ 
            p: 0, 
            pt: { xs: 1.25, sm: 2 },
            pb: 0,
            pl: { xs: 1, sm: 2 },
            pr: { xs: 1, sm: 2 },
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            borderLeft: 3, 
            borderColor: getPoolStatusColor(pool.status)
          }}
        >
          <Box
            sx={{
              mb: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
                minWidth: 0,
              }}
            >
              <Typography variant="h6" sx={{ mb: 0, whiteSpace: 'nowrap' }}>
                {pool.name}
              </Typography>
              <RiskSimulateBar
                scenarioProfile={pool.scenario_profile}
                cctvOptions={[...safetyCctvs]
                  .sort(cctvLocationNaturalCompare)
                  .map((c) => ({
                    id: c.id,
                    name:
                      String(c.location || '').trim() ||
                      String(c.name || '').trim() ||
                      `CCTV #${c.id}`,
                    detection_scenario: c.detection_scenario,
                  }))}
                onPickCctv={handleManualEventForCctv}
              />
              <DetectionLabelToolbar mode={labelMode} onChange={setLabelMode} />
              <Tooltip
                arrow
                placement="bottom"
                title={
                  <Box sx={{ maxWidth: 280, p: 0.5 }}>
                    <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
                      ON = 감시구역(물 영역) 박스만 크롭 타일로 표시.
                      OFF = CCTV 전체 프레임. 시설 설정 기본값을 따르며, 이
                      토글은 현재 관제 세션에서만 바뀜.
                    </Typography>
                  </Box>
                }
              >
                <FormControlLabel
                  sx={{ ml: 0.5, mr: 0, gap: 0.5, flexShrink: 0 }}
                  label={
                    <Typography
                      variant="caption"
                      sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                    >
                      {`구역만 ${zoneTilesOnly ? 'ON' : 'OFF'}`}
                    </Typography>
                  }
                  control={
                    <Switch
                      size="small"
                      checked={zoneTilesOnly}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(_e, checked) => setZoneTilesOnly(checked)}
                    />
                  }
                />
              </Tooltip>
              <Tooltip
                arrow
                placement="bottom"
                title={
                  <Box sx={{ maxWidth: 280, p: 0.5 }}>
                    <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
                      OFF면 이 시설의 서버 AI(OpenCV·GPU)만 중지. 그리드에
                      보이는 카메라는 브라우저 HLS 재생이 계속되므로, 화면 부하를
                      줄이려면 시설 카드를 숨기거나 가로 시설 수를 줄여 마운트된
                      영상을 줄여야
                    </Typography>
                  </Box>
                }
              >
                <FormControlLabel
                  sx={{ ml: 0.5, mr: 0, gap: 0.5, flexShrink: 0 }}
                  label={
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                      {`AI 탐지 ${poolExcluded ? 'OFF' : 'ON'}`}
                    </Typography>
                  }
                  control={
                    <Switch
                      size="small"
                      checked={!poolExcluded}
                      disabled={excludeBusyPoolId === Number(poolId)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(_e, checked) => {
                        if (excludeBusyPoolId === Number(poolId)) return;
                        const wantMonitored = checked;
                        const isMonitored = !poolExcluded;
                        if (wantMonitored === isMonitored) return;
                        onTogglePoolExclude(Number(poolId));
                      }}
                    />
                  }
                />
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              <Typography variant="caption" sx={{ mr: 1 }}>
                {pool.code}
              </Typography>
              <Tooltip title="알림 메시지 전송">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => onOpenManualAlertRef.current(pool)}
                  sx={{ mr: 0.5 }}
                >
                  <NotificationsActiveIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {!hideFullscreenButton && (
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  aria-label="전체화면"
                  title="전체화면"
                  startIcon={<FullscreenIcon sx={{ fontSize: 16 }} />}
                  onClick={handleViewAllCctvs}
                  sx={{
                    mr: 0.5,
                    minWidth: 0,
                    px: 1,
                    py: 0.15,
                    fontSize: '0.75rem',
                    lineHeight: 1.4,
                    textTransform: 'none',
                  }}
                >
                  전체화면
                </Button>
              )}
              {!disableCctvRotationClick && (
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  aria-label="순환 보기"
                  title="순환 보기"
                  startIcon={<SlideshowIcon sx={{ fontSize: 16 }} />}
                  onClick={handleOpenRotationCctvs}
                  sx={{
                    minWidth: 0,
                    px: 1,
                    py: 0.15,
                    fontSize: '0.75rem',
                    lineHeight: 1.4,
                    textTransform: 'none',
                  }}
                >
                  순환 보기
                </Button>
              )}
            </Box>
          </Box>
          {labelFeedback && (
            <Typography variant="caption" color="success.main" sx={{ mb: 0.5, display: 'block' }}>
              {labelFeedback}
            </Typography>
          )}
          {/* 시설 전체화면(z=1800)에서도 라벨 저장 결과 보이게 */}
          <Snackbar
            open={Boolean(labelFeedback)}
            autoHideDuration={4000}
            onClose={() => setLabelFeedback(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            sx={{ zIndex: 2200 }}
          >
            <Alert
              severity={
                labelFeedback && /실패/.test(labelFeedback) ? 'error' : 'success'
              }
              onClose={() => setLabelFeedback(null)}
              sx={{ width: '100%' }}
            >
              {labelFeedback}
            </Alert>
          </Snackbar>

          {hasCctvs ? (
            zoneTilesOnly && zoneTileCount === 0 ? (
              <Box
                sx={{
                  p: 2,
                  bgcolor: '#111',
                  color: '#bbb',
                  fontSize: '0.85rem',
                  lineHeight: 1.5,
                }}
              >
                감시구역 박스만 보기(ON)인데 구역이 지정된 CCTV가 없음.
                CCTV 관리에서 물 영역 박스를 지정하거나, 시설 설정에서 토글을 OFF.
              </Box>
            ) : (
            <>
              <Box 
                sx={{ 
                  display: 'grid',
                  gridTemplateRows: `repeat(${effectiveLayoutRowCount}, 1fr)`,
                  gridTemplateColumns: `repeat(${effectiveLayoutColCount}, 1fr)`,
                  gap:
                    zoneTilesOnly ||
                    stackCctvVertically ||
                    gridLayoutCells.length === 0
                      ? 0.75
                      : 0,
                  width: '100%',
                  backgroundColor: '#000',
                }}
              >
                {layoutCells.map(({ cctv, row, col, zoneIndex = 0, viewCrop }) => (
                      <Tooltip
                        key={
                          zoneTilesOnly
                            ? `${cctv.id}-z${zoneIndex}`
                            : String(cctv.id)
                        }
                        title={
                          disableCctvRotationClick
                            ? zoneTilesOnly
                              ? `${cctv.name || cctv.id} · 구역 ${zoneIndex + 1}`
                              : ''
                            : labelMode !== 'off'
                              ? '라벨링 모드 — 미검출은 드래그, 오검출은 박스 클릭 (순환 보기 안 열림)'
                              : zoneTilesOnly
                                ? `${cctv.name || cctv.id} · 구역 ${zoneIndex + 1} — 클릭: 순환 보기`
                                : '클릭: 순환 보기(일시정지)'
                        }
                        disableInteractive
                      >
                      <Box 
                        onClick={
                          disableCctvRotationClick || labelMode !== 'off'
                            ? undefined
                            : () =>
                                handleOpenRotationAtCctv(
                                  Number(cctv.id),
                                  zoneTilesOnly ? zoneIndex : undefined
                                )
                        }
                        ref={(el: HTMLDivElement | null) => {
                          attachOverlayResizeObserver(
                            zoneTilesOnly
                              ? `${Number(cctv.id)}-z${zoneIndex}`
                              : Number(cctv.id),
                            el
                          );
                        }}
                        sx={{ 
                          gridRow: row + 1,
                          gridColumn: col + 1,
                          position: 'relative',
                          overflow: 'hidden',
                          borderRadius: 0,
                          width: '100%',
                          aspectRatio: '16/9',
                          m: 0,
                          p: 0,
                          backgroundColor: '#000',
                          border: 'none',
                          cursor:
                            labelMode === 'missed_detection'
                              ? 'crosshair'
                              : labelMode === 'false_positive'
                                ? 'pointer'
                                : disableCctvRotationClick || !playbackConfigReady
                                  ? 'default'
                                  : 'pointer',
                          '&:hover':
                            !disableCctvRotationClick &&
                            playbackConfigReady &&
                            labelMode === 'off'
                              ? {
                                  outline: '1px solid rgba(76, 175, 80, 0.65)',
                                  outlineOffset: -1,
                                }
                              : undefined,
                        }}
                      >
                        {playbackConfigReady ? (
                        <>
                        <CentralMonitoringCctvPlayer
                          cctv={cctv}
                          viewCrop={viewCrop}
                          rotate180={Boolean(
                            (cctv as { rotate_180?: boolean }).rotate_180
                          )}
                          effectiveStreamUrl={resolveAbsoluteCctvStreamUrl(
                            cctv,
                            useRemotePlayback,
                            siteGatewayEnabled
                          )}
                          centralGo2rtcAvailable={centralGo2rtcAvailable}
                          centralGo2rtcStreams={centralGo2rtcStreams}
                          bufferSize={bufferSize}
                          globalQuality={globalQuality}
                          playbackResumeNonce={playbackResumeNonce}
                          streamReloadNonce={streamReloadNonce}
                          onOpenRotationView={
                            labelMode !== 'off'
                              ? undefined
                              : () =>
                                  handleOpenRotationAtCctv(
                                    Number(cctv.id),
                                    zoneTilesOnly ? zoneIndex : undefined
                                  )
                          }
                          ref={(el) => {
                            playerHandlesRef.current[Number(cctv.id)] = el;
                            const v =
                              el?.getVideoElement?.() ?? el?.videoRef?.current ?? null;
                            videoRefs.current[Number(cctv.id)] = v;
                          }}
                          onVideoElementReady={(video) => {
                            videoRefs.current[Number(cctv.id)] = video;
                          }}
                        />
                        </>
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: '#111',
                            }}
                          >
                            <CircularProgress size={28} />
                          </Box>
                        )}
                        {(() => {
                          const cctvId = Number(cctv.id);
                          const tileOverlayKey = zoneTilesOnly
                            ? `${cctvId}-z${zoneIndex}`
                            : String(cctvId);
                          const serverSnap = poolExcluded
                            ? undefined
                            : lookupServerSnapshot(
                            serverSnapshotsByCctv,
                                Number(poolId),
                                cctvId
                              );
                          const useServerOverlay =
                            !poolExcluded &&
                            !USE_HYBRID_MONITOR_PIPELINE &&
                            (TRUST_SERVER_SNAPSHOT_OVERLAY ||
                              DRAW_RAW_SERVER_SNAPSHOTS);
                          const snap = displayTracksSnapshot[cctvId] || [];
                          let overlayTracks: OverlayTrack[];
                          if (poolExcluded) {
                            overlayTracks = [];
                            detectionDebugRef.current[cctvId] = {
                              raw: -2,
                              shown: 0,
                            };
                          } else if (useServerOverlay && serverSnap) {
                            const rawLen = Array.isArray(serverSnap.detections)
                              ? serverSnap.detections.length
                              : 0;
                            if (snap.length > 0 || USE_HYBRID_MONITOR_PIPELINE) {
                              overlayTracks =
                                snap.length > 0
                                  ? snap.map((t) => {
                                      const match =
                                        wearableTrackMap[String(t.trackId)];
                                      return {
                                        ...t,
                                        wearableId: match?.wearable_id,
                                        wearableConfidence: match?.confidence,
                                      } as OverlayTrack;
                                    })
                                  : [];
                            } else {
                              overlayTracks = overlayTracksFromServerSnapshot(
                                serverSnap,
                                wearableTrackMap
                              );
                            }
                            detectionDebugRef.current[cctvId] = {
                              raw: rawLen,
                              shown: overlayTracks.length,
                            };
                          } else if (snap.length > 0) {
                            overlayTracks = snap.map((t) => {
                              const match = wearableTrackMap[String(t.trackId)];
                              return {
                                ...t,
                                wearableId: match?.wearable_id,
                                wearableConfidence: match?.confidence,
                              } as OverlayTrack;
                            });
                          } else if (
                            serverSnap &&
                            Array.isArray(serverSnap.detections) &&
                            serverSnap.detections.length > 0
                          ) {
                            overlayTracks = overlayTracksFromServerSnapshot(
                              serverSnap,
                              wearableTrackMap
                            );
                            detectionDebugRef.current[cctvId] = {
                              raw: serverSnap.detections.length,
                              shown: overlayTracks.length,
                            };
                          } else if (useServerOverlay) {
                            overlayTracks = [];
                            detectionDebugRef.current[cctvId] = {
                              raw: serverSnap
                                ? (serverSnap.detections?.length ?? 0)
                                : -1,
                              shown: 0,
                            };
                          } else {
                            overlayTracks = (bboxMapRef.current[cctvId] || []).map(
                              (
                                det: {
                                  track_id?: number;
                                  id?: number;
                                  bbox: number[];
                                  class_name: string;
                                  score?: number;
                                  confidence?: number;
                                  frame_width?: number;
                                  frame_height?: number;
                                },
                                i: number,
                              ) => {
                                const trackId = det.track_id ?? det.id ?? i;
                                const match = wearableTrackMap[String(trackId)];
                                return {
                                  trackId,
                                  displayBbox: det.bbox,
                                  class_name: det.class_name,
                                  score: det.score ?? det.confidence ?? 0,
                                  frame_width: det.frame_width,
                                  frame_height: det.frame_height,
                                  alpha: 1,
                                  wearableId: match?.wearable_id,
                                  wearableConfidence: match?.confidence,
                                };
                              },
                            );
                          }
                          const poseOverlayRaw =
                            !poolExcluded &&
                            monitoringAiSettings.openpose_enabled &&
                            monitoringAiSettings.openpose_show_skeleton &&
                            serverSnap?.openpose_overlay
                              ? serverSnap.openpose_overlay
                              : null;
                          const poseOverlay =
                            poseOverlayRaw &&
                            (!monitoringAiSettings.openpose_show_skeleton_trigger_only ||
                              serverSnap?.openpose_ran)
                              ? poseOverlayRaw
                              : null;
                          overlayTracks = enrichOverlayTracksWithOpenPose(
                            overlayTracks,
                            serverSnap?.openpose_overlay ?? null
                          );
                          const poseContainer =
                            overlayContainerRefs.current[tileOverlayKey];
                          const poseVid = videoRefs.current[cctvId];
                          const doRotate180 = Boolean(
                            (cctv as { rotate_180?: boolean }).rotate_180
                          );
                          let croppedTracks = viewCrop
                            ? overlayTracks
                                .map((t) => {
                                  const remapped = remapBboxToCrop(
                                    t.displayBbox,
                                    viewCrop
                                  );
                                  if (!remapped) return null;
                                  return {
                                    ...t,
                                    displayBbox: remapped.bbox,
                                    frame_width: remapped.frameW,
                                    frame_height: remapped.frameH,
                                  };
                                })
                                .filter(
                                  (t): t is NonNullable<typeof t> => t != null
                                )
                            : overlayTracks;
                          const poseFw = Math.max(
                            1,
                            viewCrop
                              ? viewCrop[2] - viewCrop[0]
                              : Number(serverSnap?.frame_width) ||
                                  overlayTracks[0]?.frame_width ||
                                  poseVid?.videoWidth ||
                                  1280
                          );
                          const poseFh = Math.max(
                            1,
                            viewCrop
                              ? viewCrop[3] - viewCrop[1]
                              : Number(serverSnap?.frame_height) ||
                                  overlayTracks[0]?.frame_height ||
                                  poseVid?.videoHeight ||
                                  720
                          );
                          // 영상만 CSS 180 — 오버레이는 좌표만 맞춰 글자는 정상
                          if (doRotate180) {
                            croppedTracks = croppedTracks.map((t) => {
                              const fw = Math.max(
                                1,
                                Number(t.frame_width) || poseFw
                              );
                              const fh = Math.max(
                                1,
                                Number(t.frame_height) || poseFh
                              );
                              return {
                                ...t,
                                displayBbox: remapBboxRotate180(
                                  t.displayBbox,
                                  fw,
                                  fh
                                ),
                              };
                            });
                          }
                          // 구역 좌표는 보통 스트림(video) 해상도 기준 — 스냅샷 크기와 다를 수 있음
                          const zoneFw = Math.max(
                            1,
                            poseVid?.videoWidth || poseFw
                          );
                          const zoneFh = Math.max(
                            1,
                            poseVid?.videoHeight || poseFh
                          );
                          const displayRiskZones = doRotate180
                            ? remapZoneListRotate180(
                                cctv.risk_detection_zones as
                                  | number[][]
                                  | null
                                  | undefined,
                                zoneFw,
                                zoneFh
                              )
                            : cctv.risk_detection_zones;
                          const displaySpecialZones = doRotate180
                            ? remapZoneListRotate180(
                                cctv.special_management_zones as
                                  | number[][]
                                  | null
                                  | undefined,
                                zoneFw,
                                zoneFh
                              )
                            : cctv.special_management_zones;
                          const poseUsePx =
                            poseContainer != null &&
                            (poseContainer.clientWidth || 0) > 0 &&
                            (poseContainer.clientHeight || 0) > 0;
                          return (
                            <Box
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                pointerEvents: 'none',
                                zIndex: 50,
                              }}
                            >
                              <CentralMonitoringOverlays
                                cctvId={cctvId}
                                riskZones={
                                  viewCrop ? undefined : displayRiskZones
                                }
                                specialZones={
                                  viewCrop ? undefined : displaySpecialZones
                                }
                                tracks={croppedTracks}
                                containerEl={
                                  overlayContainerRefs.current[tileOverlayKey] ??
                                  null
                                }
                                videoEl={
                                  viewCrop
                                    ? null
                                    : videoRefs.current[cctvId]
                                }
                                layoutTick={overlayLayoutTick}
                                detectionDebug={
                                  detectionDebugRef.current[cctvId] ?? {
                                    raw: overlayTracks.length,
                                    shown: croppedTracks.length,
                                  }
                                }
                              />
                              {/* 구역 크롭·180회전 시 OpenPose는 좌표 복잡도 ↑ → 생략 */}
                              {!viewCrop && !doRotate180 && poseOverlay ? (
                                <OpenPoseSkeletonLayer
                                  overlay={poseOverlay}
                                  sourceWidth={poseFw}
                                  sourceHeight={poseFh}
                                  containerWidth={
                                    poseUsePx ? poseContainer!.clientWidth : 100
                                  }
                                  containerHeight={
                                    poseUsePx ? poseContainer!.clientHeight : 100
                                  }
                                  videoWidth={poseVid?.videoWidth}
                                  videoHeight={poseVid?.videoHeight}
                                  usePxCoords={poseUsePx}
                                />
                              ) : null}
                              <DetectionLabelLayer
                                poolId={Number(pool.id)}
                                cctvId={cctvId}
                                mode={labelMode}
                                tracks={croppedTracks}
                                rotate180={doRotate180}
                                containerEl={
                                  overlayContainerRefs.current[tileOverlayKey]
                                }
                                videoEl={
                                  viewCrop
                                    ? null
                                    : videoRefs.current[cctvId]
                                }
                                onSaved={(m) => {
                                  setLabelFeedback(m);
                                  setTimeout(() => setLabelFeedback(null), 4000);
                                }}
                                onError={(m) => {
                                  setLabelFeedback(m);
                                  setTimeout(() => setLabelFeedback(null), 6000);
                                }}
                              />
                            </Box>
                          );
                        })()}
                        <CctvGpuSelector
                          poolId={Number(poolId)}
                          cctvId={Number(cctv.id)}
                          gpuStatus={gpuAllocationStatus}
                          manualMode={!monitoringAiSettings.gpu_auto_assign}
                          serverInferenceActive={serverInferenceActive}
                          onGpuChanged={(gpuIndex) =>
                            onCctvGpuChanged(Number(poolId), Number(cctv.id), gpuIndex)
                          }
                        />
                        {zoneTilesOnly ? (
                          <Box
                            sx={{
                              position: 'absolute',
                              left: 6,
                              bottom: 6,
                              zIndex: 60,
                              px: 0.75,
                              py: 0.25,
                              borderRadius: 0.5,
                              bgcolor: 'rgba(0,0,0,0.55)',
                              color: '#fff',
                              fontSize: '0.7rem',
                              lineHeight: 1.3,
                              maxWidth: '90%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              pointerEvents: 'none',
                            }}
                          >
                            {(cctv.name || `CCTV ${cctv.id}`) +
                              ` · 구역 ${zoneIndex + 1}`}
                          </Box>
                        ) : null}
                      </Box>
                      </Tooltip>
                ))}
              </Box>
            </>
            )
          ) : (
            <Box sx={{ py: 2, textAlign: 'center', flexGrow: 1 }}>
              <Typography variant="body2" color="text.secondary">
                등록된 CCTV가 없음
              </Typography>
            </Box>
          )}
          {/* 위험 알림 스낵바 — 인원수 안내([AI] N명 감지)는 표시하지 않음 */}
          <Snackbar
            open={
              Boolean(localRiskAlert) &&
              localRiskAlert?.type !== 'ai' &&
              !isPersonCountAiSnackMessage(localRiskAlert?.message || '') &&
              !riskDialogOpen
            }
            autoHideDuration={5000}
            onClose={() => setLocalRiskAlert(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert severity="error" sx={{ width: '100%' }}>
              {localRiskAlert?.message}
            </Alert>
          </Snackbar>
        </Paper>
      </Grid>
    );
    };
    return PoolGridItemComponent;
  }, [POOL_GRID_ITEM_REV]);

  const [isAutoOptimizing, setIsAutoOptimizing] = useState<boolean>(false);
  const [optimizationStatus, setOptimizationStatus] = useState<string>('');

  // 시스템 성능 분석 함수
  const analyzeSystemPerformance = async () => {
    setIsAutoOptimizing(true);
    setOptimizationStatus('시스템 성능 분석 중...');

    try {
      // 메모리 사용량 확인 (지원 브라우저에서만)
      type PerformanceWithMemory = Performance & {
        memory?: {
          jsHeapSizeLimit: number;
          usedJSHeapSize: number;
        };
      };
      const perf = performance as PerformanceWithMemory;
      const memoryInfo = perf.memory;
      const availableMemory = memoryInfo ? memoryInfo.jsHeapSizeLimit - memoryInfo.usedJSHeapSize : 0;
      
      // 네트워크 속도 테스트
      const startTime = performance.now();
      const response = await fetch('/api/ping');
      const endTime = performance.now();
      const networkLatency = endTime - startTime;

      // CPU 사용량 확인 (간접적인 방법)
      const cpuTestStart = performance.now();
      let cpuScore = 0;
      for (let i = 0; i < 1000000; i++) {
        cpuScore += Math.sqrt(i);
      }
      const cpuTestTime = performance.now() - cpuTestStart;

      // 최적의 설정 결정
      let recommendedQuality = 'auto';
      let recommendedBuffer = 60;

      // 메모리 기반 버퍼 크기 조정
      if (availableMemory > 500 * 1024 * 1024) { // 500MB 이상
        recommendedBuffer = 120;
      } else if (availableMemory > 200 * 1024 * 1024) { // 200MB 이상
        recommendedBuffer = 90;
      } else if (availableMemory < 100 * 1024 * 1024) { // 100MB 미만
        recommendedBuffer = 30;
      }

      // 네트워크 상태 기반 해상도 조정
      if (networkLatency < 100) { // 100ms 미만
        recommendedQuality = '1080p';
      } else if (networkLatency < 200) { // 200ms 미만
        recommendedQuality = '720p';
      } else if (networkLatency < 300) { // 300ms 미만
        recommendedQuality = '480p';
      } else {
        recommendedQuality = '360p';
      }

      // CPU 성능 기반 조정
      if (cpuTestTime > 1000) { // CPU 성능이 낮은 경우
        recommendedQuality = '480p';
        recommendedBuffer = Math.min(recommendedBuffer, 60);
      }

      // 설정 적용
      setGlobalQuality(recommendedQuality);
      setBufferSize(recommendedBuffer);
      localStorage.setItem('globalQuality', recommendedQuality);
      localStorage.setItem('bufferSize', recommendedBuffer.toString());

      setOptimizationStatus(`최적화 완료: 해상도 ${recommendedQuality}, 버퍼 ${recommendedBuffer}MB`);
    } catch (error) {
      console.error('시스템 최적화 중 오류:', error);
      setOptimizationStatus('최적화 실패');
    } finally {
      setIsAutoOptimizing(false);
    }
  };

  // 자동 최적화 버튼 핸들러
  const handleAutoOptimize = () => {
    analyzeSystemPerformance();
  };

  if (error && pools.length === 0) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" startIcon={<RefreshIcon />} onClick={handleRefresh}>
          다시 시도
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <AppBar position="static">
        <Toolbar
          sx={{
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: { xs: 0.75, sm: 1 },
            py: { xs: 0.75, sm: 0 },
            minHeight: { xs: 48, sm: 64 },
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: { xs: 0.5, sm: 1 },
              minWidth: 0,
              width: { xs: '100%', md: 'auto' },
            }}
          >
            <KymaBrandTitle variant={brandVariant} tone="onPrimary" size="medium" />
            <MonitoringOpsChips />
            {brandVariant === 'individual' ? (
              <CommunicationToolbarMenu />
            ) : (
              <OperatorMessageToolbarButton />
            )}
          </Box>
          <AllPoolsMonitoringPopup
            ref={allPoolsMonitoringRef}
            pools={monitoringPools}
            poolCctvs={poolCctvs}
            poolsPerRow={poolsPerRow}
            useRemotePlayback={useRemotePlayback}
            rotationIntervalSec={rotationIntervalSec}
            adminIdFilter={assignedAdminIdFilter}
          />
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 0.75, sm: 1.25 },
              alignItems: 'center',
              flexWrap: 'wrap',
              width: { xs: '100%', lg: 'auto' },
              justifyContent: { xs: 'flex-start', lg: 'flex-end' },
            }}
          >
            <Tooltip
              title="켜면 위험 이벤트 창이 뜰 때 등록된 채널(문자·카카오·웨어러블)로 알림을 즉시 자동 발송. 끄면 이벤트 창만 표시되며 알림은 수동으로 보내기 가능."
            >
              <FormControlLabel
                sx={{
                  mr: 0,
                  ml: 0.5,
                  px: 1,
                  borderRadius: 1,
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }}
                control={
                  <Switch
                    size="small"
                    color="default"
                    checked={autoAlertEnabled}
                    onChange={(_, checked) => handleToggleAutoAlert(checked)}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.87)' }}>
                    자동 알림
                  </Typography>
                }
                labelPlacement="start"
              />
            </Tooltip>
            {/* 자동 최적화 버튼 */}
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              onClick={handleAutoOptimize}
              disabled={isAutoOptimizing}
              startIcon={
                isAutoOptimizing ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <AutoFixHighIcon sx={{ fontSize: 18 }} />
                )
              }
              sx={{
                minWidth: 0,
                px: 1.25,
                py: 0.6,
                minHeight: 36,
                fontSize: '0.8125rem',
                lineHeight: 1.4,
                backgroundColor: 'rgba(255,255,255,0.1)',
                '& .MuiButton-startIcon': { mr: 0.5, ml: 0 },
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.2)',
                },
              }}
            >
              {isAutoOptimizing ? '최적화 중...' : isCompactToolbar ? '최적화' : '자동 최적화'}
            </Button>
            {/* 최적화 상태 표시 */}
            {optimizationStatus && (
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'white',
                  backgroundColor: 'rgba(0,0,0,0.3)',
                  padding: '4px 8px',
                  borderRadius: '4px'
                }}
              >
                {optimizationStatus}
              </Typography>
            )}
            {/* 설정 초기화 버튼 */}
            <Tooltip title="설정 초기화">
              <IconButton
                color="inherit"
                onClick={resetSettings}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.2)'
                  }
                }}
              >
                <RestartAltIcon />
              </IconButton>
            </Tooltip>
            {/* 해상도 선택 — HLS만. WebRTC 타일은 원본 */}
            <Tooltip
              title="HLS 타일에만 적용. WebRTC(초록 점)는 현장 원본 — 이 선택 무시"
              arrow
            >
              <FormControl size="small" sx={{ width: 98, minWidth: 98, flexShrink: 0 }}>
                <InputLabel id="quality-select-label">해상도(HLS)</InputLabel>
                <Select
                  labelId="quality-select-label"
                  value={globalQuality}
                  label="해상도(HLS)"
                  onChange={handleGlobalQualityChange}
                  sx={{ 
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.2)'
                    },
                    '& .MuiSelect-select': { pr: '28px !important' },
                  }}
                >
                  {qualityOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Tooltip>
            {/* 버퍼 크기 선택 — hls.js에 실제 반영 (최대 15초). WebRTC 무관 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
              <FormControl size="small" sx={{ width: 128, minWidth: 128 }}>
                <InputLabel id="buffer-select-label">버퍼(HLS)</InputLabel>
                <Select
                  labelId="buffer-select-label"
                  value={bufferSize}
                  label="버퍼(HLS)"
                  onChange={handleBufferSizeChange}
                  sx={{ 
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.2)'
                    },
                    '& .MuiSelect-select': { pr: '28px !important' },
                  }}
                >
                  {bufferOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Tooltip title={bufferSizeHelpTitle} arrow placement="bottom-start">
                <IconButton
                  size="small"
                  color="inherit"
                  aria-label="해상도·버퍼·WebRTC 안내"
                  sx={{ opacity: 0.9 }}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            {/* 가로 시설 수 선택 */}
            <FormControl size="small" sx={{ width: 96, minWidth: 96, flexShrink: 0 }}>
              <InputLabel id="columns-select-label">가로 시설 수</InputLabel>
              <Select
                labelId="columns-select-label"
                value={poolsPerRow}
                label="가로 시설 수"
                onChange={handleColumnsChange}
                sx={{ 
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.2)'
                  },
                  '& .MuiSelect-select': { pr: '28px !important' },
                }}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                  <MenuItem key={num} value={num}>
                    {num}개
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              color="inherit"
              startIcon={<GridViewIcon />}
              onClick={() => allPoolsMonitoringRef.current?.openAllPoolsInNewWindow()}
              sx={{ mr: { xs: 0, sm: 1 }, display: { xs: 'none', md: 'inline-flex' } }}
            >
              새창에서 모니터링
            </Button>
            <Tooltip title="새창에서 모니터링">
              <IconButton
                color="inherit"
                onClick={() => allPoolsMonitoringRef.current?.openAllPoolsInNewWindow()}
                sx={{ display: { xs: 'inline-flex', md: 'none' } }}
                aria-label="새창에서 모니터링"
              >
                <GridViewIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Button 
            color="inherit" 
            size={isMobile ? 'small' : 'medium'}
            startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? '새로고침 중…' : '새로고침'}
          </Button>
          {centralMonitoringPopup ? (
            <Button
              color="inherit"
              variant="outlined"
              size="small"
              startIcon={<CloseIcon />}
              onClick={handleCloseMonitoringWindow}
              sx={{
                ml: 1,
                borderColor: 'rgba(255,255,255,0.5)',
                flexShrink: 0,
              }}
            >
              창 닫기
            </Button>
          ) : null}
          {showIndividualLogout ? (
            <Tooltip title="로그아웃">
              <IconButton
                color="inherit"
                aria-label="로그아웃"
                onClick={() => logout()}
                size="small"
                sx={{
                  ml: 1,
                  flexShrink: 0,
                  border: '1px solid rgba(255,255,255,0.45)',
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.15)',
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Toolbar>
      </AppBar>
      {error ? (
        <Alert severity="warning" onClose={() => setError(null)} sx={{ borderRadius: 0 }}>
          {error}
        </Alert>
      ) : null}
      {!poolFullscreen && !cctvRotation ? (
      <Container sx={{ 
        flexGrow: 1, 
        p: { xs: 1, sm: 2 }, 
        maxWidth: { xs: '100% !important', sm: '95% !important' },
        width: { xs: '100%', sm: '95%' },
        margin: '0 auto',
        overflowY: 'auto',
      }}>
        <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ width: '100%' }}>
          {monitoringPools.map((pool) => (
            <PoolGridItem
              key={pool.id}
              pool={pool}
              cctvs={poolCctvs[String(pool.id)] ?? []}
              poolsPerRow={poolsPerRow}
              displayPoolColumns={effectivePoolColumns}
              stackCctvVertically={stackCctvVertically}
              wearableTrackMap={wearableTrackMap}
              bufferSize={bufferSize}
              globalQuality={globalQuality}
              playbackConfigReady={playbackConfigReady}
              useRemotePlayback={useRemotePlayback}
              centralGo2rtcAvailable={centralGo2rtcAvailable}
              centralGo2rtcStreams={centralGo2rtcStreams}
              monitoringAiSettings={monitoringAiSettings}
              centralOperatingPoolIds={poolIds}
              serverSnapshotsByCctv={serverSnapshotsByCctv}
              excludeBusyPoolId={excludeBusyPoolId}
              onTogglePoolExclude={handleTogglePoolExclude}
              gpuAllocationStatus={gpuAllocationStatus}
              serverInferenceActive={centralServerInferenceActive}
              onCctvGpuChanged={handleCctvGpuChanged}
              rotationIntervalSec={rotationIntervalSec}
              totalInferCctvCount={totalMonitoringCctvs}
              inferGpuCount={Math.max(1, gpuAllocationStatus?.device_count ?? 1)}
              playbackResumeNonce={playbackResumeNonce}
              streamReloadNonce={streamReloadNonce}
              riskDialogOpen={centralRiskDialogOpen}
              onEnterPoolFullscreen={enterPoolFullscreen}
              onEnterCctvRotation={enterCctvRotation}
            />
          ))}
        </Grid>
      </Container>
      ) : cctvRotation ? (
        (() => {
          const rotSlide =
            cctvRotation.slides[cctvRotation.slideIndex] || null;
          const rotCctvId = rotSlide?.cctvId;
          const rotViewCrop = rotSlide?.viewCrop;
          const rotZoneIndex = rotSlide?.zoneIndex ?? 0;
          const rotCctv = (poolCctvs[String(cctvRotation.poolId)] ?? []).find(
            (c) => Number(c.id) === Number(rotCctvId)
          );
          const streamUrl = rotCctv
            ? resolveAbsoluteCctvStreamUrl(
                rotCctv,
                useRemotePlayback,
                cctvRotation.siteGatewayEnabled
              )
            : '';
          const rotWebrtc = String(
            (rotCctv as { webrtc_url?: string } | undefined)?.webrtc_url || ''
          ).trim();
          const canShowRot = Boolean(rotCctv && (streamUrl || rotWebrtc));
          const posLabel =
            cctvRotation.slides.length > 0
              ? `${cctvRotation.slideIndex + 1} / ${cctvRotation.slides.length}`
              : '';
          const rotPoolMonitored = isCentralPoolMonitored(
            Number(cctvRotation.poolId),
            monitoringAiSettings,
            poolIds
          );
          const rotServerSnap =
            rotCctv && rotPoolMonitored
              ? lookupServerSnapshot(
                  serverSnapshotsByCctv,
                  Number(cctvRotation.poolId),
                  Number(rotCctv.id)
                )
              : undefined;
          let rotOverlayTracks: OverlayTrack[] = [];
          if (rotServerSnap) {
            rotOverlayTracks = overlayTracksFromServerSnapshot(
              rotServerSnap,
              wearableTrackMap
            );
            rotOverlayTracks = enrichOverlayTracksWithOpenPose(
              rotOverlayTracks,
              rotServerSnap.openpose_overlay ?? null
            );
            if (rotViewCrop) {
              rotOverlayTracks = rotOverlayTracks
                .map((t) => {
                  const remapped = remapBboxToCrop(t.displayBbox, rotViewCrop);
                  if (!remapped) return null;
                  return {
                    ...t,
                    displayBbox: remapped.bbox,
                    frame_width: remapped.frameW,
                    frame_height: remapped.frameH,
                  };
                })
                .filter((t): t is NonNullable<typeof t> => t != null);
            }
            if (Boolean((rotCctv as { rotate_180?: boolean })?.rotate_180)) {
              const rfw = Math.max(
                1,
                rotViewCrop
                  ? rotViewCrop[2] - rotViewCrop[0]
                  : Number(rotServerSnap?.frame_width) ||
                      rotOverlayTracks[0]?.frame_width ||
                      1280
              );
              const rfh = Math.max(
                1,
                rotViewCrop
                  ? rotViewCrop[3] - rotViewCrop[1]
                  : Number(rotServerSnap?.frame_height) ||
                      rotOverlayTracks[0]?.frame_height ||
                      720
              );
              rotOverlayTracks = rotOverlayTracks.map((t) => ({
                ...t,
                displayBbox: remapBboxRotate180(
                  t.displayBbox,
                  Math.max(1, Number(t.frame_width) || rfw),
                  Math.max(1, Number(t.frame_height) || rfh)
                ),
              }));
            }
          }
          return (
            <Box
              id="cctv-rotation-overlay"
              sx={{
                position: 'fixed',
                inset: 0,
                zIndex: 1800,
                bgcolor: '#000',
                display: 'flex',
                flexDirection: 'column',
                color: '#fff',
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  bgcolor: 'rgba(20,20,20,0.95)',
                  borderBottom: '1px solid #444',
                }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  startIcon={<FullscreenExitIcon />}
                  onClick={exitCctvRotation}
                >
                  돌아가기
                </Button>
                <Typography variant="body2" sx={{ fontWeight: 600, mr: 1 }}>
                  {cctvRotation.poolName} · CCTV 순환
                  {posLabel ? ` · ${posLabel}` : ''}
                  {cctvRotation.paused
                    ? ' · 순환 OFF'
                    : cctvRotVideoReady
                      ? ' · 순환 ON'
                      : ' · 로딩 중…'}
                </Typography>
                <IconButton
                  size="small"
                  color="inherit"
                  aria-label="이전 CCTV"
                  onClick={() => shiftCctvRotation(-1, true)}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <IconButton
                  size="small"
                  color="inherit"
                  aria-label="다음 CCTV"
                  onClick={() => shiftCctvRotation(1, true)}
                >
                  <ChevronRightIcon />
                </IconButton>
                <Button
                  size="small"
                  variant="contained"
                  color={cctvRotation.paused ? 'success' : 'warning'}
                  disabled={!cctvRotation.paused && !cctvRotVideoReady}
                  onClick={() =>
                    setCctvRotation((p) =>
                      p ? { ...p, paused: !p.paused } : p
                    )
                  }
                >
                  {cctvRotation.paused ? '순환 켜기' : '순환 끄기'}
                </Button>
                <TextField
                  size="small"
                  label="초"
                  value={cctvRotIntervalInput}
                  onChange={(e) => setCctvRotIntervalInput(e.target.value)}
                  onBlur={() => {
                    const sec = Math.min(
                      300,
                      Math.max(1, Math.floor(Number(cctvRotIntervalInput)) || 5)
                    );
                    setCctvRotIntervalInput(String(sec));
                    writeMonitoringRotationIntervalSec(sec);
                    setCctvRotation((p) =>
                      p ? { ...p, intervalSec: sec } : p
                    );
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      (e.target as HTMLInputElement).blur();
                    }
                  }}
                  sx={{
                    width: 72,
                    '& .MuiInputBase-root': { color: '#fff' },
                    '& .MuiInputLabel-root': { color: '#bbb' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#666' },
                  }}
                  inputProps={{ inputMode: 'numeric', min: 1, max: 300 }}
                />
                <Typography variant="caption" sx={{ color: '#aaa' }}>
                  ←/→ CCTV · Space 일시정지 · Esc 종료 · 휠 확대 · 드래그 이동
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  position: 'relative',
                  bgcolor: '#000',
                }}
              >
                {canShowRot ? (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                    }}
                  >
                    <ZoomPanViewport
                      resetKey={`${cctvRotation.poolId}-${rotCctv.id}-z${rotZoneIndex}-${cctvRotation.slideIndex}`}
                    >
                      <CentralMonitoringCctvPlayer
                        key={`rot-${cctvRotation.poolId}-${rotCctv.id}-z${rotZoneIndex}`}
                        ref={cctvRotPlayerRef}
                        cctv={{
                          id: Number(rotCctv.id),
                          pool_id: Number(cctvRotation.poolId),
                          name: rotCctv.name || `CCTV ${rotCctv.id}`,
                          stream_url: rotCctv.stream_url,
                          url: rotCctv.url,
                          playback_url: rotCctv.playback_url,
                          webrtc_url: (rotCctv as { webrtc_url?: string }).webrtc_url,
                          rotate_180: Boolean(
                            (rotCctv as { rotate_180?: boolean }).rotate_180
                          ),
                        }}
                        rotate180={Boolean(
                          (rotCctv as { rotate_180?: boolean }).rotate_180
                        )}
                        viewCrop={rotViewCrop}
                        effectiveStreamUrl={streamUrl}
                        bufferSize={bufferSize}
                        globalQuality={globalQuality}
                        centralGo2rtcAvailable={centralGo2rtcAvailable}
                        centralGo2rtcStreams={centralGo2rtcStreams}
                        playbackResumeNonce={playbackResumeNonce}
                        streamReloadNonce={streamReloadNonce}
                      />
                    </ZoomPanViewport>
                    {rotPoolMonitored && rotOverlayTracks.length > 0 ? (
                      <Box
                        ref={cctvRotOverlayContainerRef}
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          pointerEvents: 'none',
                          zIndex: 50,
                        }}
                      >
                        <CentralMonitoringOverlays
                          cctvId={Number(rotCctv.id)}
                          riskZones={
                            rotViewCrop
                              ? undefined
                              : Boolean(
                                    (rotCctv as { rotate_180?: boolean })
                                      .rotate_180
                                  )
                                ? remapZoneListRotate180(
                                    (rotCctv as Cctv).risk_detection_zones as
                                      | number[][]
                                      | null
                                      | undefined,
                                    Math.max(
                                      1,
                                      Number(rotServerSnap?.frame_width) || 1280
                                    ),
                                    Math.max(
                                      1,
                                      Number(rotServerSnap?.frame_height) || 720
                                    )
                                  )
                                : (rotCctv as Cctv).risk_detection_zones
                          }
                          specialZones={
                            rotViewCrop
                              ? undefined
                              : Boolean(
                                    (rotCctv as { rotate_180?: boolean })
                                      .rotate_180
                                  )
                                ? remapZoneListRotate180(
                                    (rotCctv as Cctv)
                                      .special_management_zones as
                                      | number[][]
                                      | null
                                      | undefined,
                                    Math.max(
                                      1,
                                      Number(rotServerSnap?.frame_width) || 1280
                                    ),
                                    Math.max(
                                      1,
                                      Number(rotServerSnap?.frame_height) || 720
                                    )
                                  )
                                : (rotCctv as Cctv).special_management_zones
                          }
                          tracks={rotOverlayTracks}
                          containerEl={cctvRotOverlayContainerRef.current}
                          videoEl={
                            rotViewCrop
                              ? null
                              : cctvRotPlayerRef.current?.videoRef.current ??
                                null
                          }
                          layoutTick={cctvRotOverlayTick}
                          detectionDebug={{
                            raw: rotServerSnap?.detections?.length ?? 0,
                            shown: rotOverlayTracks.length,
                          }}
                        />
                      </Box>
                    ) : null}
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 12,
                        bottom: 12,
                        zIndex: 5,
                        bgcolor: 'rgba(0,0,0,0.72)',
                        px: 1.5,
                        py: 1,
                        borderRadius: 1,
                        maxWidth: 'min(90vw, 480px)',
                        pointerEvents: 'none',
                      }}
                    >
                      <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
                        {(rotCctv.name || `CCTV ${rotCctv.id}`) +
                          (cctvRotation.zoneTilesOnly
                            ? ` · 구역 ${rotZoneIndex + 1}`
                            : '')}
                      </Typography>
                      {rotCctv.location ? (
                        <Typography sx={{ fontSize: 12, color: '#ccc', mt: 0.5 }}>
                          {rotCctv.location}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography>표시할 CCTV 스트림 없음.</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          );
        })()
      ) : poolFullscreen ? (
        (() => {
          const fsPool = monitoringPools[poolFullscreen.poolIndex];
          if (!fsPool) {
            return (
              <Box sx={{ p: 3, color: '#fff', bgcolor: '#000', flexGrow: 1 }}>
                <Typography>시설 정보를 불러오기 불가.</Typography>
                <Button onClick={exitPoolFullscreen} sx={{ mt: 2 }} variant="contained">
                  돌아가기
                </Button>
              </Box>
            );
          }
          const posLabel =
            monitoringPools.length > 1
              ? `${poolFullscreen.poolIndex + 1} / ${monitoringPools.length}`
              : '';
          return (
            <Box
              id="pool-fullscreen-overlay"
              sx={{
                position: 'fixed',
                inset: 0,
                zIndex: 1800,
                bgcolor: '#000',
                display: 'flex',
                flexDirection: 'column',
                color: '#fff',
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  bgcolor: 'rgba(20,20,20,0.95)',
                  borderBottom: '1px solid #444',
                }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  startIcon={<FullscreenExitIcon />}
                  onClick={exitPoolFullscreen}
                >
                  돌아가기
                </Button>
                <Typography variant="body2" sx={{ fontWeight: 600, mr: 1 }}>
                  {fsPool.name || fsPool.code}
                  {posLabel ? ` · ${posLabel}` : ''}
                  {poolFullscreen.paused
                    ? ' · 롤링 OFF'
                    : poolFsVideosReady
                      ? ' · 롤링 ON'
                      : ' · CCTV 로딩 중…'}
                </Typography>
                {monitoringPools.length > 1 && (
                  <>
                    <IconButton
                      size="small"
                      color="inherit"
                      aria-label="이전 시설"
                      onClick={() => shiftPoolFullscreen(-1, true)}
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="inherit"
                      aria-label="다음 시설"
                      onClick={() => shiftPoolFullscreen(1, true)}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                    <Button
                      size="small"
                      variant="contained"
                      color={poolFullscreen.paused ? 'success' : 'warning'}
                      disabled={!poolFullscreen.paused && !poolFsVideosReady}
                      onClick={() =>
                        setPoolFullscreen((p) =>
                          p ? { ...p, paused: !p.paused } : p
                        )
                      }
                    >
                      {poolFullscreen.paused ? '롤링 켜기' : '롤링 끄기'}
                    </Button>
                    <TextField
                      size="small"
                      label="초"
                      value={poolFsIntervalInput}
                      onChange={(e) => setPoolFsIntervalInput(e.target.value)}
                      onBlur={() => {
                        const sec = Math.min(
                          300,
                          Math.max(1, Math.floor(Number(poolFsIntervalInput)) || 20)
                        );
                        setPoolFsIntervalInput(String(sec));
                        writeMonitoringPoolGridRotationIntervalSec(sec);
                        setPoolFullscreen((p) =>
                          p ? { ...p, intervalSec: sec } : p
                        );
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          (e.target as HTMLInputElement).blur();
                        }
                      }}
                      sx={{
                        width: 72,
                        '& .MuiInputBase-root': { color: '#fff' },
                        '& .MuiInputLabel-root': { color: '#bbb' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#666' },
                      }}
                      inputProps={{ inputMode: 'numeric', min: 1, max: 300 }}
                    />
                    <Typography variant="caption" sx={{ color: '#aaa' }}>
                      기본 20초 · CCTV 로드 후 롤링 · ←/→ · Space · Esc
                    </Typography>
                  </>
                )}
              </Box>
              <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 0.5, sm: 1 } }}>
                <Grid container sx={{ width: '100%' }}>
                <PoolGridItem
                  key={`fs-${fsPool.id}`}
                  pool={fsPool}
                  cctvs={poolCctvs[String(fsPool.id)] ?? []}
                  poolsPerRow={1}
                  displayPoolColumns={1}
                  stackCctvVertically={false}
                  wearableTrackMap={wearableTrackMap}
                  bufferSize={bufferSize}
                  globalQuality={globalQuality}
                  playbackConfigReady={playbackConfigReady}
                  useRemotePlayback={useRemotePlayback}
                  centralGo2rtcAvailable={centralGo2rtcAvailable}
                  centralGo2rtcStreams={centralGo2rtcStreams}
                  monitoringAiSettings={monitoringAiSettings}
                  centralOperatingPoolIds={poolIds}
                  serverSnapshotsByCctv={serverSnapshotsByCctv}
                  excludeBusyPoolId={excludeBusyPoolId}
                  onTogglePoolExclude={handleTogglePoolExclude}
                  gpuAllocationStatus={gpuAllocationStatus}
                  serverInferenceActive={centralServerInferenceActive}
                  onCctvGpuChanged={handleCctvGpuChanged}
                  rotationIntervalSec={rotationIntervalSec}
                  totalInferCctvCount={totalMonitoringCctvs}
                  inferGpuCount={Math.max(1, gpuAllocationStatus?.device_count ?? 1)}
                  playbackResumeNonce={playbackResumeNonce}
                  streamReloadNonce={streamReloadNonce}
                  riskDialogOpen={centralRiskDialogOpen}
                  onEnterCctvRotation={enterCctvRotation}
                  hideFullscreenButton
                />
                </Grid>
              </Box>
            </Box>
          );
        })()
      ) : null}
      <CentralRiskEventDialog
        open={centralRiskDialogOpen}
        payload={centralRiskEvent}
        autoDispatchAlert={autoAlertEnabled}
        onClose={() => {
          const closed = centralRiskEvent;
          dismissCentralRiskDialogPayload(closed);
          // 처리완료·오탐 닫기: 같은 시설 대기 이벤트는 전부 버림 (연쇄 재오픈 방지)
          if (closed) {
            pendingRiskEventsRef.current = pendingRiskEventsRef.current.filter(
              (p) => p.poolId !== closed.poolId
            );
          }
          // 다른 시설 대기건은 사용자가 다시 볼 수 있게 1건만 유지하되,
          // 즉시 띄우지 않고 잠깐 쉬어 「안 닫힘」 체감을 막음
          const next = pendingRiskEventsRef.current.shift();
          setCentralRiskDialogOpen(false);
          setCentralRiskEvent(null);
          setPlaybackResumeNonce((n) => n + 1);
          if (next && !isCentralRiskDialogDismissed(next)) {
            window.setTimeout(() => {
              if (centralRiskDialogOpenRef.current) return;
              if (isCentralRiskDialogDismissed(next)) return;
              setCentralRiskEvent(next);
              setCentralRiskDialogOpen(true);
            }, 800);
          }
        }}
        onEventRecorded={(eventDbId) => {
          setCentralRiskEvent((prev) =>
            prev ? { ...prev, eventDbId } : prev
          );
        }}
      />
      <PoolManualAlertDialog
        open={manualAlertTarget != null}
        target={manualAlertTarget}
        onClose={() => setManualAlertTarget(null)}
        onSent={() => setManualAlertSnack('알림 메시지를 전송함')}
      />
      <ControlCenterSafetyAlertDialog />
      <ControlRoomSirenDialog />
      <Snackbar
        open={Boolean(manualAlertSnack)}
        autoHideDuration={4000}
        onClose={() => setManualAlertSnack(null)}
        message={manualAlertSnack ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default CentralMonitoring;